// frontend/src/components/RecordsManagementPanel.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-05-20
// Updated:      2026-09-07
//
// Description:
//   Records Management shell for the System page.
//   Provides grouped internal navigation for operational record domains and preserves
//   the existing AdminMaintenanceWidget under Data Integrity / Legacy Tools.
//
//   Mobile responsiveness update (2026-09-07):
//     - Preserve the desktop left-side Records navigation unchanged above 768px.
//     - Replace the side rail with a grouped native section selector on mobile.
//     - Reflow toolbars, grids, status rows, modal controls, and dependency rows for touch.
//     - Present existing desktop table rows as labelled mobile cards without mounting
//       duplicate data components or introducing additional API calls.
//     - Render Data Integrity context and AdminMaintenanceWidget as sibling surfaces so
//       the maintenance widget is not wrapped in a second bordered WidgetWrapper.
//
//   Phase RM2 scope:
//     - Introduce the first real domain panel for Quotes.
//     - Add safe quote search/list UI using the planned admin search contract.
//     - Add non-destructive dependency preview support.
//     - RM3A enables safe quote archive/restore lifecycle actions.
//     - RM3B moves quote dependencies into a modal with grouped linked-record detail,
//       visible restore actions for archived quotes, and clearer lifecycle UX.
//     - RM3C switches the dependency modal to the shared ModalWrapper for standard
//       close-button behaviour, overlay behaviour, and project-wide modal consistency.
//     - Delete remains intentionally disabled until dependency-enforced soft delete,
//       recovery, purge governance, and audit log integration are implemented.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "../context/ThemeContext.jsx";
import api from "../api/axiosInstance";
import ModalWrapper from "./ModalWrapper";
import AdminMaintenanceWidget from "../widgets/AdminMaintenanceWidget.jsx";

const NAV_GROUPS = [
  {
    group: "Overview",
    items: [
      {
        key: "overview",
        label: "Overview",
        description: "Signed-off AUD8 status, completed lifecycle coverage, outstanding implementation work, and future audit programme.",
      },
    ],
  },
  {
    group: "CRM",
    items: [
      { key: "customers", label: "Customers", description: "Customer records, lifecycle status, account managers, logos, ownership, and linked activity." },
      { key: "contacts", label: "Contacts", description: "Contact records, customer links, portal-user links, avatars, duplicate cleanup, and communication references." },
      { key: "opportunities", label: "Opportunities", description: "Opportunity lifecycle, tenant-safe resource mapping, access matrix, stage history, archive, restore, and controlled delete." },
      { key: "quotes", label: "Quotes", description: "Quote lifecycle, approvals, pricebooks, line items, linked jobs, tenant-safe resource mapping, archive, restore, and controlled delete." },
    ],
  },
  {
    group: "Operations",
    items: [
      { key: "jobs", label: "Jobs", description: "Job records, tenant-owned taxonomy links, assignments, workflow links, work packages, and operational cleanup." },
      { key: "resources", label: "Resources", description: "Operational resources, user-resource mappings, planner visibility, sales/resource flags, and assignment history." },
      { key: "workflow_runs", label: "Workflow Runs", description: "Runtime workflow records, progress state, task generation, and repair checks." },
      { key: "workflow_templates", label: "Workflow Templates", description: "Workflow templates, versions, steps, actions, category bindings, tenant ownership, and cross-tenant binding checks." },
      { key: "workflow_template_bindings", label: "Workflow Template Bindings", description: "Tenant-safe links between job taxonomy, workflow templates, and operational automation." },
      { key: "job_work_packages", label: "Job Work Packages", description: "Future package-level job execution records, tenant-safe job links, workflow links, and reporting dependencies." },
      { key: "tasks", label: "Tasks", description: "Job tasks, assignment state, completion state, and overdue runtime records." },
    ],
  },
  {
    group: "ANPR",
    items: [
      { key: "anpr_reads", label: "ANPR Reads / Sightings", description: "Plate sightings, recognition output, image references, enrichment state, and tenant-scoped report visibility." },
      { key: "plate_sessions", label: "Plate Sessions", description: "Plate session summaries, journey grouping, dedupe outputs, and tenant-scoped linked sightings." },
      { key: "camera_devices", label: "Camera Devices", description: "Camera registry records, ingest credentials, tenant bindings, and operational state." },
      { key: "camera_ingest_events", label: "Camera Ingest Events", description: "Inbound camera event audit, payload status, errors, and processing results." },
      { key: "job_runs", label: "Job Runs", description: "ANPR processing runs, source files, recognition progress, generated outputs, and tenant upload paths." },
    ],
  },
  {
    group: "Users & Access",
    items: [
      { key: "users", label: "Users", description: "User accounts, tenant scoping, profile state, avatars, account lifecycle, and cross-tenant visibility checks." },
      { key: "portal_users", label: "Portal Users", description: "Contact-driven portal user provisioning, contact.user_id links, portal access flags, and customer ownership." },
      { key: "roles", label: "Roles", description: "Role records, permission alignment, RoleStatsWidget tenant counts, and future access governance." },
      { key: "access_grants", label: "Access Grants", description: "Quote/opportunity/resource access rows, revocations, ownership protections, and tenant-safe DB functions." },
      { key: "cross_tenant_selectors", label: "Cross-Tenant Selectors", description: "Dropdowns, modals, widgets, and lookups that must not expose another tenant's customers, contacts, users, resources, taxonomy, reports, or theme assets." },
    ],
  },
  {
    group: "Documents & Storage",
    items: [
      { key: "documents", label: "Documents", description: "Uploaded documents, SharePoint references, job links, customer links, previews, signatures, and tenant ownership." },
      { key: "document_previews", label: "Document Previews", description: "Preview URLs, published document access, portal visibility, signed URL safety, and customer/job tenant checks." },
      { key: "sharepoint_documents", label: "SharePoint Documents", description: "SharePoint folders, resolved customer/job paths, document links, upload metadata, and tenant-safe integration settings." },
      { key: "customer_logos", label: "Customer Logos", description: "Customer logo upload paths, preview rendering, tenant-owned storage, and cross-tenant asset checks." },
      { key: "contact_avatars", label: "Contact Avatars", description: "Contact/avatar upload paths, user avatar links, cache busting, and tenant-owned static asset checks." },
      { key: "uploads", label: "Uploads", description: "Raw uploads, processed files, snapshots, tenant storage paths, and bootstrap-generated storage structure." },
      { key: "exports", label: "Exports", description: "Generated report exports, temporary files, and cleanup candidates." },
      { key: "orphaned_files", label: "Orphaned Files", description: "Files on disk without DB references and DB paths without files." },
    ],
  },
  {
    group: "System Records",
    items: [
      { key: "alerts", label: "Alerts", description: "System alerts, dismissal state, tenant visibility, resolved alert cleanup, and alert/widget tenant awareness." },
      { key: "reports", label: "Reports", description: "Report definitions, standard reports, field mappings, tenant-scoped preview/export behaviour, and cross-tenant report data checks." },
      { key: "report_templates", label: "Report Templates", description: "Saved report template settings, tenant_id/user_id ownership, admin update controls, and template isolation." },
      { key: "report_logs", label: "Report Logs", description: "Generated report history, tenant_id/user_id capture, export evidence, and forensic reporting trail." },
      { key: "widgets", label: "Widgets", description: "Widget registry records, dashboard usage, active/inactive widget state, theme awareness, and tenant-safe widget counts." },
      { key: "taxonomy", label: "Taxonomy", description: "Tenant-owned job taxonomy, subtype hierarchy, workflow bindings, and operational classification records." },
      { key: "settings", label: "Settings", description: "Tenant settings, scheduler settings, integrations, UI configuration, white-label theme, and configuration records." },
    ],
  },
  {
    group: "Tenant Isolation & Bootstrap",
    items: [
      { key: "tenant_isolation", label: "Tenant Isolation", description: "AUD8 tenant-2 validation, theme isolation, taxonomy isolation, seeded data checks, and remaining tenant leak targets." },
      { key: "bootstrap_generator", label: "Bootstrap Generator", description: "Super Admin tenant bootstrap module design, generation order, validation rules, preview/generate modes, and safe cleanup batches." },
      { key: "tenant_seed_data", label: "Tenant Seed Data", description: "Tenant-2 customers, contacts, resources, portal users, resource mappings, and pharmaceutical taxonomy used for isolation testing." },
    ],
  },
  {
    group: "Governance",
    items: [
      { key: "deleted_records", label: "Deleted Records", description: "Soft-deleted records waiting for restore or controlled purge." },
      { key: "audit_log", label: "Audit Log", description: "Administrative actions, lifecycle changes, destructive operations, recovery events, route security evidence, and tenant/user capture." },
      { key: "audit_roadmap", label: "Audit Roadmap", description: "AUD1-AUD9 completion status, signed-off areas, residual policy decisions, deferred work, and next validation targets." },
      { key: "data_integrity", label: "Data Integrity", description: "Existing maintenance tools, orphan cleanup, backup retention, DB size review, and future repair utilities." },
    ],
  },
];

const QUICK_STATS = [
  { label: "AUD8 staff/API", value: "Signed Off", tone: "success", detail: "Final closure harness executed 575 checks with zero failures across the selected staff/API tenant-isolation surface after remediation." },
  { label: "Confirmed defects", value: "0 Open", tone: "success", detail: "The cross-tenant user read was fixed by removing the implicit Super Admin bypass. The final focused regression completed with FAIL 0." },
  { label: "Lifecycle domains", value: "12 Complete", tone: "success", detail: "Quotes, Opportunities, Contacts, Customers, Jobs, Audit Log, Resources, Camera Devices, Ingest Events, Sightings, Plate Sessions, and core Documents controls are operational." },
  { label: "AUD9 endpoint audit", value: "Substantially Complete", tone: "success", detail: "478 operations analysed. Unexpected anonymous successes reduced from 12 to 6; the remaining six are intentionally public infrastructure or UI bootstrap policy endpoints." },
  { label: "Next programme", value: "Portal Validation / RBAC", tone: "attention", detail: "Complete real portal-identity ownership tests, classify intentional public endpoints, split public UI branding from authenticated configuration, and continue RBAC Console delivery." },
];

const PHASES = [
  {
    phase: "RM1",
    title: "Shell + Navigation",
    status: "Complete",
    detail: "Create the Records Management structure and keep existing tools available.",
  },
  {
    phase: "RM2",
    title: "Quotes",
    status: "Complete",
    detail: "Search, dependency modal, archive, restore, and state-aware actions are working.",
  },
  {
    phase: "RM3",
    title: "Opportunities",
    status: "Complete",
    detail: "Search, dependency modal, archive, restore, and owner/access context are working.",
  },
  {
    phase: "RM5",
    title: "Contacts",
    status: "Complete",
    detail: "Search, dependency modal, archive, restore, and customer relationship context are working.",
  },
  {
    phase: "RM6",
    title: "Customers",
    status: "Complete",
    detail: "Search, dependency modal, archive, restore, and relationship graph are working.",
  },
  {
    phase: "RM7",
    title: "Jobs",
    status: "Complete",
    detail: "Job search, dependency map, archive, restore, assignments, workflow, and ANPR output context are working.",
  },
  {
    phase: "RM8",
    title: "Audit Log",
    status: "Complete",
    detail: "Audit table, backend route, UI viewer, request logging, identity events, lifecycle events, update snapshots, document events, IP capture, user agent capture, tenant capture, and user capture are verified.",
  },
  {
    phase: "RM9",
    title: "Resources",
    status: "Complete",
    detail: "Resource search, dependency analysis, two-step reassignment, gated redaction, and audit capture are working.",
  },
  {
    phase: "RM10A",
    title: "Camera Devices",
    status: "Complete",
    detail: "Camera search, dependency analysis, ingest enable/disable, deactivate/reactivate, and audit capture are working.",
  },
  {
    phase: "RM10B",
    title: "Camera Ingest Events",
    status: "Complete",
    detail: "View-only ingest event management, payload status, parse errors, decode counts, operational diagnostics, dependency inspection, and audit are working.",
  },
  {
    phase: "RM10C",
    title: "ANPR Sightings",
    status: "Complete",
    detail: "View-only records management for plate reads, snapshots, enrichment state, reporting references, filters, dependency inspection, and audit is working.",
  },
  {
    phase: "RM10D",
    title: "Plate Sessions",
    status: "Complete",
    detail: "View-only journey/session summaries, dedupe outputs, linked sightings, dependency inspection, and audit are working.",
  },
];

const DOMAIN_STATUS_GROUPS = [
  {
    group: "Signed Off — CRM & Operations",
    domains: [
      { label: "Users", status: "Signed Off", detail: "Staff/API tenant isolation verified. Cross-tenant direct user access now returns 404; list, update, delete, avatar and activity paths remain tenant-scoped." },
      { label: "Customers", status: "Signed Off", detail: "Tenant-safe list/detail/lifecycle and selector API coverage passed. AdvancedCustomerList, logos and account-manager UX remain feature enhancements, not AUD8 defects." },
      { label: "Contacts", status: "Signed Off", detail: "Tenant-safe list/detail/lifecycle and customer ownership passed. AdvancedContactList, avatars and portal-link UX remain separate enhancement work." },
      { label: "Jobs", status: "Signed Off", detail: "Tenant ownership, taxonomy, workflow, planner, uploads and reporting access surfaces passed the final staff/API regression." },
      { label: "Resources", status: "Signed Off", detail: "Tenant-scoped resource visibility and operational user-resource mapping are verified." },
      { label: "Opportunities", status: "Complete", detail: "Search, dependencies, archive, restore, owner/access context and tenant-safe resource mapping are operational." },
      { label: "Quotes", status: "Complete", detail: "Search, dependencies, archive, restore, visibility, pricebook filtering and tenant-safe resource mapping are operational." },
    ],
  },
  {
    group: "Signed Off — Workflow, ANPR & System",
    domains: [
      { label: "Workflow Templates", status: "Signed Off", detail: "Template, version, step, action, category, binding, runtime, planner and reporting routes passed staff/API tenant-isolation closure. Empty Tenant 2 fixtures are recorded as N/A." },
      { label: "Workflow Template Bindings", status: "Signed Off", detail: "Taxonomy-to-template binding routes are tenant-scoped and cross-tenant binding exposure was not observed." },
      { label: "Camera Devices", status: "Complete", detail: "Registry, bindings, ingest controls, dependency analysis and lifecycle controls are operational." },
      { label: "Camera Ingest Events", status: "Complete", detail: "View-only operational diagnostics, dependency inspection and tenant-safe records management are operational." },
      { label: "ANPR Sightings", status: "Complete", detail: "View-only sightings, images, enrichment, dependencies and reporting references are operational." },
      { label: "Plate Sessions", status: "Complete", detail: "Journey/session summaries, linked sightings, dedupe output and dependency inspection are operational." },
      { label: "Reports", status: "Signed Off", detail: "Standard templates are intentionally shared; custom tenant templates, report execution, preview/export and report ownership passed the final staff/API closure where fixtures existed." },
      { label: "Widgets", status: "Signed Off", detail: "Widget registry is intentionally shared; tenant/user layouts and tenant-scoped widget data are enforced separately." },
      { label: "Settings & Integrations", status: "Signed Off", detail: "UI config, SharePoint, messaging, weather and traffic settings use authenticated tenant context. Missing tenant configuration may correctly return 404." },
      { label: "Audit Log", status: "Complete", detail: "Request, identity, lifecycle and mutation evidence is tenant-scoped. Nested historical tenant IDs are content, not row ownership." },
    ],
  },
  {
    group: "Complete — Records Management Lifecycle",
    domains: [
      { label: "Contacts", status: "Complete", detail: "Search, dependency modal, archive and restore are operational." },
      { label: "Customers", status: "Complete", detail: "Search, relationship graph, dependency modal, archive and restore are operational." },
      { label: "Jobs", status: "Complete", detail: "Search, dependency map, assignments, workflow/ANPR context, archive and restore are operational." },
      { label: "Documents", status: "AUD9 Hardened", detail: "Staff document controls remain complete. Portal document listing, diagnostics and health routes now require authenticated principals; customer scope is derived server-side and raw database errors are sanitised." },
      { label: "Resources", status: "Complete", detail: "Search, dependency analysis, reassignment, controlled redaction and audit capture are operational." },
      { label: "ANPR Operational Records", status: "Complete", detail: "Camera Devices, Ingest Events, Sightings and Plate Sessions have specialised records-management controls." },
    ],
  },
  {
    group: "Outstanding Implementation — Not AUD8 Failures",
    domains: [
      { label: "Portal Security", status: "Substantially Complete", detail: "Unauthenticated application-route hardening is complete. X-User-Id trust was removed; Portal Documents, Portal Job Access, diagnostics and health endpoints now require authenticated principals. Real portal-identity ownership tests remain." },
      { label: "RBAC Console", status: "In Progress", detail: "Make role creation, permission assignment, groups and effective-grant management fully functional and auditable." },
      { label: "Bootstrap Generator", status: "Design Validated", detail: "Implement preview/generate/download and reversible batch cleanup using the agreed tenant-safe creation order." },
      { label: "Job Work Packages", status: "Implementation", detail: "Complete package-level runtime, composite tenant-safe links, reporting and dependency inspection." },
      { label: "Uploads / Exports / Orphans", status: "Governance", detail: "Add retention, orphan detection, generated export cleanup and tenant-path governance." },
      { label: "Customer/Contact UX", status: "Enhancement", detail: "Advanced lists, logos, avatars, portal-user controls and selector ergonomics remain product work." },
      { label: "Deleted Records", status: "Planned", detail: "Central restore and controlled purge queue remains a later lifecycle-governance phase." },
    ],
  },
];

const AUDIT_READINESS_FINDINGS = [
  {
    label: "Core audit infrastructure",
    status: "Complete",
    detail: "audit_log table, backend audit route, Audit Log viewer, request-level middleware auditing, API request logging, IP capture, user agent capture, tenant capture, and user capture are working.",
  },
  {
    label: "Security and identity auditing",
    status: "Complete",
    detail: "Failed login audits, avatar upload audits, user update audits, role change audits, and UI configuration/theme update audits are verified. Passwords and oversized base64 image payloads are not logged.",
  },
  {
    label: "CRM update auditing",
    status: "Complete",
    detail: "Customer, Contact, Opportunity, and Quote update routes now write detached old/new snapshots with changed_fields into audit_log.",
  },
  {
    label: "Job update auditing",
    status: "Complete",
    detail: "PATCH/PUT job updates now write detached old/new snapshots into audit_log. Verified with week_commencing on Job 102.",
  },
  {
    label: "Document auditing",
    status: "Complete",
    detail: "AUD5 verified after auth hardening: flag update, bulk flag update, signature request, upload, and delete all write audit entries with useful document context.",
  },
  {
    label: "Workflow route security",
    status: "Complete",
    detail: "AUD6 reviewed workflow.py, workflow template admin routes, version/step/action/binding/category routes, workflow reporting, planner projection, and job task assignment routes. Smoke tests returned 401 without auth and 200 with bearer auth.",
  },
  {
    label: "Route security hardening",
    status: "Complete",
    detail: "SEC1-SEC19 are complete. Canonical auth imports are clean, obvious tenant_id Query/Form/Body injection routes are hardened, and the remaining tenant_id=1 hits are documented exceptions or deferred design items.",
  },
  {
    label: "Operational backup safety",
    status: "Stabilised",
    detail: "Repo bundle was created and verified. Database backup was moved outside the repo and compressed from about 20 GB to 4.2 GB. Post-audit backup retention, compression, restore testing, and cleanup remain tracked as maintenance work.",
  },
];

const AUDIT_PROGRESS = [
  { phase: "AUD1", title: "Core Audit Infrastructure", status: "Complete", detail: "audit_log, middleware request capture, API logging, IP, user agent, tenant and user capture verified." },
  { phase: "AUD2", title: "Identity & Security", status: "Complete", detail: "Failed login, avatar, user, role and UI configuration auditing verified." },
  { phase: "AUD3", title: "CRM Updates", status: "Complete", detail: "Customer, Contact, Opportunity and Quote detached old/new snapshots and changed_fields verified." },
  { phase: "AUD4", title: "Job Updates", status: "Complete", detail: "PATCH/PUT job update audit evidence verified." },
  { phase: "AUD5", title: "Documents", status: "Complete", detail: "Staff document auth, upload, flag, signature and delete audit paths verified." },
  { phase: "AUD6", title: "Workflows", status: "Complete", detail: "Runtime, template administration, bindings, planner, reporting and task routes reviewed and regression-tested." },
  { phase: "AUD7", title: "System Administration", status: "Complete", detail: "Theme, taxonomy, widgets, reports, settings, integrations, scheduler/admin and selector surfaces included in closure verification." },
  { phase: "AUD8", title: "Staff/API Tenant Isolation", status: "Signed Off", detail: "Final selected run: 575 checks, 253 PASS, 0 FAIL, 48 classified REVIEW, 274 safe-ID skips. Confirmed cross-tenant user leak fixed and re-tested." },
  { phase: "AUD9", title: "Unauthenticated Endpoint & Portal Security", status: "Substantially Complete", detail: "478 operations analysed. Unexpected anonymous successes reduced from 12 to 6 and denied-as-expected increased to 201. Remaining findings are intentional public infrastructure/UI bootstrap policy endpoints; real portal-identity ownership tests remain." },
];

const SECURITY_AUDIT_PROGRESS = [
  { phase: "SEC1", title: "Documents Route", status: "Complete", detail: "document.py hardened and verified: bearer auth required, tenant from current_user, app.user_id set, flag update, bulk update, signature request, upload, and delete audit paths verified." },
  { phase: "SEC2", title: "Messaging Settings/Test Routes", status: "Complete", detail: "messaging.py authenticated settings/test routes hardened and verified: settings read/save, test email, and test SMS work; unauthorised access returns 401; tenant_id query input is ignored or rejected under authenticated tenant context." },
  { phase: "SEC3", title: "SharePoint Settings", status: "Complete", detail: "sharepoint.py hardened and verified: unauthorised access returns 401, authorised settings read works, raw secret is not returned, and tenant_id=999 is rejected with 403." },
  { phase: "SEC4", title: "Organisation Route", status: "Complete", detail: "organisation.py hardened and verified: unauthorised read returns 401, authorised read works, and tenant_id=999 is rejected with 403. Mutation routes derive tenant from current_user." },
  { phase: "SEC5", title: "Uploads Route", status: "Complete", detail: "uploads.py hardened and verified: unauthorised upload returns 401, supplied tenant_id is compatibility-only, cross-tenant tenant_id=999 upload returns 403, job ownership is checked against authenticated tenant." },
  { phase: "SEC6", title: "Portal Downloads", status: "Complete", detail: "portal_downloads.py hardened and verified: unauthorised list returns 401, tenant_id=999 returns 403, job/customer/internal_ref resolution is scoped to authenticated tenant before filesystem scan." },
  { phase: "SEC7", title: "WhatsApp Template Routes", status: "Complete", detail: "whatsapp_template.py hardened and verified: unauthorised list returns 401, authorised list returns tenant templates, and tenant_id=999 returns 403. Twilio sync/create routes now derive tenant from current_user." },
  { phase: "SEC8", title: "Job Assignments", status: "Complete", detail: "job_assignments.py hardened and verified: canonical auth import, app.user_id context, payload tenant_id validated/overwritten, unauthorised list returns 401, authorised list returns tenant assignments." },
  { phase: "SEC9", title: "Widget Export Route", status: "Complete / Bug Deferred", detail: "export.py normalised: canonical auth import and app.user_id context added. Unauthorised export returns 401. Authorised job_list export hits a separate pre-existing widget data bug: Job object has no attribute client." },
  { phase: "SEC10", title: "Scheduler Settings", status: "Hardened / Not Live", detail: "settings_scheduler.py normalised with canonical auth and app.user_id context. Route is not currently mounted in main.py, so there is no live exposure or curl test." },
  { phase: "SEC11", title: "Portal Sync Jobs", status: "Complete", detail: "portal_sync_jobs.py hardened and verified: enqueue and status endpoints return 401 without token and 200 with token; Celery sync completed successfully." },
  { phase: "SEC12", title: "Admin Maintenance", status: "Complete", detail: "admin_maintenance.py reviewed and normalised with tenant checks preserved. Destructive maintenance paths remain protected by same-tenant validation." },
  { phase: "SEC13", title: "Admin Route", status: "Complete", detail: "admin.py hardened and safe endpoint verified: /api/admin/backups returns 401 without token and 200 with token. Delete and restore endpoints were not live-tested by design." },
  { phase: "SEC14", title: "Messaging Webhook", status: "Complete", detail: "Inbound SMS webhook no longer defaults to tenant_id=1. Tenant is resolved from the inbound Twilio To number. Unknown To returns 403 and configured To returns TwiML 200. Twilio signature validation helper added with enforcement switch." },
  { phase: "SEC15", title: "Conversation Webhook Tenant Fallback", status: "Deferred", detail: "conversation.py still contains tenant_id=1 fallback for unmatched inbound messages. It is not a direct tenant breakout, but it is a cross-tenant attribution risk. Defer until Unassigned Inbox design is agreed." },
  { phase: "SEC16", title: "Unassigned Jobs", status: "Complete", detail: "jobs_unassigned.py hardened with canonical auth and app.user_id context. main.py route order fixed so /api/jobs/unassigned mounts before /api/jobs/{job_id}. 401/200 tests passed." },
  { phase: "SEC17", title: "UI Config", status: "Complete", detail: "ui_config.py reviewed and hardened. Public GET fallback is retained for login theming; unauthorised PUT returns 401; authenticated GET works; write path now sets app.user_id and prevents non-superadmin tenant targeting." },
  { phase: "SEC18", title: "Job Templates", status: "Complete", detail: "job_template.py was a real finding and is now hardened. Auth required, list/get/update/delete tenant-scoped, body tenant_id rejected when mismatched, and spoof create tenant_id=999 returns 403." },
  { phase: "SEC19", title: "Route Grep Closure", status: "Complete", detail: "Legacy auth import sweep is clean. tenant_id Query/Form/Body sweep shows only already-hardened compatibility parameters, comments, or documented exceptions. Remaining tenant_id=1 hits are conversation.py deferred design, ui_config.py public bootstrap fallback, and messaging.py comment only." },
  { phase: "AUD9", title: "Unauthenticated Endpoint Hardening", status: "Substantially Complete", detail: "OpenAPI verifier processed 478 operations. Critical unexpected successes reduced from 12 to 6 and denied-as-expected increased to 201. Portal Documents, Portal Job Access, diagnostics, health and document-service health were secured; X-User-Id trust was removed. The six residual successes are intentional public infrastructure/UI bootstrap policy endpoints." },
];

const AUD8_TENANT_ISOLATION_FINDINGS = [
  { label: "Staff/API closure harness", status: "Signed Off", detail: "OpenAPI-driven closure harness now provides repeatable anonymous-access, tenant-list, cross-tenant ID, spoofed tenant and targeted domain checks with JSON, CSV and Markdown evidence." },
  { label: "User tenant isolation", status: "Fixed / Signed Off", detail: "The implicit Super Admin cross-tenant bypass was removed. Tenant 1 now receives 404 when requesting Tenant 2 user 199." },
  { label: "Contacts and customers", status: "Signed Off", detail: "List/detail/lifecycle and customer ownership remained isolated between Tenant 1 and Tenant 2. Responses omit tenant_id by design but cross-tenant IDs are denied." },
  { label: "Jobs, workflows and planner", status: "Signed Off", detail: "Job, workflow template, binding, category, runtime, planner and reporting surfaces passed the selected staff/API closure. Empty fixtures are recorded as N/A rather than failures." },
  { label: "Reports and templates", status: "Signed Off", detail: "Standard templates are intentionally shared. Tenant 1 custom template visibility was not exposed to Tenant 2; missing Tenant 2 custom fixtures are a fixture gap, not a leak." },
  { label: "Widgets and roles", status: "Shared / Verified", detail: "Widget and role catalogues are intentional shared registries. Tenant-specific user counts, layouts and effective access remain tenant-scoped." },
  { label: "Settings and integrations", status: "Signed Off", detail: "Authenticated settings derive tenant context server-side. A 404 for an unconfigured tenant is accepted safe behaviour." },
  { label: "Theme and taxonomy", status: "Signed Off", detail: "Tenant themes and three-level job taxonomy are tenant-owned, tenant-filtered and protected from cross-tenant parent attachment." },
  { label: "Portal routes", status: "AUD9 Hardened", detail: "Portal Documents, Portal Job Access, diagnostics and health endpoints now reject anonymous requests. Caller-controlled X-User-Id trust was removed and customer context is derived server-side. Real portal-identity ownership tests remain." },
];

const BOOTSTRAP_GENERATOR_RULES = [
  "Super Admin-only module under System / Administration; not a one-off script.",
  "Never accept frontend-supplied tenant_id. Derive tenant context server-side except controlled Super Admin target selection.",
  "Creation order: tenant, UI/theme config, internal users, resources, user-resource links, tenant taxonomy, customers, contacts, portal users, contact-user links, primary/billing contacts, account managers, jobs, opportunities, quotes, documents, then SharePoint structure.",
  "Use valid public-style domains. Do not use .local or .example.local because EmailStr validation can reject them.",
  "Use valid UK-style addresses, postcodes, phone numbers, public-style email addresses, unique customer internal_reference, and unique contact email values.",
  "Resource JSON fields such as skills and certifications must use object shape { items: [...] }, not a bare array.",
  "Operational users must be mapped to resources before quote/opportunity creation; sales users need the required is_sales/resource flags.",
  "Tenant taxonomy must be industry-specific and tenant-owned. Do not blindly clone Tenant 1 traffic survey taxonomy into every tenant.",
  "Preview/generate modes should include Insert Directly, Download SQL, Download CSV, repeatable seed support, and safe batch delete using generated-record tracking.",
];

const POST_AUD8_PROGRAMME = [
  { label: "AUD9 Portal Security", status: "Substantially Complete", detail: "Application-facing unauthenticated endpoint hardening is complete. Remaining work is real portal-identity ownership testing, intentional-public endpoint classification, UI-config separation and later RBAC capability delivery." },
  { label: "RBAC Console", status: "In Progress", detail: "Create/edit roles, assign permissions, manage groups, inspect effective grants and record all changes in audit_log." },
  { label: "Bootstrap Generator", status: "Design Validated", detail: "Build controlled tenant generation, preview, SQL/CSV export and safe batch cleanup." },
  { label: "Records Management Completion", status: "In Progress", detail: "Finish UX enhancements, Deleted Records, work packages, upload/export retention and orphan-file governance." },
];

const STATIC_RECORD_SECTIONS = {
  users: {
    title: "Users",
    status: "Signed Off",
    summary: "User tenant isolation is signed off. The final closure exercise found one implicit Super Admin cross-tenant bypass; it was removed and Tenant 1 now receives 404 when requesting Tenant 2 user 199.",
    complete: [
      "User list, single-user read, logged-in users, update, delete, avatar upload, and avatar preview are scoped to the authenticated tenant.",
      "The legacy implicit Super Admin cross-tenant bypass was removed from ordinary user routes.",
      "Created users are forced into current_user.tenant_id and client-supplied tenant_id mutation is blocked.",
      "User create, update, delete, and avatar mutations write actor and tenant context into audit_log.",
      "The focused users closure run completed with zero failures."
    ],
    remainingTitle: "Future administration work",
    remaining: [
      "Build explicit, audited tenant switching only if a future Super Admin administration workflow requires it.",
      "Complete the RBAC Console so role, permission, group, and effective-access changes can be managed through the UI.",
      "Keep new user selectors and avatar surfaces in the AUD8 regression harness as they are introduced."
    ],
    warning: "AUD8 user tenant isolation is signed off. Future Super Admin tenant switching must never reintroduce an implicit cross-tenant bypass."
  },
  portal_users: {
    title: "Portal Users",
    status: "AUD9 Substantially Complete",
    summary: "The AUD9 unauthenticated endpoint review materially hardened the portal trust boundary. Portal Documents, Portal Job Access, diagnostics and health endpoints now require authenticated principals; caller-controlled X-User-Id trust has been removed and customer context is derived server-side. Real portal-identity ownership and lifecycle validation remains.",
    complete: [
      "Portal access remains contact-driven and linked to the owning customer and tenant.",
      "Portal users are created through application workflow rather than assumed database triggers.",
      "Anonymous callers now receive 401 on Portal Documents, Portal Job Access, portal diagnostics, portal health, and document service health routes.",
      "The X-User-Id development authentication bypass was removed from portal document and job-access code.",
      "Customer scope is derived server-side and active portal-role checks were strengthened.",
      "Raw database exceptions are no longer disclosed through hardened portal routes.",
      "Bootstrap requirements include portal-user creation and contact-user linking after contacts are created."
    ],
    remainingTitle: "Remaining portal validation scope",
    remaining: [
      "Authenticate with real portal identities for Tenant 1 and Tenant 2.",
      "Verify contact, customer, job, document, preview, and download scope across customers and tenants.",
      "Verify enabling and disabling portal access preserves audit history and does not orphan access records.",
      "Verify portal-user links and customer ownership in AdvancedContactList and ManageContactModal."
    ],
    warning: "AUD9 route hardening is substantially complete. Do not sign off the full portal trust boundary until real Tenant 1 and Tenant 2 portal identities have passed ownership, lifecycle, preview, download, and cross-customer denial tests."
  },
  roles: {
    title: "Roles",
    status: "Shared Catalogue Verified",
    summary: "The role catalogue is intentionally shared across tenants. Tenant-specific user membership and effective access remain tenant-scoped. The remaining work is RBAC administration capability, not AUD8 tenant isolation.",
    complete: [
      "Both tenants see the same seven-role catalogue by design.",
      "RoleStatsWidget user counts are tenant-scoped rather than global.",
      "Role changes are represented in audit coverage.",
      "System Admin is documented as tenant-wide administration without cross-tenant control."
    ],
    remainingTitle: "RBAC Console implementation",
    remaining: [
      "Create and edit roles through the console.",
      "Assign and remove permissions and groups.",
      "Display inherited and effective permissions clearly.",
      "Audit every role, permission, and group change with old/new snapshots."
    ],
    warning: "The shared role catalogue is verified. RBAC Console completion is a product-administration programme, not an open AUD8 defect."
  },
  access_grants: {
    title: "Access Grants",
    status: "Backend Verified / UI In Progress",
    summary: "Opportunity and quote access grants are tenant-safe and visibility-aware. The remaining requirement is a complete administration surface for assigning, revoking, and explaining effective access.",
    complete: [
      "Opportunity and quote access routes are authenticated and tenant-scoped.",
      "Visibility-aware list and Records Management search endpoints are in place.",
      "Grant and revoke operations are represented in the API surface and audit model.",
      "Cross-tenant direct-record access passed the closure harness."
    ],
    remainingTitle: "Administration work",
    remaining: [
      "Expose access grants and revocations clearly in the RBAC/record administration UI.",
      "Show direct, inherited, and group-derived access separately.",
      "Add dependency and audit views for access changes.",
      "Add regression fixtures when new grant types are introduced."
    ],
    warning: "Tenant isolation is signed off; the remaining work is visibility and administration UX."
  },
  cross_tenant_selectors: {
    title: "Cross-Tenant Selectors",
    status: "Signed Off",
    summary: "The staff/API selector surface is signed off. Tenant-scoped lists, taxonomy, users, resources, customers, contacts, jobs, sales records, settings, and shared-catalogue exceptions were exercised through the closure harness.",
    complete: [
      "Tenant 1 and Tenant 2 list endpoints returned isolated operational data.",
      "Cross-tenant direct-ID replay was blocked where unique records existed.",
      "Legacy tenant_id query parameters were rejected or safely ignored.",
      "Shared registries such as roles, widgets, and standard report templates were classified explicitly rather than treated as leaks.",
      "Theme switching and taxonomy selectors were previously verified without tenant bleed."
    ],
    remainingTitle: "Regression maintenance",
    remaining: [
      "Add every newly introduced selector to the closure harness.",
      "Create Tenant 2 fixtures for new modules when empty datasets prevent direct-ID replay.",
      "Require explicit, audited tenant context for any future Super Admin cross-tenant selector."
    ],
    warning: "AUD8 selector isolation is signed off. New selector code must not bypass the established tenant-context pattern."
  },
  workflow_templates: {
    title: "Workflow Templates",
    status: "Signed Off",
    summary: "Workflow template administration, versions, steps, actions, runtime, planner, and reporting routes were included in the route-security and closure exercises.",
    complete: [
      "Workflow template, version, step, action, binding, category, runtime, planner, assignment, override, retry, and reporting routes require authentication.",
      "Tenant context is derived from the authenticated user and DB actor context is set for workflow mutations.",
      "Tenant 1 workflow records were not exposed to Tenant 2.",
      "Empty Tenant 2 workflow fixtures are recorded as N/A rather than failures."
    ],
    remainingTitle: "Product delivery work",
    remaining: [
      "Populate richer Tenant 2 workflow fixtures as workflow implementation expands.",
      "Complete end-to-end workflow product behaviour, planner UX, notifications, approvals, and reporting.",
      "Keep published-version immutability and taxonomy-binding tests in regression coverage."
    ],
    warning: "Workflow tenant isolation is signed off. Runtime and UX completion remain separate product-delivery work."
  },
  workflow_template_bindings: {
    title: "Workflow Template Bindings",
    status: "Signed Off",
    summary: "Workflow-template bindings are authenticated, tenant-scoped, and aligned to tenant-owned taxonomy.",
    complete: [
      "Binding list, read, create, update, and deactivate routes are authenticated.",
      "Taxonomy parents are tenant-owned and cross-tenant attachment is blocked.",
      "The final staff/API closure found no binding isolation failure.",
      "Empty Tenant 2 binding fixtures are classified as N/A, not failed isolation."
    ],
    remainingTitle: "Regression maintenance",
    remaining: [
      "Add direct cross-tenant binding replay fixtures when Tenant 2 workflow templates are created.",
      "Keep default-binding resolution and reporting joins in regression coverage."
    ],
    warning: "Binding isolation is signed off. Additional fixtures improve evidence depth but do not represent an open defect."
  },
  job_work_packages: {
    title: "Job Work Packages",
    status: "Implementation Pending",
    summary: "Job work packages remain a product implementation and governance area. They were not falsely marked as an AUD8 failure.",
    complete: [
      "The workflow architecture defines the intended tenant-safe relationship between jobs, packages, workflows, tasks, assignments, and reports.",
      "Existing nested workflow routes validate job and work-package context through authenticated tenant ownership."
    ],
    remainingTitle: "Implementation work",
    remaining: [
      "Complete work-package schema, UI, lifecycle, and dependency inspection.",
      "Validate composite tenant-safe foreign keys across jobs, workflows, tasks, documents, uploads, and reports.",
      "Add work-package Records Management search and dependency views.",
      "Add fixture-backed regression tests when the feature is complete."
    ],
    warning: "Keep this as Implementation Pending until the product surface is complete and testable."
  },
  document_previews: {
    title: "Document Previews",
    status: "AUD9 Hardened / Portal Validation Remaining",
    summary: "Staff document ownership remains signed off and AUD9 has now removed unintended anonymous access from portal document listing, diagnostics, health and the document-service health endpoint. Portal customer scope is derived server-side. Preview/download ownership still requires real portal identities.",
    complete: [
      "Document upload, update, signature request, bulk flag update, and delete audit paths were validated.",
      "Staff document routes use canonical bearer authentication and authenticated tenant context.",
      "Document list isolation passed the staff/API closure harness.",
      "Private access is enforced before exposing file URLs rather than relying on public static-path authentication.",
      "Portal document routes no longer trust X-User-Id and reject anonymous requests with 401.",
      "Document service /api/documents/_health now requires the existing get_current_user dependency.",
      "Portal customer context is derived from the authenticated principal and database errors are sanitised."
    ],
    remainingTitle: "Remaining portal ownership checks",
    remaining: [
      "Verify portal preview URLs with portal identities and customer-scoped fixtures.",
      "Verify published/private document scope across customers and tenants.",
      "Verify download and preview resolvers reject foreign customer and job identifiers."
    ],
    warning: "Anonymous document exposure is remediated. Final portal sign-off still requires portal-authenticated preview/download and cross-customer ownership tests."
  },
  sharepoint_documents: {
    title: "SharePoint Documents",
    status: "Settings Signed Off / Portal Validation Remaining",
    summary: "SharePoint settings remain tenant-scoped and portal document listing now requires an authenticated principal. Customer/job folder visibility and resolver ownership still require real portal identities before final portal sign-off.",
    complete: [
      "SharePoint settings derive tenant context from the authenticated user.",
      "Tenant 2 returning 404 when no SharePoint configuration exists is accepted safe behaviour.",
      "Document upload integration and customer/job folder conventions are implemented.",
      "No staff/API SharePoint tenant-isolation failure remained in the closure run."
    ],
    remainingTitle: "Portal validation and integration work",
    remaining: [
      "Verify portal document lists with portal identities and customer scope.",
      "Verify customer/job folder URLs cannot be resolved through foreign IDs.",
      "Verify secret masking and explicit audited Super Admin configuration targeting."
    ],
    warning: "SharePoint staff settings and anonymous route hardening are complete; portal folder/document ownership remains a portal-identity validation responsibility."
  },
  customer_logos: {
    title: "Customer Logos",
    status: "Implementation Pending",
    summary: "Customer records are tenant-safe. Rich customer-logo management remains a UI and storage feature rather than an AUD8 blocker.",
    complete: [
      "Customer list, detail, lifecycle, and ownership checks are signed off.",
      "Tenant theme logos are already tenant-isolated.",
      "Customer records cannot be read through foreign tenant IDs."
    ],
    remainingTitle: "Feature implementation",
    remaining: [
      "Add customer-logo upload, replacement, preview, and removal controls.",
      "Store logo references against tenant-owned customer records and tenant-owned paths.",
      "Add MIME, size, filename, orphan-file, and audit controls.",
      "Add logo endpoints to the regression harness when implemented."
    ],
    warning: "Customer tenant isolation is signed off. Customer-logo support is a separate feature enhancement."
  },
  contact_avatars: {
    title: "Contact Avatars",
    status: "Implementation Pending",
    summary: "Contact records and user avatar routes are tenant-safe. Dedicated contact-avatar management remains a feature enhancement.",
    complete: [
      "Contact list, detail, customer ownership, lifecycle, and direct-ID isolation are signed off.",
      "User avatar upload and preview now resolve strictly within the authenticated tenant.",
      "The cross-tenant user/avatar bypass was removed and verified with a 404 response."
    ],
    remainingTitle: "Feature implementation",
    remaining: [
      "Implement dedicated contact-avatar upload and preview behaviour if required.",
      "Use tenant-owned paths, MIME/size checks, cache-busting, orphan detection, and audit events.",
      "Add the final contact-avatar endpoints to regression coverage when implemented."
    ],
    warning: "Contact and user-avatar isolation are signed off. Dedicated contact avatars remain product work."
  },
  uploads: {
    title: "Uploads",
    status: "Tenant Isolation Signed Off / Governance In Progress",
    summary: "Known upload routes are authenticated and job ownership is tenant-scoped. Storage retention, conversion, cleanup, and orphan governance remain operational work.",
    complete: [
      "Tenant-aware storage structure is defined under /data/{tenant_id}/uploads.",
      "Known job upload routes validate authenticated job ownership.",
      "The closure exercise found no confirmed cross-tenant upload API failure."
    ],
    remainingTitle: "Storage governance",
    remaining: [
      "Complete tenant-path verification for raw files, ZIP extraction, conversions, snapshots, and processing logs.",
      "Add dry-run cleanup and orphan detection before destructive purge controls.",
      "Define retention and audit requirements for temporary and generated files."
    ],
    warning: "API isolation is signed off; storage governance must still prevent shared-folder fallbacks and untracked files."
  },
  exports: {
    title: "Exports",
    status: "Tenant Isolation Signed Off / Retention Pending",
    summary: "Report preview and export routes are authenticated and operate through tenant-scoped report data. Export retention and temporary-file cleanup remain operational governance items.",
    complete: [
      "Report run, preview, full export, filtered export, and UID export routes require authentication.",
      "Standard templates are intentionally shared while tenant-created templates remain tenant-owned.",
      "The staff/API closure found no confirmed report/export tenant leak."
    ],
    remainingTitle: "Operational governance",
    remaining: [
      "Define retention and cleanup for generated exports and temporary files.",
      "Track generated-file ownership and expiry consistently.",
      "Add portal export/download checks to AUD9 where applicable."
    ],
    warning: "Export tenant isolation is signed off. File lifecycle and portal download governance remain separate work."
  },
  orphaned_files: {
    title: "Orphaned Files",
    status: "Planned",
    summary: "Orphaned-file detection is a future data-integrity and storage-governance control.",
    complete: [
      "The need for database-path versus disk-file comparison is recorded.",
      "Large-backup retention and cleanup are tracked as maintenance work."
    ],
    remainingTitle: "Implementation work",
    remaining: [
      "Build a tenant-grouped dry-run scanner for files without database references.",
      "Build a scanner for database paths that reference missing files.",
      "Require explicit retention, purge, and audit rules before deletion.",
      "Add safe remediation and evidence export."
    ],
    warning: "Do not introduce destructive orphan cleanup until dry-run evidence and purge governance are complete."
  },
  job_runs: {
    title: "Job Runs",
    status: "Implementation / Governance Pending",
    summary: "Job-run processing spans jobs, cameras, uploads, sightings, sessions, snapshots, logs, and reports. Existing ANPR isolation is signed off; a consolidated Job Runs Records Management surface remains to be built.",
    complete: [
      "Camera devices, ingest events, sightings, and plate sessions have tenant-safe Records Management coverage.",
      "Job and ANPR report surfaces passed tenant-isolation checks.",
      "Tenant-aware upload-path requirements are defined."
    ],
    remainingTitle: "Implementation work",
    remaining: [
      "Build a consolidated job-run search and dependency view.",
      "Link runs to jobs, cameras, uploads, sightings, sessions, reports, and generated assets.",
      "Add retention and lifecycle governance for source and generated files.",
      "Add fixture-backed job-run regression tests."
    ],
    warning: "ANPR tenant isolation is signed off. Consolidated Job Runs governance remains product work."
  },
  alerts: {
    title: "Alerts",
    status: "Tenant Isolation Signed Off / Lifecycle In Progress",
    summary: "Alert API isolation is signed off. Polling, auto-clear, role filtering, and administration remain product lifecycle work.",
    complete: [
      "Alert list, active/all aliases, create, and delete routes require authentication.",
      "Tenant 1 and Tenant 2 alert list probes completed without a confirmed cross-tenant leak.",
      "Backend dismissal replaces local-only state.",
      "Alert flyout and widget share backend data."
    ],
    remainingTitle: "Product lifecycle work",
    remaining: [
      "Complete polling and self-recovering auto-clear behaviour.",
      "Define final tenant, role, and global-alert policy.",
      "Complete alert administration and settings UX.",
      "Keep new alert selectors and counts in regression coverage."
    ],
    warning: "Tenant isolation is signed off; alert lifecycle and policy remain product decisions."
  },
  reports: {
    title: "Reports",
    status: "Signed Off",
    summary: "Report templates, report execution, preview, export, and reporting data surfaces passed the staff/API tenant-isolation closure after shared standard templates were classified correctly.",
    complete: [
      "Report list and template endpoints require authenticated access.",
      "Standard report templates are intentionally shared across tenants.",
      "Tenant-created custom templates remain tenant-scoped; Tenant 1 custom template 39 was not listed to Tenant 2.",
      "Report run, weaving, preview, full export, filtered export, and UID export use authenticated tenant context.",
      "The closure harness found no confirmed report tenant-isolation failure."
    ],
    remainingTitle: "Regression and product work",
    remaining: [
      "Create a Tenant 2 custom template fixture to strengthen bidirectional direct-ID replay evidence.",
      "Keep new report datasets, joins, fields, previews, and exports in the regression harness.",
      "Complete any remaining report-builder UX enhancements independently of AUD8."
    ],
    warning: "Reports are signed off for staff/API tenant isolation. Missing Tenant 2 custom fixtures are an evidence-depth gap, not a confirmed leak."
  },
  report_templates: {
    title: "Report Templates",
    status: "Signed Off",
    summary: "Standard templates are shared by design; custom templates are tenant-owned. The final closure correctly distinguishes those two models.",
    complete: [
      "Tenant 1 listed custom template 39 while Tenant 2 did not list it.",
      "Both tenants listed the same standard templates by design.",
      "Template create, read, update, delete, available-fields, run, preview, and export routes require authentication.",
      "Tenant and creator ownership are enforced server-side for custom templates."
    ],
    remainingTitle: "Evidence maintenance",
    remaining: [
      "Create one Tenant 2 custom template fixture for a bidirectional direct-ID regression test.",
      "Retain shared-standard versus tenant-custom classification in future harness versions."
    ],
    warning: "Template isolation is signed off. A Tenant 2 custom fixture would improve symmetry but is not evidence of a current defect."
  },
  report_logs: {
    title: "Report Logs",
    status: "Signed Off",
    summary: "Report logging and export evidence are tenant/user-owned forensic records and are included in the completed reports audit surface.",
    complete: [
      "Report ownership requirements include tenant_id and user_id.",
      "Preview/export activity is linked to authenticated report execution context.",
      "Audit infrastructure records actor, tenant, route, status, and request evidence.",
      "No cross-tenant report-history exposure was confirmed in the closure exercise."
    ],
    remainingTitle: "Operational enhancement",
    remaining: [
      "Add retention and purge policy for report logs and generated files.",
      "Expand Records Management reporting-log views if operational users need richer forensic search."
    ],
    warning: "Report-log tenant isolation is signed off. Retention and richer forensic UX remain operational enhancements."
  },
  widgets: {
    title: "Widgets",
    status: "Shared Registry Verified",
    summary: "The widget catalogue is intentionally shared. Tenant-specific layouts, counts, data, role visibility, and effective access remain tenant-scoped.",
    complete: [
      "Both tenants receive the same 66-widget registry by design.",
      "Widget layout endpoints validate current user and tenant context.",
      "RoleStatsWidget counts are tenant-scoped and theme-aware.",
      "Registry-sync operations are authenticated and administrative.",
      "The closure exercise found no confirmed widget tenant-isolation failure."
    ],
    remainingTitle: "Product and regression work",
    remaining: [
      "Continue validating each new widget's data queries, counts, and selectors.",
      "Complete role/category visibility administration in the RBAC Console.",
      "Keep tenant switching and theme behaviour in UI regression tests."
    ],
    warning: "The shared widget registry is verified. Widget-specific data queries must remain tenant-scoped as new widgets are added."
  },
  taxonomy: {
    title: "Taxonomy",
    status: "Fixed / Signed Off",
    summary: "The three-level job taxonomy was tenantised, constrained, seeded, and CRUD-tested across Tenant 1 and Tenant 2.",
    complete: [
      "job_types, job_subtypes, and job_subtypes2 contain tenant_id.",
      "Tenant-safe constraints and tenant-filtered CRUD are in place.",
      "Cross-tenant parent attachment is blocked.",
      "Tenant 2 contains 10 pharmaceutical job types, 27 subtypes, and 59 subtype2 records.",
      "Tenant 1 taxonomy was preserved.",
      "Create, update, and delete were verified through the UI for both tenants and smoke records were removed.",
      "Workflow and report selector consumers passed the final staff/API closure."
    ],
    remainingTitle: "Future generation work",
    remaining: [
      "Make the Bootstrap Generator create industry-specific tenant taxonomy rather than cloning Tenant 1.",
      "Keep new taxonomy consumers in regression coverage."
    ],
    warning: "Taxonomy and its current staff/API consumers are signed off. Bootstrap automation remains separate work."
  },
  settings: {
    title: "Settings",
    status: "Signed Off",
    summary: "Tenant themes and authenticated settings surfaces derive tenant context server-side. Safe 404 responses for unconfigured tenant integrations are accepted behaviour.",
    complete: [
      "Tenant 2 colours, fonts, and logos were configured and verified without cached theme bleed.",
      "UI config authenticated updates are tenant-scoped; public GET fallback remains limited to login bootstrap behaviour.",
      "Weather, traffic, messaging, SharePoint, and scheduler/settings routes use authenticated tenant context.",
      "Tenant 2 SharePoint settings returning 404 when unconfigured is safe and expected.",
      "No confirmed settings tenant-isolation failure remained in the closure run."
    ],
    remainingTitle: "Administration enhancements",
    remaining: [
      "Complete secret masking and configuration-health UX across integrations.",
      "Expose explicit audited tenant targeting only where a future Super Admin workflow genuinely needs it.",
      "Keep new settings endpoints and selectors in regression coverage."
    ],
    warning: "Settings tenant isolation is signed off. Administration UX and explicit Super Admin tooling remain product work."
  },
  tenant_isolation: {
    title: "Tenant Isolation",
    status: "Signed Off",
    summary: "AUD8 staff/API tenant isolation is complete. The final selected closure run recorded 575 checks, 253 PASS, 0 FAIL, 48 classified REVIEW items, and 274 safe-ID skips.",
    complete: AUD8_TENANT_ISOLATION_FINDINGS.map((item) => `${item.label}: ${item.detail}`),
    remainingTitle: "Post-AUD8 programme",
    remaining: POST_AUD8_PROGRAMME.map((item) => `${item.label}: ${item.detail}`),
    warning: "No confirmed staff/API tenant-isolation failures remain. Portal security proceeds separately under AUD9."
  },
  bootstrap_generator: {
    title: "Bootstrap Generator",
    status: "Design Validated / Build Pending",
    summary: "The Bootstrap Generator is defined as a governed Super Admin module for realistic, reversible tenant creation and test-data generation.",
    complete: BOOTSTRAP_GENERATOR_RULES,
    remainingTitle: "Implementation work",
    remaining: [
      "Build preview and generation UI under System or Administration.",
      "Implement Insert Directly, Download SQL, and Download CSV modes.",
      "Create bootstrap_generated_records batch tracking for safe cleanup.",
      "Enforce tenant-safe foreign keys and selector validation during generation.",
      "Add industry templates and deterministic seed options."
    ],
    warning: "Do not implement this as an unmanaged script. It requires preview, audit, repeatability, and safe cleanup."
  },
  tenant_seed_data: {
    title: "Tenant Seed Data",
    status: "Signed Off",
    summary: "Tenant 2 seed data successfully exposed tenant-isolation issues and supported the final AUD8 closure exercise.",
    complete: [
      "Apex BioSystems, Northbridge Therapeutics, and Clinical Pathways were seeded under tenant_id=2 with valid public-style domains.",
      "Eight contacts were created with realistic portal access and security levels.",
      "Tenant 2 contains realistic customers, contacts, resources, account-manager assignments, and user-resource mappings.",
      "Tenant 2 admin user 199 was mapped to an active sales resource for quote and opportunity testing.",
      "Tenant 2 pharmaceutical taxonomy contains 10 job types, 27 subtypes, and 59 subtype2 records.",
      "The data enabled discovery of the user cross-tenant bypass that was then fixed."
    ],
    remainingTitle: "Future fixture work",
    remaining: [
      "Generate portal identities and portal-owned fixtures for AUD9.",
      "Add richer workflows, documents, reports, terms, pricebooks, and WhatsApp fixtures when deeper bidirectional tests are required.",
      "Batch-track future generated records for safe deletion."
    ],
    warning: "The current Tenant 2 seed set is signed off for AUD8. Future fixtures should be produced through the Bootstrap Generator."
  },
  deleted_records: {
    title: "Deleted Records",
    status: "Planned",
    summary: "Deleted Records will become the central recovery and governed purge queue across archive-capable domains.",
    complete: [
      "Archive/restore lifecycle exists for quotes, opportunities, customers, contacts, and jobs.",
      "Hard delete remains disabled by design in governed lifecycle domains."
    ],
    remainingTitle: "Implementation work",
    remaining: [
      "Build a central tenant-scoped soft-deleted record listing.",
      "Add dependency-aware restore workflows.",
      "Define retention, legal hold, purge approval, and audit rules.",
      "Capture actor and tenant evidence for every restore and purge action."
    ],
    warning: "Keep this non-destructive until recovery, retention, dependency, and purge governance are complete."
  },
  audit_roadmap: {
    title: "Audit Roadmap",
    status: "AUD8 Signed Off / AUD9 Substantially Complete",
    summary: "The security roadmap now records completed staff/API tenant isolation and the substantially complete AUD9 unauthenticated endpoint review, while separating residual portal-identity validation, public-endpoint policy decisions, and product implementation programmes.",
    complete: [
      "SEC1-SEC19 route security hardening and closure sweeps are complete, verified, or formally deferred.",
      "AUD6 workflow route review and smoke tests are complete.",
      "AUD8 staff/API tenant isolation is signed off with zero confirmed failures in the final selected run.",
      "The implicit Super Admin cross-tenant user bypass was fixed and regression-tested.",
      "Theme isolation, taxonomy tenantisation, shared-catalogue classification, reports, widgets, settings, and core operational domains are signed off.",
      "AUD9 analysed 478 operations, reduced critical unexpected anonymous successes from 12 to 6, increased denied-as-expected to 201, and skipped 271 mutation endpoints for safety.",
      "Portal Documents, Portal Job Access, portal diagnostics, portal health, and document-service health now reject anonymous access; X-User-Id trust was removed."
    ],
    remainingTitle: "Next programme",
    remaining: POST_AUD8_PROGRAMME.map((item) => `${item.label}: ${item.detail}`),
    warning: "AUD9 application-route hardening is substantially complete. Remaining portal-identity validation, intentional-public endpoint classification, UI-config separation, RBAC, Bootstrap, Deleted Records, and lifecycle UX are tracked as distinct follow-on work."
  }
};


const RESOURCE_REDACTION_PRINCIPLES = [
  "Resources now follow a dedicated reassign/redact pattern rather than the simple archive/restore pattern used by quotes, contacts, customers, and jobs.",
  "Before redaction, assignments, ownership, access grants, planner visibility, opportunities, quotes, jobs, tasks, and audit ownership are reviewed through the dependency modal.",
  "The implemented control pattern is: inspect dependencies → choose replacement resource → reassign active dependencies → re-check dependencies → redact personal fields only when dependency count is zero.",
  "Hard delete remains disabled for resources unless a separate purge governance process proves no historical reporting, audit, payroll, job, quote, or assignment dependency remains.",
];

const QUOTE_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Submitted" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "sent", label: "Sent" },
  { value: "expired", label: "Expired" },
  { value: "archived", label: "Archived" },
 ];

const OPPORTUNITY_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "open", label: "Open" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
  { value: "archived", label: "Archived" },
];

const JOB_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "processing", label: "Processing" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "archived", label: "Archived" },
];

const AUDIT_ENTITY_OPTIONS = [
  { value: "", label: "All entities" },
  { value: "quote", label: "Quotes" },
  { value: "opportunity", label: "Opportunities" },
  { value: "contact", label: "Contacts" },
  { value: "customer", label: "Customers" },
  { value: "document", label: "Document" },
  { value: "job", label: "Jobs" },
  { value: "resource", label: "Resources" },
  { value: "camera_device", label: "Camera Devices" },
  { value: "camera_ingest_event", label: "Camera Ingest Events" },
  { value: "anpr_sighting", label: "ANPR Sightings" },
  { value: "workflow", label: "Workflow Runs" },
  { value: "workflow_template", label: "Workflow Templates" },
  { value: "workflow_template_version", label: "Workflow Template Versions" },
  { value: "workflow_template_step", label: "Workflow Template Steps" },
  { value: "workflow_template_step_action", label: "Workflow Template Step Actions" },
  { value: "workflow_template_binding", label: "Workflow Template Bindings" },
  { value: "workflow_template_category", label: "Workflow Template Categories" },
  { value: "job_task", label: "Job Tasks" },
  { value: "job_task_assignment", label: "Job Task Assignments" },
];

const AUDIT_ACTION_OPTIONS = [
  { value: "", label: "All actions" },
  { value: "dependency_viewed", label: "Dependency viewed" },
  { value: "update", label: "Updated" },
  { value: "delete", label: "Deleted" },
  { value: "archive", label: "Archive" },
  { value: "restore", label: "Restore" },
  { value: "reassign", label: "Reassign" },
  { value: "redact", label: "Redact" },
  { value: "ingest_disabled", label: "Ingest disabled" },
  { value: "ingest_enabled", label: "Ingest enabled" },
  { value: "deactivate", label: "Deactivate" },
  { value: "reactivate", label: "Reactivate" },
  { value: "delete_attempt", label: "Delete attempt" },
  { value: "complete", label: "Complete" },
  { value: "reopen", label: "Reopen" },
  { value: "approve", label: "Approve" },
  { value: "reject", label: "Reject" },
  { value: "override_requested", label: "Override requested" },
  { value: "override_approved", label: "Override approved" },
  { value: "override_rejected", label: "Override rejected" },
  { value: "override_applied", label: "Override applied" },
  { value: "publish", label: "Publish" },
  { value: "retry", label: "Retry" },
];

const safeText = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return String(value);
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 2,
  }).format(numeric);
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const normaliseCollection = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.quotes)) return payload.quotes;
  if (Array.isArray(payload?.opportunities)) return payload.opportunities;
  if (Array.isArray(payload?.customers)) return payload.customers;
  if (Array.isArray(payload?.jobs)) return payload.jobs;
  if (Array.isArray(payload?.audit_logs)) return payload.audit_logs;
  return [];
};

const formatJsonPreview = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch (error) {
    return String(value);
  }
};

const prettifyAuditKey = (value) => {
  if (!value) return "";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatAuditSummary = (row) => {
  const details = row?.details || {};
  const entityType = prettifyAuditKey(row?.entity_type || details.module || "record");
  const label = details.entity_label || details.label || details.entity_reference || row?.entity_id;

  if (row?.action === "dependency_viewed") {
    const counts = details.dependency_counts || {};
    const countParts = Object.entries(counts)
      .filter(([, value]) => Number(value || 0) > 0)
      .slice(0, 4)
      .map(([key, value]) => `${prettifyAuditKey(key)}: ${value}`);

    return countParts.length > 0
      ? `Viewed dependencies for ${entityType}: ${label} — ${countParts.join(" · ")}`
      : `Viewed dependencies for ${entityType}: ${label}`;
  }

  if (row?.action === "archive") {
    return `Archived ${entityType}: ${label}`;
  }

  if (row?.action === "restore") {
    return `Restored ${entityType}: ${label}`;
  }

  if (row?.action === "reassign") {
    const replacement = details.replacement_resource_label || details.replacement_resource_id || "replacement resource";
    return `Reassigned ${entityType}: ${label} → ${replacement}`;
  }

  if (row?.action === "redact") {
    return `Redacted ${entityType}: ${label}`;
  }

  if (row?.action === "delete_attempt") {
    return `Delete attempt for ${entityType}: ${label}`;
  }

  return `${prettifyAuditKey(row?.action || "Audit event")} for ${entityType}: ${label}`;
};

const dependencyHref = (groupKey, record) => {
  const id = record?.id;
  if (!id) return "";

  const routes = {
    customer: `/dashboard/customers/${id}`,
    customers: `/dashboard/customers/${id}`,
    contact: `/dashboard/contacts/${id}`,
    contacts: `/dashboard/contacts/${id}`,
    opportunity: `/dashboard/opportunities/${id}`,
    opportunities: `/dashboard/opportunities/${id}`,
    quote: `/dashboard/quotes/${id}`,
    quotes: `/dashboard/quotes/${id}`,
    job: `/dashboard/jobs/${id}`,
    jobs: `/dashboard/jobs/${id}`,
    documents: `/dashboard/documents/${id}`,
    owner_resource: `/dashboard/resources/${id}`,
    workflow_runs: `/dashboard/jobs/${record?.job_id || id}`,
    tasks: `/dashboard/jobs/${record?.job_id || id}`,
  };

  return routes[groupKey] || "";
};

const dependencyGroupsFromResult = (dependencyResult) => {
  if (!dependencyResult) return [];
  if (Array.isArray(dependencyResult.dependency_groups)) return dependencyResult.dependency_groups;

  const counts = dependencyResult.dependency_counts || {};
  return Object.entries(counts).map(([key, value]) => ({
    key,
    label: key.replace(/_/g, " "),
    count: Number(value || 0),
    records: [],
    blocking_delete: false,
    severity: "info",
    note: "Legacy dependency count only. Backend should return dependency_groups for full detail.",
  }));
};

const DependencyModal = ({ open, quote, dependencyResult, loading, error, onClose, onRefresh, styles }) => {
  if (!open) return null;

  const groups = dependencyGroupsFromResult(dependencyResult);
  const visibleGroups = groups.filter((group) => Number(group.count || 0) > 0 || (group.records || []).length > 0);
  const blockers = groups.filter((group) => group.blocking_delete && Number(group.count || 0) > 0);
  const quoteLabel = safeText(quote?.quote_ref || quote?.reference || quote?.id, "Selected quote");

  return (
    <ModalWrapper
      title="Quote dependency map"
      subtitle={`${quoteLabel} — linked records that affect archive, restore, delete, reporting, and recovery decisions.`}
      onClose={onClose}
      maxWidth="980px"
      width="min(980px, 96vw)"
    >
      <div style={styles.modalBody}>
        {loading && <div style={styles.emptyPanel}>Loading dependency details...</div>}
        {error && <div style={styles.warning}>{error}</div>}

        {!loading && !error && (
          <>
            <div style={blockers.length > 0 ? styles.warning : styles.safePanel}>
              <strong>Recommendation:</strong> {safeText(dependencyResult?.recommendation, "Archive/restore is safe. Hard delete remains disabled.")}
            </div>

            <div className="rm-modal-stats-grid" style={styles.modalStatsGrid}>
              <div style={styles.statCard}>
                <p style={styles.statValue}>{groups.reduce((sum, group) => sum + Number(group.count || 0), 0)}</p>
                <p style={styles.statLabel}>Linked records</p>
                <p style={styles.statDetail}>Across known quote dependency tables.</p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statValue}>{blockers.length}</p>
                <p style={styles.statLabel}>Hard-delete blockers</p>
                <p style={styles.statDetail}>Delete remains disabled regardless.</p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statValue}>{dependencyResult?.can_edit ? "Yes" : "No"}</p>
                <p style={styles.statLabel}>Can edit</p>
                <p style={styles.statDetail}>Backend visibility/edit check result.</p>
              </div>
            </div>

            {visibleGroups.length === 0 ? (
              <div style={{ ...styles.safePanel, marginTop: 14 }}>
                No linked records were returned by the dependency endpoint.
              </div>
            ) : (
              <div style={styles.dependencyModalList}>
                {visibleGroups.map((group) => (
                  <div key={group.key} style={styles.dependencyModalGroup(group.severity, group.blocking_delete)}>
                    <div style={styles.dependencyModalGroupHeader}>
                      <div>
                        <div style={{ fontWeight: 900 }}>{group.label}</div>
                        <div style={styles.subtle}>{group.note || group.table}</div>
                      </div>
                      <span style={styles.statusBadge}>{Number(group.count || 0)}</span>
                    </div>

                    {(group.records || []).length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                        {(group.records || []).map((record, index) => {
                          const href = dependencyHref(group.key, record);
                          return (
                            <div key={`${group.key}-${record.id || index}`} style={styles.dependencyRecordRow}>
                              <div>
                                <div style={{ fontWeight: 800 }}>{safeText(record.label || record.id)}</div>
                                <div style={styles.subtle}>
                                  ID: {safeText(record.id)}{record.status ? ` · ${record.status}` : ""}
                                </div>
                              </div>
                              {href ? (
                                <button type="button" onClick={() => window.open(href, "_blank", "noopener,noreferrer")} style={styles.secondaryButton}>
                                  Open
                                </button>
                              ) : (
                                <span style={styles.subtle}>No route</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ ...styles.subtle, marginTop: 10 }}>
                        {Number(group.count || 0)} linked record(s). No preview rows returned.
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div className="rm-modal-footer" style={styles.modalFooter}>
          <button type="button" onClick={onRefresh} style={styles.secondaryButton} disabled={!quote?.id || loading}>
            Refresh dependencies
          </button>
          <button type="button" onClick={onClose} style={styles.primaryButton}>
            Close
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

const StaticRecordSection = ({ section, styles, accent }) => {
  const completeItems = Array.isArray(section?.complete) ? section.complete : [];
  const remainingItems = Array.isArray(section?.remaining) ? section.remaining : [];

  return (
    <>
      <div className="rm-hero" style={styles.hero}>
        <div className="rm-title-row" style={styles.titleRow}>
          <div>
            <p style={styles.eyebrow}>Records Management</p>
            <h3 style={styles.h3}>{section.title}</h3>
            <p style={styles.description}>{section.summary}</p>
          </div>
          <span style={{
            ...styles.badge,
            ...(String(section.status || "").toLowerCase().includes("signed off") ||
            String(section.status || "").toLowerCase().includes("verified") ||
            String(section.status || "").toLowerCase().includes("complete")
              ? { background: "#ecfdf5", color: "#047857", border: "1px solid #10b98155" }
              : {})
          }}>{section.status}</span>
        </div>
      </div>

      <div className="rm-split-grid" style={styles.splitGrid}>
        <div className="rm-panel-card" style={styles.placeholder}>
          <h4 style={{ margin: "0 0 12px 0", fontSize: 16, fontWeight: 900 }}>Completed / verified</h4>
          {completeItems.length ? (
            <div style={styles.checkList}>
              {completeItems.map((item) => (
                <div key={item} style={styles.checkItem}>{item}</div>
              ))}
            </div>
          ) : (
            <div style={styles.emptyPanel}>No completed evidence has been recorded for this section yet.</div>
          )}
        </div>

        <div className="rm-panel-card" style={styles.placeholder}>
          <h4 style={{ margin: "0 0 12px 0", fontSize: 16, fontWeight: 900 }}>
            {section.remainingTitle || "Remaining checks"}
          </h4>
          {remainingItems.length ? (
            <div style={styles.checkList}>
              {remainingItems.map((item) => (
                <div key={item} style={styles.checkItem}>{item}</div>
              ))}
            </div>
          ) : (
            <div style={styles.safePanel}>No remaining checks are currently recorded.</div>
          )}
        </div>
      </div>

      {section.warning ? (
        <div style={styles.warning}>
          <strong style={{ color: "inherit" }}>Sign-off note: </strong>{section.warning}
        </div>
      ) : null}

      <div style={{ ...styles.safePanel, borderColor: `${accent}55`, background: `${accent}10`, color: "#374151" }}>
        Updated 2026-07-29: statuses reflect the final AUD8 staff/API closure and the substantially complete AUD9 unauthenticated endpoint review. Remaining items are portal-identity validation, intentional-public endpoint policy, UI-config separation, RBAC implementation, governance, or regression work unless explicitly stated otherwise.
      </div>
    </>
  );
};

const QuoteManagementSection = ({ styles, accent, actionButton }) => {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [dependencyResult, setDependencyResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dependencyLoading, setDependencyLoading] = useState(false);
  const [error, setError] = useState("");
  const [dependencyError, setDependencyError] = useState("");
  const [dependencyModalOpen, setDependencyModalOpen] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [actionMessage, setActionMessage] = useState("");
  const [actionError, setActionError] = useState("");

  const loadQuotes = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        q: query || undefined,
        status: status || undefined,
        include_archived: includeArchived ? "true" : "false",
        limit: 50,
      };
      const response = await api.get("/quotes/admin/search", { params });
      const rows = normaliseCollection(response.data);
      setQuotes(rows);
      if (selectedQuote && !rows.some((row) => String(row.id) === String(selectedQuote.id))) {
        setSelectedQuote(null);
        setDependencyResult(null);
      }
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Quote admin search failed. Backend endpoint /api/quotes/admin/search may not exist yet."
      );
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  }, [includeArchived, query, selectedQuote, status]);

  useEffect(() => {
    loadQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewDependencies = async (quote, openModal = true) => {
    if (!quote?.id) return;
    setSelectedQuote(quote);
    setDependencyResult(null);
    setDependencyError("");
    if (openModal) setDependencyModalOpen(true);
    setDependencyLoading(true);
    try {
      const response = await api.get(`/quotes/${quote.id}/dependencies`);
      setDependencyResult(response.data || {});
    } catch (err) {
      setDependencyError(
        err?.response?.data?.detail ||
          err?.message ||
          "Quote dependency preview failed. Backend endpoint /api/quotes/{id}/dependencies may not exist yet."
      );
    } finally {
      setDependencyLoading(false);
    }
  };


  const runQuoteLifecycleAction = async (quote, action) => {
    if (!quote?.id || !action) return;

    const label = action === "archive" ? "archive" : "restore";
    const confirmed = window.confirm(`Confirm ${label} for quote ${quote.quote_ref || quote.reference || quote.id}?`);
    if (!confirmed) return;

    setActionLoadingId(`${quote.id}:${action}`);
    setActionError("");
    setActionMessage("");

    try {
      const response = await api.post(`/quotes/${quote.id}/${action}`);
      const updatedQuote = response.data?.quote || quote;
      setSelectedQuote(updatedQuote);
      setDependencyResult(response.data?.dependencies || null);
      setActionMessage(`Quote ${quote.quote_ref || quote.reference || quote.id} ${action === "archive" ? "archived" : "restored"}.`);
      await loadQuotes();
    } catch (err) {
      setActionError(
        err?.response?.data?.detail ||
          err?.message ||
          `Quote ${label} failed.`
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  const statusBadge = (value) => {
    const lower = String(value || "unknown").toLowerCase();
    const background = lower === "archived" ? "#f3f4f6" : `${accent}14`;
    const color = lower === "archived" ? "#4b5563" : accent;
    return <span style={{ ...styles.statusBadge, background, color }}>{safeText(value, "unknown")}</span>;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="rm-hero" style={styles.hero}>
        <div className="rm-title-row" style={styles.titleRow}>
          <div>
            <p style={styles.eyebrow}>CRM / Quotes</p>
            <h3 style={styles.h3}>Quote Records Management</h3>
            <p style={styles.description}>
              First production domain for the new Records Management pattern. This view is intentionally safe:
              it can search and preview dependencies, but archive/delete/restore actions remain disabled until
              backend soft-delete, audit logging, and dependency enforcement are implemented.
            </p>
          </div>
          <span style={styles.badge}>Phase RM2: safe quote controls</span>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <div className="rm-toolbar-grid" style={styles.toolbarGrid}>
          <label style={styles.fieldLabel}>
            Search quotes
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Quote ref, customer, opportunity, status..."
              style={styles.input}
            />
          </label>

          <label style={styles.fieldLabel}>
            Status
            <select value={status} onChange={(event) => setStatus(event.target.value)} style={styles.input}>
              {QUOTE_STATUS_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(event) => setIncludeArchived(event.target.checked)}
            />
            Include archived
          </label>

          <button type="button" onClick={loadQuotes} disabled={loading} style={styles.primaryButton}>
            {loading ? "Loading..." : "Search"}
          </button>
        </div>

        {error && <div style={styles.warning}>{error}</div>}
        {actionError && <div style={styles.warning}>{actionError}</div>}
        {actionMessage && <div style={styles.safePanel}>{actionMessage}</div>}

        <div className="rm-table-shell" style={styles.tableShell}>
          <table className="rm-responsive-table" style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Quote</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Opportunity</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Total</th>
                <th style={styles.th}>Updated</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {quotes.length === 0 && !loading && (
                <tr>
                  <td className="rm-empty-table-cell" colSpan="7" style={styles.emptyTableCell}>
                    No quote records loaded. If the backend endpoint is not built yet, this table will populate once
                    /api/quotes/admin/search is added.
                  </td>
                </tr>
              )}

              {quotes.map((quote) => {
                const isSelected = selectedQuote && String(selectedQuote.id) === String(quote.id);
                return (
                  <tr key={quote.id || quote.quote_ref || JSON.stringify(quote)} style={isSelected ? styles.selectedRow : undefined}>
                    <td data-label="Quote" style={styles.td}>
                      <div style={{ fontWeight: 900 }}>{safeText(quote.quote_ref || quote.reference || quote.id)}</div>
                      <div style={styles.subtle}>ID: {safeText(quote.id)}</div>
                    </td>
                    <td data-label="Customer" style={styles.td}>{safeText(quote.customer_name || quote.customer || quote.customer_id)}</td>
                    <td data-label="Opportunity" style={styles.td}>{safeText(quote.opportunity_name || quote.opportunity || quote.opportunity_id)}</td>
                    <td data-label="Status" style={styles.td}>{statusBadge(quote.is_archived ? "archived" : quote.status)}</td>
                    <td data-label="Total" style={styles.td}>{formatMoney(quote.total_amount || quote.total || quote.amount || quote.grand_total)}</td>
                    <td data-label="Updated" style={styles.td}>{formatDateTime(quote.updated_at || quote.created_at)}</td>
                    <td data-label="Actions" style={styles.td}>
                      <div className="rm-action-row" style={styles.actionRow}>
                        <button type="button" onClick={() => previewDependencies(quote)} style={styles.secondaryButton}>
                          Dependencies
                        </button>
                        {quote.is_archived ? (
                          <button
                            type="button"
                            onClick={() => runQuoteLifecycleAction(quote, "restore")}
                            disabled={actionLoadingId === `${quote.id}:restore`}
                            style={styles.secondaryButton}
                          >
                            {actionLoadingId === `${quote.id}:restore` ? "Restoring..." : "Restore"}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => runQuoteLifecycleAction(quote, "archive")}
                            disabled={actionLoadingId === `${quote.id}:archive`}
                            style={styles.archiveButton}
                          >
                            {actionLoadingId === `${quote.id}:archive` ? "Archiving..." : "Archive"}
                          </button>
                        )}
                        <button type="button" disabled style={styles.disabledDangerButton} title="Dependency-enforced delete endpoint required first">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 900 }}>Quote lifecycle controls</h4>
        <div style={styles.checkList}>
          <div style={styles.checkItem}>Dependencies now open in a modal, not at the bottom of the page.</div>
          <div style={styles.checkItem}>Active quotes show Archive.</div>
          <div style={styles.checkItem}>Archived quotes show Restore.</div>
          <div style={styles.checkItem}>Delete remains disabled until controlled soft-delete/recovery/purge governance exists.</div>
          <div style={styles.checkItem}>No client-supplied tenant_id anywhere.</div>
        </div>
        <div style={{ ...styles.safePanel, marginTop: 12, borderColor: `${actionButton}33` }}>
          Archive and restore are live lifecycle actions. Dependency preview now shows linked record categories and available open links.
        </div>
      </div>

      <DependencyModal
        open={dependencyModalOpen}
        quote={selectedQuote}
        dependencyResult={dependencyResult}
        loading={dependencyLoading}
        error={dependencyError}
        onClose={() => setDependencyModalOpen(false)}
        onRefresh={() => selectedQuote && previewDependencies(selectedQuote, true)}
        styles={styles}
      />
    </div>
  );
};



const DocumentManagementSection = ({ styles, accent, actionButton }) => {
  const [query, setQuery] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [jobId, setJobId] = useState("");
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [actionMessage, setActionMessage] = useState("");
  const [actionError, setActionError] = useState("");

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        q: query || undefined,
        customer_id: customerId || undefined,
        job_id: jobId || undefined,
        limit: 50,
      };
      const response = await api.get("/documents/", { params });
      setDocuments(normaliseCollection(response.data));
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Document search failed. Confirm /api/documents/ is mounted and hardened with bearer auth."
      );
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [customerId, jobId, query]);

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patchDocumentFlags = async (document, patch) => {
    if (!document?.id) return;
    setActionLoadingId(`${document.id}:flags`);
    setActionError("");
    setActionMessage("");
    try {
      const response = await api.patch(`/documents/${document.id}`, patch);
      const updated = response.data || {};
      setDocuments((rows) => rows.map((row) => (String(row.id) === String(document.id) ? { ...row, ...updated } : row)));
      const changed = Object.keys(patch).join(", ");
      setActionMessage(`Document ${document.name || document.id} updated (${changed}). Audit action: update.`);
    } catch (err) {
      setActionError(err?.response?.data?.detail || err?.message || "Document flag update failed.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const requestSignature = async (document) => {
    if (!document?.id) return;

    const signerName = window.prompt("Signer name", "AUD5 Test Signer");
    if (!signerName) return;

    const signerEmail = window.prompt("Signer email", "audit-test@example.com");
    if (!signerEmail) return;

    setActionLoadingId(`${document.id}:sign`);
    setActionError("");
    setActionMessage("");
    try {
      const response = await api.post(`/documents/${document.id}/sign`, {
        version: Number(document.latest_version || 1),
        signer_name: signerName,
        signer_email: signerEmail,
      });
      setActionMessage(
        `Signature request created for ${document.name || document.id}. Signature ID: ${response.data?.signature_id || "-"}. Audit action: signature_requested.`
      );
    } catch (err) {
      setActionError(err?.response?.data?.detail || err?.message || "Signature request failed.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const statusBadge = (enabled, label) => {
    const background = enabled ? `${accent}14` : "#f3f4f6";
    const color = enabled ? accent : "#4b5563";
    return <span style={{ ...styles.statusBadge, background, color }}>{label}</span>;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="rm-hero" style={styles.hero}>
        <div className="rm-title-row" style={styles.titleRow}>
          <div>
            <p style={styles.eyebrow}>Documents & Storage / Documents</p>
            <h3 style={styles.h3}>Document Records Management</h3>
            <p style={styles.description}>
              AUD5 staff document controls are complete and AUD9 anonymous-route hardening is substantially complete. This section
              lists tenant-scoped documents through the authenticated document route and exposes safe controls for portal visibility,
              downloadability, and signature request metadata. Portal document listing, diagnostics, health, and document-service
              health now reject anonymous callers; final preview/download ownership sign-off still requires real portal identities.
            </p>
          </div>
          <span style={styles.badge}>AUD5 signed off / AUD9 hardened</span>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <div className="rm-toolbar-grid" style={styles.toolbarGrid}>
          <label style={styles.fieldLabel}>
            Search documents
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Name, key, tag, MIME type..."
              style={styles.input}
            />
          </label>

          <label style={styles.fieldLabel}>
            Customer ID
            <input
              value={customerId}
              onChange={(event) => setCustomerId(event.target.value.replace(/[^0-9]/g, ""))}
              placeholder="Optional"
              style={styles.input}
            />
          </label>

          <label style={styles.fieldLabel}>
            Job ID
            <input
              value={jobId}
              onChange={(event) => setJobId(event.target.value.replace(/[^0-9]/g, ""))}
              placeholder="Optional"
              style={styles.input}
            />
          </label>

          <button type="button" onClick={loadDocuments} disabled={loading} style={styles.primaryButton}>
            {loading ? "Loading..." : "Search"}
          </button>
        </div>

        {error && <div style={styles.warning}>{error}</div>}
        {actionError && <div style={styles.warning}>{actionError}</div>}
        {actionMessage && <div style={styles.safePanel}>{actionMessage}</div>}

        <div className="rm-table-shell" style={styles.tableShell}>
          <table className="rm-responsive-table" style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Document</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Job</th>
                <th style={styles.th}>Flags</th>
                <th style={styles.th}>Size</th>
                <th style={styles.th}>Updated</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents.length === 0 && !loading && (
                <tr>
                  <td className="rm-empty-table-cell" colSpan="7" style={styles.emptyTableCell}>
                    No documents loaded. The hardened endpoint is /api/documents/ and requires bearer authentication.
                  </td>
                </tr>
              )}

              {documents.map((document) => (
                <tr key={document.id || document.document_key || JSON.stringify(document)}>
                  <td data-label="Document" style={styles.td}>
                    <div style={{ fontWeight: 900 }}>{safeText(document.name || document.filename || document.id)}</div>
                    <div style={styles.subtle}>ID: {safeText(document.id)} · v{safeText(document.latest_version, "1")}</div>
                    {document.latest_url ? (
                      <a
                        href={document.latest_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ ...styles.inlineLink, display: "inline-block", marginTop: 4 }}
                        title={document.latest_url}
                      >
                        Document URL
                      </a>
                    ) : (
                      <div style={{ ...styles.subtle, marginTop: 4 }}>{safeText(document.document_key, "No document key")}</div>
                    )}
                  </td>
                  <td data-label="Customer" style={styles.td}>{safeText(document.customer_id)}</td>
                  <td data-label="Job" style={styles.td}>{safeText(document.job_id)}</td>
                  <td data-label="Flags" style={styles.td}>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {statusBadge(Boolean(document.portal_visible), document.portal_visible ? "Portal" : "Private")}
                      {statusBadge(Boolean(document.downloadable), document.downloadable ? "Downloadable" : "No download")}
                    </div>
                  </td>
                  <td data-label="Size" style={styles.td}>{safeText(document.size_bytes)}</td>
                  <td data-label="Updated" style={styles.td}>{formatDateTime(document.updated_at || document.created_at)}</td>
                  <td data-label="Actions" style={styles.td}>
                    <div className="rm-action-row" style={styles.actionRow}>
                      <button
                        type="button"
                        onClick={() => patchDocumentFlags(document, { portal_visible: !Boolean(document.portal_visible) })}
                        disabled={actionLoadingId === `${document.id}:flags`}
                        style={styles.secondaryButton}
                      >
                        {document.portal_visible ? "Make private" : "Show in portal"}
                      </button>
                      <button
                        type="button"
                        onClick={() => patchDocumentFlags(document, { downloadable: !Boolean(document.downloadable) })}
                        disabled={actionLoadingId === `${document.id}:flags`}
                        style={styles.secondaryButton}
                      >
                        {document.downloadable ? "Disable download" : "Enable download"}
                      </button>
                      <button
                        type="button"
                        onClick={() => requestSignature(document)}
                        disabled={actionLoadingId === `${document.id}:sign`}
                        style={styles.secondaryButton}
                      >
                        Signature
                      </button>
                      <button
                        type="button"
                        disabled
                        style={styles.disabledDangerButton}
                        title="Delete audit is verified, but Records Management UI must wait for purge governance before exposing document deletion."
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 900 }}>Document audit and security controls</h4>
        <div style={styles.checkList}>
          <div style={styles.checkItem}>Document route has been hardened: bearer auth required, tenant comes from current_user, and app.user_id is set per request.</div>
          <div style={styles.checkItem}>Flag updates write document update audit entries with old/new snapshots and changed_fields.</div>
          <div style={styles.checkItem}>Bulk flag updates write batch audit entries with requested and updated counts.</div>
          <div style={styles.checkItem}>Signature requests write signature_requested audit entries. Current signature table still needs created_at added later.</div>
          <div style={styles.checkItem}>Upload and delete audit paths are verified, but delete is not exposed here until controlled purge governance exists.</div>
        </div>
        <div style={{ ...styles.safePanel, marginTop: 12, borderColor: `${actionButton}33` }}>
          Uploads are still treated as a separate storage/raw-file governance area. The Uploads navigation item should remain planned until raw upload paths, orphan-file checks, and tenant storage cleanup are audited separately.
        </div>
      </div>
    </div>
  );
};


const CustomerManagementSection = ({ styles, accent, actionButton }) => {
  const [query, setQuery] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [dependencyResult, setDependencyResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dependencyLoading, setDependencyLoading] = useState(false);
  const [error, setError] = useState("");
  const [dependencyError, setDependencyError] = useState("");
  const [dependencyModalOpen, setDependencyModalOpen] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [actionMessage, setActionMessage] = useState("");
  const [actionError, setActionError] = useState("");

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        q: query || undefined,
        include_archived: includeArchived ? "true" : "false",
        limit: 50,
      };
      const response = await api.get("/customers/admin/search", { params });
      const rows = normaliseCollection(response.data);
      setCustomers(rows);
      if (selectedCustomer && !rows.some((row) => String(row.id) === String(selectedCustomer.id))) {
        setSelectedCustomer(null);
        setDependencyResult(null);
      }
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Customer admin search failed. Backend endpoint /api/customers/admin/search may not exist yet."
      );
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [includeArchived, query, selectedCustomer]);

  useEffect(() => {
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewDependencies = async (customer, openModal = true) => {
    if (!customer?.id) return;
    setSelectedCustomer(customer);
    setDependencyResult(null);
    setDependencyError("");
    if (openModal) setDependencyModalOpen(true);
    setDependencyLoading(true);
    try {
      const response = await api.get(`/customers/${customer.id}/dependencies`);
      setDependencyResult(response.data || {});
    } catch (err) {
      setDependencyError(
        err?.response?.data?.detail ||
          err?.message ||
          "Customer dependency preview failed. Backend endpoint /api/customers/{id}/dependencies may not exist yet."
      );
    } finally {
      setDependencyLoading(false);
    }
  };

  const runCustomerLifecycleAction = async (customer, action) => {
    if (!customer?.id || !action) return;

    const label = action === "archive" ? "archive" : "restore";
    const confirmed = window.confirm(`Confirm ${label} for customer ${customer.name || customer.internal_reference || customer.id}?`);
    if (!confirmed) return;

    setActionLoadingId(`${customer.id}:${action}`);
    setActionError("");
    setActionMessage("");

    try {
      const response = await api.post(`/customers/${customer.id}/${action}`);
      const updatedCustomer = response.data?.customer || customer;
      setSelectedCustomer(updatedCustomer);
      setDependencyResult(response.data?.dependencies || null);
      setActionMessage(`Customer ${customer.name || customer.internal_reference || customer.id} ${action === "archive" ? "archived" : "restored"}.`);
      await loadCustomers();
    } catch (err) {
      setActionError(
        err?.response?.data?.detail ||
          err?.message ||
          `Customer ${label} failed.`
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  const groups = dependencyGroupsFromResult(dependencyResult);
  const visibleGroups = groups.filter((group) => Number(group.count || 0) > 0 || (group.records || []).length > 0);
  const blockers = groups.filter((group) => group.blocking_delete && Number(group.count || 0) > 0);
  const selectedLabel = safeText(selectedCustomer?.name || selectedCustomer?.internal_reference || selectedCustomer?.id, "Selected customer");

  const statusBadge = (value) => {
    const lower = String(value || "active").toLowerCase();
    const background = lower === "archived" ? "#f3f4f6" : `${accent}14`;
    const color = lower === "archived" ? "#4b5563" : accent;
    return <span style={{ ...styles.statusBadge, background, color }}>{safeText(value, "active")}</span>;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="rm-hero" style={styles.hero}>
        <div className="rm-title-row" style={styles.titleRow}>
          <div>
            <p style={styles.eyebrow}>CRM / Customers</p>
            <h3 style={styles.h3}>Customer Records Management</h3>
            <p style={styles.description}>
              Fourth CRM domain using the Records Management lifecycle pattern. This view supports customer search,
              relationship context, dependency mapping, archive, and restore. Delete remains disabled until controlled
              soft-delete/recovery/purge governance exists.
            </p>
          </div>
          <span style={styles.badge}>Phase RM6A: customers lifecycle</span>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <div className="rm-toolbar-grid" style={{ ...styles.toolbarGrid, gridTemplateColumns: "minmax(240px, 1fr) 150px 120px" }}>
          <label style={styles.fieldLabel}>
            Search customers
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Name, reference, email, company number, postcode..."
              style={styles.input}
            />
          </label>

          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(event) => setIncludeArchived(event.target.checked)}
            />
            Include archived
          </label>

          <button type="button" onClick={loadCustomers} disabled={loading} style={styles.primaryButton}>
            {loading ? "Loading..." : "Search"}
          </button>
        </div>

        {error && <div style={styles.warning}>{error}</div>}
        {actionError && <div style={styles.warning}>{actionError}</div>}
        {actionMessage && <div style={styles.safePanel}>{actionMessage}</div>}

        <div className="rm-table-shell" style={styles.tableShell}>
          <table className="rm-responsive-table" style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Reference</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Phone</th>
                <th style={styles.th}>Lifecycle</th>
                <th style={styles.th}>Relationship</th>
                <th style={styles.th}>State</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.length === 0 && !loading && (
                <tr>
                  <td className="rm-empty-table-cell" colSpan="8" style={styles.emptyTableCell}>
                    No customer records loaded. If the backend endpoint is not built yet, this table will populate once
                    /api/customers/admin/search is added.
                  </td>
                </tr>
              )}

              {customers.map((customer) => {
                const isSelected = selectedCustomer && String(selectedCustomer.id) === String(customer.id);
                return (
                  <tr key={customer.id || customer.internal_reference || JSON.stringify(customer)} style={isSelected ? styles.selectedRow : undefined}>
                    <td data-label="Customer" style={styles.td}>
                      <div style={{ fontWeight: 900 }}>{safeText(customer.name || customer.id)}</div>
                      <div style={styles.subtle}>ID: {safeText(customer.id)}</div>
                    </td>
                    <td data-label="Reference" style={styles.td}>{safeText(customer.internal_reference)}</td>
                    <td data-label="Email" style={styles.td}>{safeText(customer.email)}</td>
                    <td data-label="Phone" style={styles.td}>{safeText(customer.phone_number)}</td>
                    <td data-label="Lifecycle" style={styles.td}>{safeText(customer.lifecycle_type || customer.status)}</td>
                    <td data-label="Relationship" style={styles.td}>{safeText(customer.relationship_status || customer.category)}</td>
                    <td data-label="State" style={styles.td}>{statusBadge(customer.is_archived ? "archived" : "active")}</td>
                    <td data-label="Actions" style={styles.td}>
                      <div className="rm-action-row" style={styles.actionRow}>
                        <button type="button" onClick={() => previewDependencies(customer)} style={styles.secondaryButton}>
                          Dependencies
                        </button>
                        {customer.is_archived ? (
                          <button
                            type="button"
                            onClick={() => runCustomerLifecycleAction(customer, "restore")}
                            disabled={actionLoadingId === `${customer.id}:restore`}
                            style={styles.secondaryButton}
                          >
                            {actionLoadingId === `${customer.id}:restore` ? "Restoring..." : "Restore"}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => runCustomerLifecycleAction(customer, "archive")}
                            disabled={actionLoadingId === `${customer.id}:archive`}
                            style={styles.archiveButton}
                          >
                            {actionLoadingId === `${customer.id}:archive` ? "Archiving..." : "Archive"}
                          </button>
                        )}
                        <button type="button" disabled style={styles.disabledDangerButton} title="Dependency-enforced delete endpoint required first">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 900 }}>Customer lifecycle controls</h4>
        <div style={styles.checkList}>
          <div style={styles.checkItem}>Search is tenant-scoped from current user context.</div>
          <div style={styles.checkItem}>Dependency modal shows contacts, child customers, opportunities, quotes, jobs, documents, communications, and account manager context where available.</div>
          <div style={styles.checkItem}>Active customers show Archive.</div>
          <div style={styles.checkItem}>Archived customers show Restore.</div>
          <div style={styles.checkItem}>Delete remains disabled until controlled soft-delete/recovery/purge governance exists.</div>
        </div>
      </div>

      {dependencyModalOpen && (
        <ModalWrapper
          title="Customer dependency map"
          subtitle={`${selectedLabel} — linked records that affect archive, restore, delete, reporting, and recovery decisions.`}
          onClose={() => setDependencyModalOpen(false)}
          maxWidth="980px"
          width="min(980px, 96vw)"
        >
          <div style={styles.modalBody}>
            {dependencyLoading && <div style={styles.emptyPanel}>Loading dependency details...</div>}
            {dependencyError && <div style={styles.warning}>{dependencyError}</div>}

            {!dependencyLoading && !dependencyError && (
              <>
                <div style={blockers.length > 0 ? styles.warning : styles.safePanel}>
                  <strong>Recommendation:</strong> {safeText(dependencyResult?.recommendation, "Archive/restore is safe. Hard delete remains disabled.")}
                </div>

                <div className="rm-modal-stats-grid" style={styles.modalStatsGrid}>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{groups.reduce((sum, group) => sum + Number(group.count || 0), 0)}</p>
                    <p style={styles.statLabel}>Linked records</p>
                    <p style={styles.statDetail}>Across known customer dependency tables.</p>
                  </div>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{blockers.length}</p>
                    <p style={styles.statLabel}>Hard-delete blockers</p>
                    <p style={styles.statDetail}>Delete remains disabled regardless.</p>
                  </div>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{dependencyResult?.can_edit ? "Yes" : "No"}</p>
                    <p style={styles.statLabel}>Can edit</p>
                    <p style={styles.statDetail}>Backend lifecycle permission result.</p>
                  </div>
                </div>

                {visibleGroups.length === 0 ? (
                  <div style={{ ...styles.safePanel, marginTop: 14 }}>
                    No linked records were returned by the dependency endpoint.
                  </div>
                ) : (
                  <div style={styles.dependencyModalList}>
                    {visibleGroups.map((group) => (
                      <div key={group.key} style={styles.dependencyModalGroup(group.severity, group.blocking_delete)}>
                        <div style={styles.dependencyModalGroupHeader}>
                          <div>
                            <div style={{ fontWeight: 900 }}>{group.label}</div>
                            <div style={styles.subtle}>{group.note || group.table}</div>
                          </div>
                          <span style={styles.statusBadge}>{Number(group.count || 0)}</span>
                        </div>

                        {(group.records || []).length > 0 ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                            {(group.records || []).map((record, index) => {
                              const href = dependencyHref(group.key, record);
                              return (
                                <div key={`${group.key}-${record.id || index}`} style={styles.dependencyRecordRow}>
                                  <div>
                                    <div style={{ fontWeight: 800 }}>{safeText(record.label || record.id)}</div>
                                    <div style={styles.subtle}>
                                      ID: {safeText(record.id)}{record.status ? ` · ${record.status}` : ""}
                                    </div>
                                  </div>
                                  {href ? (
                                    <button type="button" onClick={() => window.open(href, "_blank", "noopener,noreferrer")} style={styles.secondaryButton}>
                                      Open
                                    </button>
                                  ) : (
                                    <span style={styles.subtle}>No route</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div style={{ ...styles.subtle, marginTop: 10 }}>
                            {Number(group.count || 0)} linked record(s). No preview rows returned.
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            <div className="rm-modal-footer" style={styles.modalFooter}>
              <button type="button" onClick={() => selectedCustomer && previewDependencies(selectedCustomer, true)} style={styles.secondaryButton} disabled={!selectedCustomer?.id || dependencyLoading}>
                Refresh dependencies
              </button>
              <button type="button" onClick={() => setDependencyModalOpen(false)} style={styles.primaryButton}>
                Close
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}
    </div>
  );
};



const JobManagementSection = ({ styles, accent, actionButton }) => {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [dependencyResult, setDependencyResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dependencyLoading, setDependencyLoading] = useState(false);
  const [error, setError] = useState("");
  const [dependencyError, setDependencyError] = useState("");
  const [dependencyModalOpen, setDependencyModalOpen] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [actionMessage, setActionMessage] = useState("");
  const [actionError, setActionError] = useState("");

  const loadJobs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        q: query || undefined,
        status: status || undefined,
        include_archived: includeArchived ? "true" : "false",
        limit: 50,
      };
      const response = await api.get("/jobs/admin/search", { params });
      const rows = normaliseCollection(response.data);
      setJobs(rows);
      if (selectedJob && !rows.some((row) => String(row.id) === String(selectedJob.id))) {
        setSelectedJob(null);
        setDependencyResult(null);
      }
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Job admin search failed. Backend endpoint /api/jobs/admin/search may not exist yet."
      );
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [includeArchived, query, selectedJob, status]);

  useEffect(() => {
    loadJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewDependencies = async (job, openModal = true) => {
    if (!job?.id) return;
    setSelectedJob(job);
    setDependencyResult(null);
    setDependencyError("");
    if (openModal) setDependencyModalOpen(true);
    setDependencyLoading(true);
    try {
      const response = await api.get(`/jobs/${job.id}/dependencies`);
      setDependencyResult(response.data || {});
    } catch (err) {
      setDependencyError(
        err?.response?.data?.detail ||
          err?.message ||
          "Job dependency preview failed. Backend endpoint /api/jobs/{id}/dependencies may not exist yet."
      );
    } finally {
      setDependencyLoading(false);
    }
  };

  const runJobLifecycleAction = async (job, action) => {
    if (!job?.id || !action) return;

    const label = action === "archive" ? "archive" : "restore";
    const confirmed = window.confirm(`Confirm ${label} for job ${job.job_ref || job.id}?`);
    if (!confirmed) return;

    setActionLoadingId(`${job.id}:${action}`);
    setActionError("");
    setActionMessage("");

    try {
      const response = await api.post(`/jobs/${job.id}/${action}`);
      const updatedJob = response.data?.job || job;
      setSelectedJob(updatedJob);
      setDependencyResult(response.data?.dependencies || null);
      setActionMessage(`Job ${job.job_ref || job.id} ${action === "archive" ? "archived" : "restored"}.`);
      await loadJobs();
    } catch (err) {
      setActionError(
        err?.response?.data?.detail ||
          err?.message ||
          `Job ${label} failed.`
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  const groups = dependencyGroupsFromResult(dependencyResult);
  const visibleGroups = groups.filter((group) => Number(group.count || 0) > 0 || (group.records || []).length > 0);
  const blockers = groups.filter((group) => group.blocking_delete && Number(group.count || 0) > 0);
  const selectedLabel = safeText(selectedJob?.job_ref || selectedJob?.id, "Selected job");

  const statusBadge = (value) => {
    const lower = String(value || "unknown").toLowerCase();
    const background = lower === "archived" ? "#f3f4f6" : `${accent}14`;
    const color = lower === "archived" ? "#4b5563" : accent;
    return <span style={{ ...styles.statusBadge, background, color }}>{safeText(value, "unknown")}</span>;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="rm-hero" style={styles.hero}>
        <div className="rm-title-row" style={styles.titleRow}>
          <div>
            <p style={styles.eyebrow}>Operations / Jobs</p>
            <h3 style={styles.h3}>Job Records Management</h3>
            <p style={styles.description}>
              First Operations domain using the Records Management lifecycle pattern. This view supports job search,
              customer/contact context, dependency mapping, archive, and restore. Delete remains disabled until controlled
              soft-delete/recovery/purge governance exists.
            </p>
          </div>
          <span style={styles.badge}>Phase RM7A: jobs lifecycle</span>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <div className="rm-toolbar-grid" style={styles.toolbarGrid}>
          <label style={styles.fieldLabel}>
            Search jobs
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Job ref, customer, contact, model type, status..."
              style={styles.input}
            />
          </label>

          <label style={styles.fieldLabel}>
            Status
            <select value={status} onChange={(event) => setStatus(event.target.value)} style={styles.input}>
              {JOB_STATUS_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(event) => setIncludeArchived(event.target.checked)}
            />
            Include archived
          </label>

          <button type="button" onClick={loadJobs} disabled={loading} style={styles.primaryButton}>
            {loading ? "Loading..." : "Search"}
          </button>
        </div>

        {error && <div style={styles.warning}>{error}</div>}
        {actionError && <div style={styles.warning}>{actionError}</div>}
        {actionMessage && <div style={styles.safePanel}>{actionMessage}</div>}

        <div className="rm-table-shell" style={styles.tableShell}>
          <table className="rm-responsive-table" style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Job</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Contact</th>
                <th style={styles.th}>Model</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Progress</th>
                <th style={styles.th}>Week Commencing</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0 && !loading && (
                <tr>
                  <td className="rm-empty-table-cell" colSpan="8" style={styles.emptyTableCell}>
                    No job records loaded. If the backend endpoint is not built yet, this table will populate once
                    /api/jobs/admin/search is added.
                  </td>
                </tr>
              )}

              {jobs.map((job) => {
                const isSelected = selectedJob && String(selectedJob.id) === String(job.id);
                return (
                  <tr key={job.id || job.job_ref || JSON.stringify(job)} style={isSelected ? styles.selectedRow : undefined}>
                    <td data-label="Job" style={styles.td}>
                      <div style={{ fontWeight: 900 }}>{safeText(job.job_ref || job.id)}</div>
                      <div style={styles.subtle}>ID: {safeText(job.id)}</div>
                    </td>
                    <td data-label="Customer" style={styles.td}>{safeText(job.customer_name || job.customer_id)}</td>
                    <td data-label="Contact" style={styles.td}>{safeText(job.contact_name || job.contact_id)}</td>
                    <td data-label="Model" style={styles.td}>{safeText(job.model_type)}</td>
                    <td data-label="Status" style={styles.td}>{statusBadge(job.is_archived ? "archived" : job.status)}</td>
                    <td data-label="Progress" style={styles.td}>{safeText(job.progress)}</td>
                    <td data-label="Week Commencing" style={styles.td}>{safeText(job.week_commencing)}</td>
                    <td data-label="Actions" style={styles.td}>
                      <div className="rm-action-row" style={styles.actionRow}>
                        <button type="button" onClick={() => previewDependencies(job)} style={styles.secondaryButton}>
                          Dependencies
                        </button>
                        {job.is_archived ? (
                          <button
                            type="button"
                            onClick={() => runJobLifecycleAction(job, "restore")}
                            disabled={actionLoadingId === `${job.id}:restore`}
                            style={styles.secondaryButton}
                          >
                            {actionLoadingId === `${job.id}:restore` ? "Restoring..." : "Restore"}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => runJobLifecycleAction(job, "archive")}
                            disabled={actionLoadingId === `${job.id}:archive`}
                            style={styles.archiveButton}
                          >
                            {actionLoadingId === `${job.id}:archive` ? "Archiving..." : "Archive"}
                          </button>
                        )}
                        <button type="button" disabled style={styles.disabledDangerButton} title="Dependency-enforced delete endpoint required first">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 900 }}>Job lifecycle controls</h4>
        <div style={styles.checkList}>
          <div style={styles.checkItem}>Search is tenant-scoped from current user context.</div>
          <div style={styles.checkItem}>Dependency modal shows customer, contact, assignments, workflow runs, tasks, uploads, ANPR runs, sightings, and detections where available.</div>
          <div style={styles.checkItem}>Active jobs show Archive.</div>
          <div style={styles.checkItem}>Archived jobs show Restore.</div>
          <div style={styles.checkItem}>Delete remains disabled until controlled soft-delete/recovery/purge governance exists.</div>
        </div>
      </div>

      {dependencyModalOpen && (
        <ModalWrapper
          title="Job dependency map"
          subtitle={`${selectedLabel} — linked records that affect archive, restore, delete, reporting, and recovery decisions.`}
          onClose={() => setDependencyModalOpen(false)}
          maxWidth="980px"
          width="min(980px, 96vw)"
        >
          <div style={styles.modalBody}>
            {dependencyLoading && <div style={styles.emptyPanel}>Loading dependency details...</div>}
            {dependencyError && <div style={styles.warning}>{dependencyError}</div>}

            {!dependencyLoading && !dependencyError && (
              <>
                <div style={blockers.length > 0 ? styles.warning : styles.safePanel}>
                  <strong>Recommendation:</strong> {safeText(dependencyResult?.recommendation, "Archive/restore is safe. Hard delete remains disabled.")}
                </div>

                <div className="rm-modal-stats-grid" style={styles.modalStatsGrid}>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{groups.reduce((sum, group) => sum + Number(group.count || 0), 0)}</p>
                    <p style={styles.statLabel}>Linked records</p>
                    <p style={styles.statDetail}>Across known job dependency tables.</p>
                  </div>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{blockers.length}</p>
                    <p style={styles.statLabel}>Hard-delete blockers</p>
                    <p style={styles.statDetail}>Delete remains disabled regardless.</p>
                  </div>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{dependencyResult?.can_edit ? "Yes" : "No"}</p>
                    <p style={styles.statLabel}>Can edit</p>
                    <p style={styles.statDetail}>Backend lifecycle permission result.</p>
                  </div>
                </div>

                {visibleGroups.length === 0 ? (
                  <div style={{ ...styles.safePanel, marginTop: 14 }}>
                    No linked records were returned by the dependency endpoint.
                  </div>
                ) : (
                  <div style={styles.dependencyModalList}>
                    {visibleGroups.map((group) => (
                      <div key={group.key} style={styles.dependencyModalGroup(group.severity, group.blocking_delete)}>
                        <div style={styles.dependencyModalGroupHeader}>
                          <div>
                            <div style={{ fontWeight: 900 }}>{group.label}</div>
                            <div style={styles.subtle}>{group.note || group.table}</div>
                          </div>
                          <span style={styles.statusBadge}>{Number(group.count || 0)}</span>
                        </div>

                        {(group.records || []).length > 0 ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                            {(group.records || []).map((record, index) => {
                              const href = dependencyHref(group.key, record);
                              return (
                                <div key={`${group.key}-${record.id || index}`} style={styles.dependencyRecordRow}>
                                  <div>
                                    <div style={{ fontWeight: 800 }}>{safeText(record.label || record.id)}</div>
                                    <div style={styles.subtle}>
                                      ID: {safeText(record.id)}{record.status ? ` · ${record.status}` : ""}
                                    </div>
                                  </div>
                                  {href ? (
                                    <button type="button" onClick={() => window.open(href, "_blank", "noopener,noreferrer")} style={styles.secondaryButton}>
                                      Open
                                    </button>
                                  ) : (
                                    <span style={styles.subtle}>No route</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div style={{ ...styles.subtle, marginTop: 10 }}>
                            {Number(group.count || 0)} linked record(s). No preview rows returned.
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            <div className="rm-modal-footer" style={styles.modalFooter}>
              <button type="button" onClick={() => selectedJob && previewDependencies(selectedJob, true)} style={styles.secondaryButton} disabled={!selectedJob?.id || dependencyLoading}>
                Refresh dependencies
              </button>
              <button type="button" onClick={() => setDependencyModalOpen(false)} style={styles.primaryButton}>
                Close
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}
    </div>
  );
};

const ContactManagementSection = ({ styles, accent, actionButton }) => {
  const [query, setQuery] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [dependencyResult, setDependencyResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dependencyLoading, setDependencyLoading] = useState(false);
  const [error, setError] = useState("");
  const [dependencyError, setDependencyError] = useState("");
  const [dependencyModalOpen, setDependencyModalOpen] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [actionMessage, setActionMessage] = useState("");
  const [actionError, setActionError] = useState("");

  const loadContacts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        q: query || undefined,
        include_archived: includeArchived ? "true" : "false",
        limit: 50,
      };
      const response = await api.get("/contacts/admin/search", { params });
      const rows = normaliseCollection(response.data);
      setContacts(rows);
      if (selectedContact && !rows.some((row) => String(row.id) === String(selectedContact.id))) {
        setSelectedContact(null);
        setDependencyResult(null);
      }
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Contact admin search failed. Backend endpoint /api/contacts/admin/search may not exist yet."
      );
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [includeArchived, query, selectedContact]);

  useEffect(() => {
    loadContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewDependencies = async (contact, openModal = true) => {
    if (!contact?.id) return;
    setSelectedContact(contact);
    setDependencyResult(null);
    setDependencyError("");
    if (openModal) setDependencyModalOpen(true);
    setDependencyLoading(true);
    try {
      const response = await api.get(`/contacts/${contact.id}/dependencies`);
      setDependencyResult(response.data || {});
    } catch (err) {
      setDependencyError(
        err?.response?.data?.detail ||
          err?.message ||
          "Contact dependency preview failed. Backend endpoint /api/contacts/{id}/dependencies may not exist yet."
      );
    } finally {
      setDependencyLoading(false);
    }
  };

  const runContactLifecycleAction = async (contact, action) => {
    if (!contact?.id || !action) return;

    const label = action === "archive" ? "archive" : "restore";
    const confirmed = window.confirm(`Confirm ${label} for contact ${contact.contact_name || contact.email || contact.id}?`);
    if (!confirmed) return;

    setActionLoadingId(`${contact.id}:${action}`);
    setActionError("");
    setActionMessage("");

    try {
      const response = await api.post(`/contacts/${contact.id}/${action}`);
      const updatedContact = response.data?.contact || contact;
      setSelectedContact(updatedContact);
      setDependencyResult(response.data?.dependencies || null);
      setActionMessage(`Contact ${contact.contact_name || contact.email || contact.id} ${action === "archive" ? "archived" : "restored"}.`);
      await loadContacts();
    } catch (err) {
      setActionError(
        err?.response?.data?.detail ||
          err?.message ||
          `Contact ${label} failed.`
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  const groups = dependencyGroupsFromResult(dependencyResult);
  const visibleGroups = groups.filter((group) => Number(group.count || 0) > 0 || (group.records || []).length > 0);
  const blockers = groups.filter((group) => group.blocking_delete && Number(group.count || 0) > 0);
  const selectedLabel = safeText(selectedContact?.contact_name || selectedContact?.email || selectedContact?.id, "Selected contact");

  const statusBadge = (value) => {
    const lower = String(value || "active").toLowerCase();
    const background = lower === "archived" ? "#f3f4f6" : `${accent}14`;
    const color = lower === "archived" ? "#4b5563" : accent;
    return <span style={{ ...styles.statusBadge, background, color }}>{safeText(value, "active")}</span>;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="rm-hero" style={styles.hero}>
        <div className="rm-title-row" style={styles.titleRow}>
          <div>
            <p style={styles.eyebrow}>CRM / Contacts</p>
            <h3 style={styles.h3}>Contact Records Management</h3>
            <p style={styles.description}>
              Third CRM domain using the Records Management lifecycle pattern. This view supports contact search,
              customer context, dependency mapping, archive, and restore. Delete remains disabled until controlled
              soft-delete/recovery/purge governance exists.
            </p>
          </div>
          <span style={styles.badge}>Phase RM5A: contacts lifecycle</span>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <div className="rm-toolbar-grid" style={{ ...styles.toolbarGrid, gridTemplateColumns: "minmax(240px, 1fr) 150px 120px" }}>
          <label style={styles.fieldLabel}>
            Search contacts
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Name, email, customer..."
              style={styles.input}
            />
          </label>

          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(event) => setIncludeArchived(event.target.checked)}
            />
            Include archived
          </label>

          <button type="button" onClick={loadContacts} disabled={loading} style={styles.primaryButton}>
            {loading ? "Loading..." : "Search"}
          </button>
        </div>

        {error && <div style={styles.warning}>{error}</div>}
        {actionError && <div style={styles.warning}>{actionError}</div>}
        {actionMessage && <div style={styles.safePanel}>{actionMessage}</div>}

        <div className="rm-table-shell" style={styles.tableShell}>
          <table className="rm-responsive-table" style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Contact</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Phone</th>
                <th style={styles.th}>State</th>
                <th style={styles.th}>Updated</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.length === 0 && !loading && (
                <tr>
                  <td className="rm-empty-table-cell" colSpan="7" style={styles.emptyTableCell}>
                    No contact records loaded. If the backend endpoint is not built yet, this table will populate once
                    /api/contacts/admin/search is added.
                  </td>
                </tr>
              )}

              {contacts.map((contact) => {
                const isSelected = selectedContact && String(selectedContact.id) === String(contact.id);
                return (
                  <tr key={contact.id || contact.email || JSON.stringify(contact)} style={isSelected ? styles.selectedRow : undefined}>
                    <td data-label="Contact" style={styles.td}>
                      <div style={{ fontWeight: 900 }}>{safeText(contact.contact_name || `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || contact.id)}</div>
                      <div style={styles.subtle}>ID: {safeText(contact.id)}</div>
                    </td>
                    <td data-label="Customer" style={styles.td}>{safeText(contact.customer_name || contact.customer || contact.customer_id)}</td>
                    <td data-label="Email" style={styles.td}>{safeText(contact.email)}</td>
                    <td data-label="Phone" style={styles.td}>{safeText(contact.phone || contact.phone_number)}</td>
                    <td data-label="State" style={styles.td}>{statusBadge(contact.is_archived ? "archived" : "active")}</td>
                    <td data-label="Updated" style={styles.td}>{formatDateTime(contact.updated_at || contact.created_at)}</td>
                    <td data-label="Actions" style={styles.td}>
                      <div className="rm-action-row" style={styles.actionRow}>
                        <button type="button" onClick={() => previewDependencies(contact)} style={styles.secondaryButton}>
                          Dependencies
                        </button>
                        {contact.is_archived ? (
                          <button
                            type="button"
                            onClick={() => runContactLifecycleAction(contact, "restore")}
                            disabled={actionLoadingId === `${contact.id}:restore`}
                            style={styles.secondaryButton}
                          >
                            {actionLoadingId === `${contact.id}:restore` ? "Restoring..." : "Restore"}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => runContactLifecycleAction(contact, "archive")}
                            disabled={actionLoadingId === `${contact.id}:archive`}
                            style={styles.archiveButton}
                          >
                            {actionLoadingId === `${contact.id}:archive` ? "Archiving..." : "Archive"}
                          </button>
                        )}
                        <button type="button" disabled style={styles.disabledDangerButton} title="Dependency-enforced delete endpoint required first">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 900 }}>Contact lifecycle controls</h4>
        <div style={styles.checkList}>
          <div style={styles.checkItem}>Search is tenant-scoped from current user context.</div>
          <div style={styles.checkItem}>Dependency modal shows customer, opportunities, quotes, jobs, communications, and documents where available.</div>
          <div style={styles.checkItem}>Active contacts show Archive.</div>
          <div style={styles.checkItem}>Archived contacts show Restore.</div>
          <div style={styles.checkItem}>Delete remains disabled until controlled soft-delete/recovery/purge governance exists.</div>
        </div>
      </div>

      {dependencyModalOpen && (
        <ModalWrapper
          title="Contact dependency map"
          subtitle={`${selectedLabel} — linked records that affect archive, restore, delete, reporting, and recovery decisions.`}
          onClose={() => setDependencyModalOpen(false)}
          maxWidth="980px"
          width="min(980px, 96vw)"
        >
          <div style={styles.modalBody}>
            {dependencyLoading && <div style={styles.emptyPanel}>Loading dependency details...</div>}
            {dependencyError && <div style={styles.warning}>{dependencyError}</div>}

            {!dependencyLoading && !dependencyError && (
              <>
                <div style={blockers.length > 0 ? styles.warning : styles.safePanel}>
                  <strong>Recommendation:</strong> {safeText(dependencyResult?.recommendation, "Archive/restore is safe. Hard delete remains disabled.")}
                </div>

                <div className="rm-modal-stats-grid" style={styles.modalStatsGrid}>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{groups.reduce((sum, group) => sum + Number(group.count || 0), 0)}</p>
                    <p style={styles.statLabel}>Linked records</p>
                    <p style={styles.statDetail}>Across known contact dependency tables.</p>
                  </div>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{blockers.length}</p>
                    <p style={styles.statLabel}>Hard-delete blockers</p>
                    <p style={styles.statDetail}>Delete remains disabled regardless.</p>
                  </div>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{dependencyResult?.can_edit ? "Yes" : "No"}</p>
                    <p style={styles.statLabel}>Can edit</p>
                    <p style={styles.statDetail}>Backend lifecycle permission result.</p>
                  </div>
                </div>

                {visibleGroups.length === 0 ? (
                  <div style={{ ...styles.safePanel, marginTop: 14 }}>
                    No linked records were returned by the dependency endpoint.
                  </div>
                ) : (
                  <div style={styles.dependencyModalList}>
                    {visibleGroups.map((group) => (
                      <div key={group.key} style={styles.dependencyModalGroup(group.severity, group.blocking_delete)}>
                        <div style={styles.dependencyModalGroupHeader}>
                          <div>
                            <div style={{ fontWeight: 900 }}>{group.label}</div>
                            <div style={styles.subtle}>{group.note || group.table}</div>
                          </div>
                          <span style={styles.statusBadge}>{Number(group.count || 0)}</span>
                        </div>

                        {(group.records || []).length > 0 ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                            {(group.records || []).map((record, index) => {
                              const href = dependencyHref(group.key, record);
                              return (
                                <div key={`${group.key}-${record.id || index}`} style={styles.dependencyRecordRow}>
                                  <div>
                                    <div style={{ fontWeight: 800 }}>{safeText(record.label || record.id)}</div>
                                    <div style={styles.subtle}>
                                      ID: {safeText(record.id)}{record.status ? ` · ${record.status}` : ""}
                                    </div>
                                  </div>
                                  {href ? (
                                    <button type="button" onClick={() => window.open(href, "_blank", "noopener,noreferrer")} style={styles.secondaryButton}>
                                      Open
                                    </button>
                                  ) : (
                                    <span style={styles.subtle}>No route</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div style={{ ...styles.subtle, marginTop: 10 }}>
                            {Number(group.count || 0)} linked record(s). No preview rows returned.
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            <div className="rm-modal-footer" style={styles.modalFooter}>
              <button type="button" onClick={() => selectedContact && previewDependencies(selectedContact, true)} style={styles.secondaryButton} disabled={!selectedContact?.id || dependencyLoading}>
                Refresh dependencies
              </button>
              <button type="button" onClick={() => setDependencyModalOpen(false)} style={styles.primaryButton}>
                Close
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}
    </div>
  );
};

const OpportunityManagementSection = ({ styles, accent, actionButton }) => {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [opportunities, setOpportunities] = useState([]);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [dependencyResult, setDependencyResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dependencyLoading, setDependencyLoading] = useState(false);
  const [error, setError] = useState("");
  const [dependencyError, setDependencyError] = useState("");
  const [dependencyModalOpen, setDependencyModalOpen] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [actionMessage, setActionMessage] = useState("");
  const [actionError, setActionError] = useState("");

  const loadOpportunities = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        q: query || undefined,
        status: status || undefined,
        include_archived: includeArchived ? "true" : "false",
        limit: 50,
      };
      const response = await api.get("/opportunities/admin/search", { params });
      const rows = normaliseCollection(response.data);
      setOpportunities(rows);
      if (selectedOpportunity && !rows.some((row) => String(row.id) === String(selectedOpportunity.id))) {
        setSelectedOpportunity(null);
        setDependencyResult(null);
      }
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Opportunity admin search failed. Backend endpoint /api/opportunities/admin/search may not exist yet."
      );
      setOpportunities([]);
    } finally {
      setLoading(false);
    }
  }, [includeArchived, query, selectedOpportunity, status]);

  useEffect(() => {
    loadOpportunities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewDependencies = async (opportunity, openModal = true) => {
    if (!opportunity?.id) return;
    setSelectedOpportunity(opportunity);
    setDependencyResult(null);
    setDependencyError("");
    if (openModal) setDependencyModalOpen(true);
    setDependencyLoading(true);
    try {
      const response = await api.get(`/opportunities/${opportunity.id}/dependencies`);
      setDependencyResult(response.data || {});
    } catch (err) {
      setDependencyError(
        err?.response?.data?.detail ||
          err?.message ||
          "Opportunity dependency preview failed. Backend endpoint /api/opportunities/{id}/dependencies may not exist yet."
      );
    } finally {
      setDependencyLoading(false);
    }
  };

  const runOpportunityLifecycleAction = async (opportunity, action) => {
    if (!opportunity?.id || !action) return;

    const label = action === "archive" ? "archive" : "restore";
    const confirmed = window.confirm(`Confirm ${label} for opportunity ${opportunity.name || opportunity.id}?`);
    if (!confirmed) return;

    setActionLoadingId(`${opportunity.id}:${action}`);
    setActionError("");
    setActionMessage("");

    try {
      const response = await api.post(`/opportunities/${opportunity.id}/${action}`);
      const updatedOpportunity = response.data?.opportunity || opportunity;
      setSelectedOpportunity(updatedOpportunity);
      setDependencyResult(response.data?.dependencies || null);
      setActionMessage(`Opportunity ${opportunity.name || opportunity.id} ${action === "archive" ? "archived" : "restored"}.`);
      await loadOpportunities();
    } catch (err) {
      setActionError(
        err?.response?.data?.detail ||
          err?.message ||
          `Opportunity ${label} failed.`
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  const statusBadge = (value) => {
    const lower = String(value || "unknown").toLowerCase();
    const background = lower === "archived" ? "#f3f4f6" : `${accent}14`;
    const color = lower === "archived" ? "#4b5563" : accent;
    return <span style={{ ...styles.statusBadge, background, color }}>{safeText(value, "unknown")}</span>;
  };

  const groups = dependencyGroupsFromResult(dependencyResult);
  const visibleGroups = groups.filter((group) => Number(group.count || 0) > 0 || (group.records || []).length > 0);
  const blockers = groups.filter((group) => group.blocking_delete && Number(group.count || 0) > 0);
  const selectedLabel = safeText(selectedOpportunity?.name || selectedOpportunity?.id, "Selected opportunity");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="rm-hero" style={styles.hero}>
        <div className="rm-title-row" style={styles.titleRow}>
          <div>
            <p style={styles.eyebrow}>CRM / Opportunities</p>
            <h3 style={styles.h3}>Opportunity Records Management</h3>
            <p style={styles.description}>
              Second CRM domain using the Records Management lifecycle pattern. This view supports search,
              owner/access context, dependency mapping, archive, and restore. Delete remains disabled until
              controlled soft-delete/recovery/purge governance exists.
            </p>
          </div>
          <span style={styles.badge}>Phase RM4A: opportunities lifecycle</span>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <div className="rm-toolbar-grid" style={styles.toolbarGrid}>
          <label style={styles.fieldLabel}>
            Search opportunities
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Name, customer, status, stage..."
              style={styles.input}
            />
          </label>

          <label style={styles.fieldLabel}>
            Status
            <select value={status} onChange={(event) => setStatus(event.target.value)} style={styles.input}>
              {OPPORTUNITY_STATUS_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(event) => setIncludeArchived(event.target.checked)}
            />
            Include archived
          </label>

          <button type="button" onClick={loadOpportunities} disabled={loading} style={styles.primaryButton}>
            {loading ? "Loading..." : "Search"}
          </button>
        </div>

        {error && <div style={styles.warning}>{error}</div>}
        {actionError && <div style={styles.warning}>{actionError}</div>}
        {actionMessage && <div style={styles.safePanel}>{actionMessage}</div>}

        <div className="rm-table-shell" style={styles.tableShell}>
          <table className="rm-responsive-table" style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Opportunity</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Stage</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Owner</th>
                <th style={styles.th}>Value</th>
                <th style={styles.th}>Updated</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {opportunities.length === 0 && !loading && (
                <tr>
                  <td className="rm-empty-table-cell" colSpan="8" style={styles.emptyTableCell}>
                    No opportunity records loaded. If the backend endpoint is not built yet, this table will populate once
                    /api/opportunities/admin/search is added.
                  </td>
                </tr>
              )}

              {opportunities.map((opportunity) => {
                const isSelected = selectedOpportunity && String(selectedOpportunity.id) === String(opportunity.id);
                return (
                  <tr key={opportunity.id || opportunity.name || JSON.stringify(opportunity)} style={isSelected ? styles.selectedRow : undefined}>
                    <td data-label="Opportunity" style={styles.td}>
                      <div style={{ fontWeight: 900 }}>{safeText(opportunity.name || opportunity.id)}</div>
                      <div style={styles.subtle}>ID: {safeText(opportunity.id)}</div>
                    </td>
                    <td data-label="Customer" style={styles.td}>{safeText(opportunity.customer_name || opportunity.customer || opportunity.customer_id)}</td>
                    <td data-label="Stage" style={styles.td}>{safeText(opportunity.stage)}</td>
                    <td data-label="Status" style={styles.td}>{statusBadge(opportunity.is_archived ? "archived" : opportunity.status)}</td>
                    <td data-label="Owner" style={styles.td}>{safeText(opportunity.owner_name || opportunity.owner_resource_id || opportunity.owner_user_id)}</td>
                    <td data-label="Value" style={styles.td}>{formatMoney(opportunity.amount)}</td>
                    <td data-label="Updated" style={styles.td}>{formatDateTime(opportunity.updated_at || opportunity.created_at)}</td>
                    <td data-label="Actions" style={styles.td}>
                      <div className="rm-action-row" style={styles.actionRow}>
                        <button type="button" onClick={() => previewDependencies(opportunity)} style={styles.secondaryButton}>
                          Dependencies
                        </button>
                        {opportunity.is_archived ? (
                          <button
                            type="button"
                            onClick={() => runOpportunityLifecycleAction(opportunity, "restore")}
                            disabled={actionLoadingId === `${opportunity.id}:restore`}
                            style={styles.secondaryButton}
                          >
                            {actionLoadingId === `${opportunity.id}:restore` ? "Restoring..." : "Restore"}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => runOpportunityLifecycleAction(opportunity, "archive")}
                            disabled={actionLoadingId === `${opportunity.id}:archive`}
                            style={styles.archiveButton}
                          >
                            {actionLoadingId === `${opportunity.id}:archive` ? "Archiving..." : "Archive"}
                          </button>
                        )}
                        <button type="button" disabled style={styles.disabledDangerButton} title="Dependency-enforced delete endpoint required first">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 900 }}>Opportunity lifecycle controls</h4>
        <div style={styles.checkList}>
          <div style={styles.checkItem}>Search uses canonical backend visibility via get_visible_opportunities.</div>
          <div style={styles.checkItem}>Dependency modal shows customer, contacts, quotes, jobs, access grants, activities, and line items where available.</div>
          <div style={styles.checkItem}>Active opportunities show Archive.</div>
          <div style={styles.checkItem}>Archived opportunities show Restore.</div>
          <div style={styles.checkItem}>Delete remains disabled until controlled soft-delete/recovery/purge governance exists.</div>
        </div>
        <div style={{ ...styles.safePanel, marginTop: 12, borderColor: `${actionButton}33` }}>
          This now proves the Records Management lifecycle pattern across both Quotes and Opportunities.
        </div>
      </div>

      {dependencyModalOpen && (
        <ModalWrapper
          title="Opportunity dependency map"
          subtitle={`${selectedLabel} — linked records that affect archive, restore, delete, reporting, and recovery decisions.`}
          onClose={() => setDependencyModalOpen(false)}
          maxWidth="980px"
          width="min(980px, 96vw)"
        >
          <div style={styles.modalBody}>
            {dependencyLoading && <div style={styles.emptyPanel}>Loading dependency details...</div>}
            {dependencyError && <div style={styles.warning}>{dependencyError}</div>}

            {!dependencyLoading && !dependencyError && (
              <>
                <div style={blockers.length > 0 ? styles.warning : styles.safePanel}>
                  <strong>Recommendation:</strong> {safeText(dependencyResult?.recommendation, "Archive/restore is safe. Hard delete remains disabled.")}
                </div>

                <div className="rm-modal-stats-grid" style={styles.modalStatsGrid}>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{groups.reduce((sum, group) => sum + Number(group.count || 0), 0)}</p>
                    <p style={styles.statLabel}>Linked records</p>
                    <p style={styles.statDetail}>Across known opportunity dependency tables.</p>
                  </div>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{blockers.length}</p>
                    <p style={styles.statLabel}>Hard-delete blockers</p>
                    <p style={styles.statDetail}>Delete remains disabled regardless.</p>
                  </div>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{dependencyResult?.can_edit ? "Yes" : "No"}</p>
                    <p style={styles.statLabel}>Can edit</p>
                    <p style={styles.statDetail}>Backend visibility/edit check result.</p>
                  </div>
                </div>

                {visibleGroups.length === 0 ? (
                  <div style={{ ...styles.safePanel, marginTop: 14 }}>
                    No linked records were returned by the dependency endpoint.
                  </div>
                ) : (
                  <div style={styles.dependencyModalList}>
                    {visibleGroups.map((group) => (
                      <div key={group.key} style={styles.dependencyModalGroup(group.severity, group.blocking_delete)}>
                        <div style={styles.dependencyModalGroupHeader}>
                          <div>
                            <div style={{ fontWeight: 900 }}>{group.label}</div>
                            <div style={styles.subtle}>{group.note || group.table}</div>
                          </div>
                          <span style={styles.statusBadge}>{Number(group.count || 0)}</span>
                        </div>

                        {(group.records || []).length > 0 ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                            {(group.records || []).map((record, index) => {
                              const href = dependencyHref(group.key, record);
                              return (
                                <div key={`${group.key}-${record.id || index}`} style={styles.dependencyRecordRow}>
                                  <div>
                                    <div style={{ fontWeight: 800 }}>{safeText(record.label || record.id)}</div>
                                    <div style={styles.subtle}>
                                      ID: {safeText(record.id)}{record.status ? ` · ${record.status}` : ""}
                                    </div>
                                  </div>
                                  {href ? (
                                    <button type="button" onClick={() => window.open(href, "_blank", "noopener,noreferrer")} style={styles.secondaryButton}>
                                      Open
                                    </button>
                                  ) : (
                                    <span style={styles.subtle}>No route</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div style={{ ...styles.subtle, marginTop: 10 }}>
                            {Number(group.count || 0)} linked record(s). No preview rows returned.
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            <div className="rm-modal-footer" style={styles.modalFooter}>
              <button type="button" onClick={() => selectedOpportunity && previewDependencies(selectedOpportunity, true)} style={styles.secondaryButton} disabled={!selectedOpportunity?.id || dependencyLoading}>
                Refresh dependencies
              </button>
              <button type="button" onClick={() => setDependencyModalOpen(false)} style={styles.primaryButton}>
                Close
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}
    </div>
  );
};



const isResourceRedacted = (resource) => {
  const firstName = String(resource?.first_name || "").trim().toUpperCase();
  const lastName = String(resource?.last_name || "").trim().toUpperCase();
  const employmentStatus = String(resource?.employment_status || "").trim().toLowerCase();
  const resourceType = String(resource?.resource_type || "").trim().toLowerCase();
  return employmentStatus === "redacted" || resourceType === "redacted" || firstName === "REDACTED" || lastName === "REDACTED";
};

const resourceLabel = (resource) => {
  if (!resource) return "Resource";
  const fullName = `${resource.first_name || ""} ${resource.last_name || ""}`.trim();
  return fullName || resource.identifier || resource.email || `Resource #${resource.id}`;
};

const ResourceManagementSection = ({ styles, accent, actionButton }) => {
  const [query, setQuery] = useState("");
  const [includeInactive, setIncludeInactive] = useState(true);
  const [resources, setResources] = useState([]);
  const [selectedResource, setSelectedResource] = useState(null);
  const [replacementResourceId, setReplacementResourceId] = useState("");
  const [dependencyResult, setDependencyResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dependencyLoading, setDependencyLoading] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [redacting, setRedacting] = useState(false);
  const [error, setError] = useState("");
  const [dependencyError, setDependencyError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [actionError, setActionError] = useState("");
  const [dependencyModalOpen, setDependencyModalOpen] = useState(false);

  const loadResources = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        q: query || undefined,
        include_inactive: includeInactive ? "true" : "false",
        limit: 200,
      };
      const response = await api.get("/resources", { params });
      const rows = normaliseCollection(response.data);
      setResources(rows);
      if (selectedResource && !rows.some((row) => String(row.id) === String(selectedResource.id))) {
        setSelectedResource(null);
        setReplacementResourceId("");
        setDependencyResult(null);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Resource search failed. Backend endpoint /api/resources may not be available.");
      setResources([]);
    } finally {
      setLoading(false);
    }
  }, [includeInactive, query, selectedResource]);

  useEffect(() => {
    loadResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewDependencies = async (resource, openModal = true) => {
    if (!resource?.id) return;
    setSelectedResource(resource);
    setReplacementResourceId("");
    setDependencyResult(null);
    setDependencyError("");
    setActionMessage("");
    setActionError("");
    if (openModal) setDependencyModalOpen(true);
    setDependencyLoading(true);
    try {
      const response = await api.get(`/admin/resources/${resource.id}/dependencies`);
      setDependencyResult(response.data || {});
    } catch (err) {
      setDependencyError(err?.response?.data?.detail || err?.message || "Resource dependency analysis failed. Backend endpoint /api/admin/resources/{id}/dependencies may not be available.");
    } finally {
      setDependencyLoading(false);
    }
  };

  const runReassignment = async () => {
    if (!selectedResource?.id) return;

    const totalDependencies = Number(dependencyResult?.total_dependencies || 0);
    if (!dependencyResult || String(dependencyResult?.resource?.id || selectedResource.id) !== String(selectedResource.id)) {
      setActionError("Run dependency analysis before reassigning this resource.");
      return;
    }

    if (totalDependencies === 0) {
      setActionError("This resource has no dependencies to reassign. Redaction can proceed.");
      return;
    }

    if (!replacementResourceId) {
      setActionError("Select a replacement resource before reassignment.");
      return;
    }

    if (String(replacementResourceId) === String(selectedResource.id)) {
      setActionError("Replacement resource cannot be the same as the resource being redacted.");
      return;
    }

    const targetLabel = resourceLabel(selectedResource);
    const replacementLabel = resourceLabel(resources.find((resource) => String(resource.id) === String(replacementResourceId)));

    const confirmed = window.confirm(
      `Reassign dependencies from ${targetLabel} (#${selectedResource.id}) to ${replacementLabel} (#${replacementResourceId})?\n\n` +
        `Dependencies currently linked: ${totalDependencies}\n\n` +
        "This will move linked records only. It will not redact the source resource. Continue?"
    );
    if (!confirmed) return;

    setReassigning(true);
    setActionError("");
    setActionMessage("");

    try {
      const payload = { replacement_resource_id: Number(replacementResourceId) };
      const response = await api.post(`/admin/resources/${selectedResource.id}/reassign`, payload);
      const reassigned = response.data?.reassigned || {};
      const reassignedTotal = Object.values(reassigned).reduce((sum, value) => sum + Number(value || 0), 0);
      const afterDependencies = response.data?.dependencies_after || null;
      if (afterDependencies) {
        setDependencyResult({
          ...(dependencyResult || {}),
          dependency_counts: afterDependencies.dependency_counts || {},
          dependency_groups: afterDependencies.dependency_groups || [],
          total_dependencies: Number(afterDependencies.total_dependencies || 0),
          replacement_required: Number(afterDependencies.total_dependencies || 0) > 0,
          recommendation:
            Number(afterDependencies.total_dependencies || 0) === 0
              ? "Dependencies have been reassigned and re-check is clear. Redaction is now available."
              : "Some dependencies remain after reassignment. Redaction is still blocked.",
        });
      } else {
        await previewDependencies(selectedResource, true);
      }
      setActionMessage(`Reassigned ${reassignedTotal} linked record(s) from ${targetLabel} to resource #${replacementResourceId}. Dependency re-check complete.`);
      await loadResources();
      window.dispatchEvent(new CustomEvent("dashboard:refresh"));
    } catch (err) {
      setActionError(err?.response?.data?.detail || err?.message || "Resource dependency reassignment failed.");
    } finally {
      setReassigning(false);
    }
  };

  const runRedaction = async () => {
    if (!selectedResource?.id) return;

    const totalDependencies = Number(dependencyResult?.total_dependencies || 0);
    if (!dependencyResult || String(dependencyResult?.resource?.id || selectedResource.id) !== String(selectedResource.id)) {
      setActionError("Run dependency analysis before redacting this resource.");
      return;
    }

    if (totalDependencies > 0) {
      setActionError("Redaction is blocked until dependencies are reassigned and a fresh dependency check returns zero.");
      return;
    }

    const targetLabel = resourceLabel(selectedResource);
    const confirmed = window.confirm(
      `Redact resource ${targetLabel} (#${selectedResource.id})?\n\n` +
        "Dependency check is clear. This will wipe PII and deactivate the resource. Continue?"
    );
    if (!confirmed) return;

    setRedacting(true);
    setActionError("");
    setActionMessage("");

    try {
      await api.post(`/admin/resources/${selectedResource.id}/redact`, {});
      setActionMessage(`Resource ${targetLabel} redacted.`);
      setDependencyModalOpen(false);
      setSelectedResource(null);
      setReplacementResourceId("");
      setDependencyResult(null);
      await loadResources();
      window.dispatchEvent(new CustomEvent("dashboard:refresh"));
    } catch (err) {
      setActionError(err?.response?.data?.detail || err?.message || "Resource redaction failed.");
    } finally {
      setRedacting(false);
    }
  };

  const activeReplacementResources = resources.filter((resource) => {
    if (!resource?.id || String(resource.id) === String(selectedResource?.id)) return false;
    if (resource.active === false) return false;
    if (isResourceRedacted(resource)) return false;
    return true;
  });

  const groups = dependencyGroupsFromResult(dependencyResult);
  const visibleGroups = groups.filter((group) => Number(group.count || 0) > 0 || (group.records || []).length > 0);
  const totalDependencies = Number(dependencyResult?.total_dependencies || 0);
  const selectedLabel = resourceLabel(selectedResource);

  const statusBadge = (resource) => {
    if (isResourceRedacted(resource)) {
      return <span style={{ ...styles.statusBadge, background: "#fef2f2", color: "#991b1b" }}>redacted</span>;
    }
    if (resource?.active === false) {
      return <span style={{ ...styles.statusBadge, background: "#f3f4f6", color: "#4b5563" }}>inactive</span>;
    }
    return <span style={{ ...styles.statusBadge, background: `${accent}14`, color: accent }}>active</span>;
  };

  const canRedact = selectedResource && dependencyResult && !redacting && !reassigning && !isResourceRedacted(selectedResource) && totalDependencies === 0;
  const canReassign = selectedResource && dependencyResult && !redacting && !reassigning && !isResourceRedacted(selectedResource);
  const canRunReassignNow = canReassign && totalDependencies > 0 && !!replacementResourceId;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="rm-hero" style={styles.hero}>
        <div className="rm-title-row" style={styles.titleRow}>
          <div>
            <p style={styles.eyebrow}>Operations / Resources</p>
            <h3 style={styles.h3}>Resource Records Management</h3>
            <p style={styles.description}>
              RM9 is now using the working GDPR-safe resource redaction tool inside Records Management. Resources do not use
              the simple archive/restore pattern. The safe flow is dependency analysis, replacement selection,
              reassignment, dependency re-check, then PII redaction and deactivation.
            </p>
          </div>
          <span style={styles.badge}>Phase RM9: complete</span>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <div className="rm-toolbar-grid" style={{ ...styles.toolbarGrid, gridTemplateColumns: "minmax(240px, 1fr) 170px 120px" }}>
          <label style={styles.fieldLabel}>
            Search resources
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Name, email, identifier, role, job title..."
              style={styles.input}
            />
          </label>

          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(event) => setIncludeInactive(event.target.checked)}
            />
            Include inactive/redacted
          </label>

          <button type="button" onClick={loadResources} disabled={loading} style={styles.primaryButton}>
            {loading ? "Loading..." : "Search"}
          </button>
        </div>

        {error && <div style={styles.warning}>{error}</div>}
        {actionError && <div style={styles.warning}>{actionError}</div>}
        {actionMessage && <div style={styles.safePanel}>{actionMessage}</div>}

        <div className="rm-table-shell" style={styles.tableShell}>
          <table className="rm-responsive-table" style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Resource</th>
                <th style={styles.th}>Identifier</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Job Title</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>State</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {resources.length === 0 && !loading && (
                <tr>
                  <td className="rm-empty-table-cell" colSpan="7" style={styles.emptyTableCell}>
                    No resource records loaded. Search will use /api/resources with include_inactive enabled by default.
                  </td>
                </tr>
              )}

              {resources.map((resource) => {
                const isSelected = selectedResource && String(selectedResource.id) === String(resource.id);
                return (
                  <tr key={resource.id || resource.email || JSON.stringify(resource)} style={isSelected ? styles.selectedRow : undefined}>
                    <td data-label="Resource" style={styles.td}>
                      <div style={{ fontWeight: 900 }}>{resourceLabel(resource)}</div>
                      <div style={styles.subtle}>ID: {safeText(resource.id)}</div>
                    </td>
                    <td data-label="Identifier" style={styles.td}>{safeText(resource.identifier)}</td>
                    <td data-label="Email" style={styles.td}>{safeText(resource.email)}</td>
                    <td data-label="Job Title" style={styles.td}>{safeText(resource.job_title)}</td>
                    <td data-label="Type" style={styles.td}>{safeText(resource.resource_type)}</td>
                    <td data-label="State" style={styles.td}>{statusBadge(resource)}</td>
                    <td data-label="Actions" style={styles.td}>
                      <div className="rm-action-row" style={styles.actionRow}>
                        <button type="button" onClick={() => previewDependencies(resource)} style={styles.secondaryButton}>
                          Dependencies
                        </button>
                        <button
                          type="button"
                          onClick={() => previewDependencies(resource, true)}
                          disabled={isResourceRedacted(resource)}
                          style={isResourceRedacted(resource) ? styles.disabledDangerButton : styles.archiveButton}
                          title={isResourceRedacted(resource) ? "Resource is already redacted" : "Analyse dependencies before redaction"}
                        >
                          Redact
                        </button>
                        <button type="button" disabled style={styles.disabledDangerButton} title="Hard delete is disabled for resources">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 900 }}>Resource redaction controls</h4>
        <div style={styles.checkList}>
          <div style={styles.checkItem}>Dependency analysis is required before redaction.</div>
          <div style={styles.checkItem}>If dependencies exist, a replacement resource is required before reassignment.</div>
          <div style={styles.checkItem}>Reassignment and redaction are separate actions: reassign first, re-check dependencies, then redact.</div>
          <div style={styles.checkItem}>PII fields are wiped and the resource is deactivated using employment_status = redacted.</div>
          <div style={styles.checkItem}>Redaction writes to the central audit log.</div>
        </div>
      </div>

      {dependencyModalOpen && (
        <ModalWrapper
          title="Resource dependency, reassignment, and redaction gate"
          subtitle={`${selectedLabel} — analyse links, choose a replacement, reassign, re-check, then redact.`}
          onClose={() => setDependencyModalOpen(false)}
          maxWidth="980px"
          width="min(980px, 96vw)"
        >
          <div style={styles.modalBody}>
            {dependencyLoading && <div style={styles.emptyPanel}>Loading resource dependency details...</div>}
            {dependencyError && <div style={styles.warning}>{dependencyError}</div>}
            {actionError && <div style={styles.warning}>{actionError}</div>}
            {actionMessage && <div style={styles.safePanel}>{actionMessage}</div>}

            {!dependencyLoading && !dependencyError && dependencyResult && (
              <>
                <div style={totalDependencies > 0 ? styles.warning : styles.safePanel}>
                  <strong>Recommendation:</strong> {safeText(dependencyResult?.recommendation, "Run dependency analysis before redaction.")}
                </div>

                <div className="rm-modal-stats-grid" style={styles.modalStatsGrid}>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{totalDependencies}</p>
                    <p style={styles.statLabel}>Linked records</p>
                    <p style={styles.statDetail}>Rows currently pointing at this resource.</p>
                  </div>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{totalDependencies > 0 ? "Yes" : "No"}</p>
                    <p style={styles.statLabel}>Replacement required</p>
                    <p style={styles.statDetail}>Required when dependencies exist.</p>
                  </div>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{canRedact ? "Ready" : "Blocked"}</p>
                    <p style={styles.statLabel}>Redaction gate</p>
                    <p style={styles.statDetail}>Backend will verify again before PII wipe.</p>
                  </div>
                </div>

                {totalDependencies > 0 && (
                  <div className="rm-panel-card" style={styles.placeholder}>
                    <label style={styles.fieldLabel}>
                      Replacement resource
                      <select
                        value={replacementResourceId}
                        onChange={(event) => setReplacementResourceId(event.target.value)}
                        style={styles.input}
                      >
                        <option value="">Select active replacement resource...</option>
                        {activeReplacementResources.map((resource) => (
                          <option key={resource.id} value={resource.id}>
                            #{resource.id} — {resourceLabel(resource)}{resource.job_title ? ` — ${resource.job_title}` : ""}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div style={{ ...styles.subtle, marginTop: 8 }}>
                      Use Reassign dependencies first. Redaction remains blocked until the dependency re-check returns zero. Redacted and inactive resources are excluded from this list.
                    </div>
                  </div>
                )}

                {visibleGroups.length === 0 ? (
                  <div style={{ ...styles.safePanel, marginTop: 14 }}>
                    No linked resource dependencies were returned by the dependency endpoint.
                  </div>
                ) : (
                  <div style={styles.dependencyModalList}>
                    {visibleGroups.map((group) => (
                      <div key={group.key} style={styles.dependencyModalGroup(group.severity, group.blocking_redact || group.blocking_delete)}>
                        <div style={styles.dependencyModalGroupHeader}>
                          <div>
                            <div style={{ fontWeight: 900 }}>{group.label}</div>
                            <div style={styles.subtle}>{group.note || group.table}</div>
                          </div>
                          <span style={styles.statusBadge}>{Number(group.count || 0)}</span>
                        </div>

                        {(group.records || []).length > 0 ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                            {(group.records || []).map((record, index) => (
                              <div key={`${group.key}-${record.id || index}`} style={styles.dependencyRecordRow}>
                                <div>
                                  <div style={{ fontWeight: 800 }}>{safeText(record.label || record.id)}</div>
                                  <div style={styles.subtle}>
                                    {safeText(record.table)} · {safeText(record.column)} · ID: {safeText(record.id)}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ ...styles.subtle, marginTop: 10 }}>
                            {Number(group.count || 0)} linked record(s). No preview rows returned.
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            <div className="rm-resource-footer" style={styles.resourceRedactionFooter}>
              <button
                type="button"
                onClick={() => selectedResource && previewDependencies(selectedResource, true)}
                style={styles.resourceModalActionButton(styles.secondaryButton)}
                disabled={!selectedResource?.id || dependencyLoading || redacting || reassigning}
              >
                Re-check dependencies
              </button>
              <button
                type="button"
                onClick={runReassignment}
                style={styles.resourceModalActionButton(canRunReassignNow ? styles.archiveButton : styles.secondaryButton)}
                disabled={!canReassign}
                title={
                  !dependencyResult
                    ? "Run dependency analysis first"
                    : totalDependencies === 0
                      ? "No dependencies remain; redaction can proceed"
                      : !replacementResourceId
                        ? "Select a replacement resource first"
                        : "Reassign dependencies to the selected replacement resource"
                }
              >
                {reassigning ? "Reassigning..." : "Reassign dependencies"}
              </button>
              <button
                type="button"
                onClick={runRedaction}
                style={styles.resourceModalActionButton(canRedact ? styles.archiveButton : styles.disabledDangerButton)}
                disabled={!canRedact}
              >
                {redacting ? "Redacting..." : "Redact resource"}
              </button>
              <button
                type="button"
                onClick={() => setDependencyModalOpen(false)}
                style={styles.resourceModalActionButton(styles.primaryButton)}
                disabled={redacting || reassigning}
              >
                Close
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}
    </div>
  );
};


const CameraDeviceManagementSection = ({ styles, accent, actionButton }) => {
  const [query, setQuery] = useState("");
  const [includeInactive, setIncludeInactive] = useState(true);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [dependencyResult, setDependencyResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dependencyLoading, setDependencyLoading] = useState(false);
  const [error, setError] = useState("");
  const [dependencyError, setDependencyError] = useState("");
  const [actionLoading, setActionLoading] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [actionError, setActionError] = useState("");
  const [dependencyModalOpen, setDependencyModalOpen] = useState(false);

  const cameraLabel = (camera) => camera?.camera_name || camera?.serial_no || camera?.mac_address || `Camera #${camera?.id || ""}`;

  const loadCameras = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        q: query || undefined,
        include_inactive: includeInactive ? "true" : "false",
        limit: 200,
      };
      const response = await api.get("/anpr/cameras/admin/search", { params });
      const rows = normaliseCollection(response.data);
      setCameras(rows);
      if (selectedCamera && !rows.some((row) => String(row.id) === String(selectedCamera.id))) {
        setSelectedCamera(null);
        setDependencyResult(null);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Camera admin search failed. Backend endpoint /api/anpr/cameras/admin/search may not be available.");
      setCameras([]);
    } finally {
      setLoading(false);
    }
  }, [includeInactive, query, selectedCamera]);

  useEffect(() => {
    loadCameras();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewDependencies = async (camera, openModal = true) => {
    if (!camera?.id) return;
    setSelectedCamera(camera);
    setDependencyResult(null);
    setDependencyError("");
    setActionMessage("");
    setActionError("");
    if (openModal) setDependencyModalOpen(true);
    setDependencyLoading(true);
    try {
      const response = await api.get(`/anpr/cameras/${camera.id}/dependencies`);
      setDependencyResult(response.data || {});
    } catch (err) {
      setDependencyError(err?.response?.data?.detail || err?.message || "Camera dependency analysis failed.");
    } finally {
      setDependencyLoading(false);
    }
  };

  const runCameraAction = async (camera, action) => {
    if (!camera?.id || !action) return;

    const labels = {
      "disable-ingest": "disable ingest for",
      "enable-ingest": "enable ingest for",
      deactivate: "deactivate",
      reactivate: "reactivate",
    };

    const confirmed = window.confirm(`Confirm ${labels[action] || action} ${cameraLabel(camera)} (#${camera.id})?`);
    if (!confirmed) return;

    setActionLoading(`${camera.id}:${action}`);
    setActionError("");
    setActionMessage("");

    try {
      const response = await api.post(`/anpr/cameras/${camera.id}/${action}`);
      const updatedCamera = response.data?.camera || camera;
      setSelectedCamera(updatedCamera);
      if (response.data?.dependencies) {
        setDependencyResult(response.data.dependencies);
      }
      setActionMessage(`Camera ${cameraLabel(camera)} ${labels[action] || action} completed.`);
      await loadCameras();
      window.dispatchEvent(new CustomEvent("dashboard:refresh"));
    } catch (err) {
      setActionError(err?.response?.data?.detail || err?.message || `Camera ${action} failed.`);
    } finally {
      setActionLoading("");
    }
  };

  const groups = dependencyGroupsFromResult(dependencyResult);
  const visibleGroups = groups.filter((group) => Number(group.count || 0) > 0 || (group.records || []).length > 0);
  const blockers = groups.filter((group) => group.blocking_delete && Number(group.count || 0) > 0);
  const selectedLabel = cameraLabel(selectedCamera);

  const statusBadge = (camera) => {
    if (camera?.is_active === false) {
      return <span style={{ ...styles.statusBadge, background: "#f3f4f6", color: "#4b5563" }}>inactive</span>;
    }
    return <span style={{ ...styles.statusBadge, background: `${accent}14`, color: accent }}>active</span>;
  };

  const ingestBadge = (camera) => {
    if (camera?.ingest_enabled === false) {
      return <span style={{ ...styles.statusBadge, background: "#fef2f2", color: "#991b1b" }}>disabled</span>;
    }
    return <span style={{ ...styles.statusBadge, background: "#ecfdf5", color: "#047857" }}>enabled</span>;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="rm-hero" style={styles.hero}>
        <div className="rm-title-row" style={styles.titleRow}>
          <div>
            <p style={styles.eyebrow}>ANPR / Camera Devices</p>
            <h3 style={styles.h3}>Camera Device Records Management</h3>
            <p style={styles.description}>
              RM10A starts the ANPR records sequence at the operational source. Camera devices can be searched,
              dependency-checked, ingest-enabled/disabled, deactivated, and reactivated. Ingest events, sightings,
              and plate sessions remain view-only evidential records.
            </p>
          </div>
          <span style={styles.badge}>Phase RM10A: camera devices</span>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <div className="rm-toolbar-grid" style={{ ...styles.toolbarGrid, gridTemplateColumns: "minmax(240px, 1fr) 170px 120px" }}>
          <label style={styles.fieldLabel}>
            Search cameras
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Camera name, serial, MAC, model, IP, location..."
              style={styles.input}
            />
          </label>

          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(event) => setIncludeInactive(event.target.checked)}
            />
            Include inactive
          </label>

          <button type="button" onClick={loadCameras} disabled={loading} style={styles.primaryButton}>
            {loading ? "Loading..." : "Search"}
          </button>
        </div>

        {error && <div style={styles.warning}>{error}</div>}
        {actionError && <div style={styles.warning}>{actionError}</div>}
        {actionMessage && <div style={styles.safePanel}>{actionMessage}</div>}

        <div className="rm-table-shell" style={styles.tableShell}>
          <table className="rm-responsive-table" style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Camera</th>
                <th style={styles.th}>MAC / Serial</th>
                <th style={styles.th}>Model</th>
                <th style={styles.th}>IP / Location</th>
                <th style={styles.th}>State</th>
                <th style={styles.th}>Ingest</th>
                <th style={styles.th}>Last Ingest</th>
                <th style={styles.th}>Events</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cameras.length === 0 && !loading && (
                <tr>
                  <td className="rm-empty-table-cell" colSpan="9" style={styles.emptyTableCell}>
                    No camera device records loaded. This view uses /api/anpr/cameras/admin/search.
                  </td>
                </tr>
              )}

              {cameras.map((camera) => {
                const isSelected = selectedCamera && String(selectedCamera.id) === String(camera.id);
                return (
                  <tr key={camera.id || camera.mac_address || JSON.stringify(camera)} style={isSelected ? styles.selectedRow : undefined}>
                    <td data-label="Camera" style={styles.td}>
                      <div style={{ fontWeight: 900 }}>{cameraLabel(camera)}</div>
                      <div style={styles.subtle}>ID: {safeText(camera.id)}</div>
                    </td>
                    <td data-label="MAC / Serial" style={styles.td}>
                      <div>{safeText(camera.mac_address)}</div>
                      <div style={styles.subtle}>{safeText(camera.serial_no)}</div>
                    </td>
                    <td data-label="Model" style={styles.td}>
                      <div>{safeText(camera.model_no)}</div>
                      <div style={styles.subtle}>{safeText(camera.firmware_version)}</div>
                    </td>
                    <td data-label="IP / Location" style={styles.td}>
                      <div>{safeText(camera.ip_address)}</div>
                      <div style={styles.subtle}>{safeText(camera.location_name)}</div>
                    </td>
                    <td data-label="State" style={styles.td}>{statusBadge(camera)}</td>
                    <td data-label="Ingest" style={styles.td}>{ingestBadge(camera)}</td>
                    <td data-label="Last Ingest" style={styles.td}>{formatDateTime(camera.last_ingest_at || camera.ingest_last_used_at)}</td>
                    <td data-label="Events" style={styles.td}>
                      <div>{safeText(camera.ingest_events, "0")} events</div>
                      <div style={styles.subtle}>{safeText(camera.total_decodes, "0")} decodes · {safeText(camera.failed_ingest_events, "0")} failed</div>
                    </td>
                    <td data-label="Actions" style={styles.td}>
                      <div className="rm-action-row" style={styles.actionRow}>
                        <button type="button" onClick={() => previewDependencies(camera)} style={styles.secondaryButton}>
                          Dependencies
                        </button>
                        {camera.ingest_enabled === false ? (
                          <button type="button" onClick={() => runCameraAction(camera, "enable-ingest")} disabled={actionLoading === `${camera.id}:enable-ingest`} style={styles.secondaryButton}>
                            {actionLoading === `${camera.id}:enable-ingest` ? "Enabling..." : "Enable ingest"}
                          </button>
                        ) : (
                          <button type="button" onClick={() => runCameraAction(camera, "disable-ingest")} disabled={actionLoading === `${camera.id}:disable-ingest`} style={styles.archiveButton}>
                            {actionLoading === `${camera.id}:disable-ingest` ? "Disabling..." : "Disable ingest"}
                          </button>
                        )}
                        {camera.is_active === false ? (
                          <button type="button" onClick={() => runCameraAction(camera, "reactivate")} disabled={actionLoading === `${camera.id}:reactivate`} style={styles.secondaryButton}>
                            {actionLoading === `${camera.id}:reactivate` ? "Reactivating..." : "Reactivate"}
                          </button>
                        ) : (
                          <button type="button" onClick={() => runCameraAction(camera, "deactivate")} disabled={actionLoading === `${camera.id}:deactivate`} style={styles.archiveButton}>
                            {actionLoading === `${camera.id}:deactivate` ? "Deactivating..." : "Deactivate"}
                          </button>
                        )}
                        <button type="button" disabled style={styles.disabledDangerButton} title="Hard delete is disabled for camera devices">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 900 }}>Camera device lifecycle controls</h4>
        <div style={styles.checkList}>
          <div style={styles.checkItem}>Dependency analysis shows bindings, pairings, ingest events, sightings, failed events, and total decodes.</div>
          <div style={styles.checkItem}>Disable ingest stops new payload acceptance without losing historical data.</div>
          <div style={styles.checkItem}>Deactivate also disables ingest and marks the camera inactive.</div>
          <div style={styles.checkItem}>Reactivate restores operational visibility but does not automatically enable ingest.</div>
          <div style={styles.checkItem}>Delete remains disabled because camera data links to evidential ANPR records.</div>
        </div>
      </div>

      {dependencyModalOpen && (
        <ModalWrapper
          title="Camera dependency map"
          subtitle={`${selectedLabel} — bindings, pairings, ingest activity, sightings, and operational warnings.`}
          onClose={() => setDependencyModalOpen(false)}
          maxWidth="980px"
          width="min(980px, 96vw)"
        >
          <div style={styles.modalBody}>
            {dependencyLoading && <div style={styles.emptyPanel}>Loading camera dependency details...</div>}
            {dependencyError && <div style={styles.warning}>{dependencyError}</div>}
            {actionError && <div style={styles.warning}>{actionError}</div>}
            {actionMessage && <div style={styles.safePanel}>{actionMessage}</div>}

            {!dependencyLoading && !dependencyError && dependencyResult && (
              <>
                <div style={blockers.length > 0 ? styles.warning : styles.safePanel}>
                  <strong>Recommendation:</strong> {safeText(dependencyResult?.recommendation, "Review dependency state before changing this camera.")}
                </div>

                <div className="rm-modal-stats-grid" style={styles.modalStatsGrid}>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{safeText(dependencyResult?.summary?.active_bindings, "0")}</p>
                    <p style={styles.statLabel}>Active bindings</p>
                    <p style={styles.statDetail}>Operational job bindings currently active.</p>
                  </div>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{safeText(dependencyResult?.summary?.ingest_events, "0")}</p>
                    <p style={styles.statLabel}>Ingest events</p>
                    <p style={styles.statDetail}>{safeText(dependencyResult?.summary?.total_decodes, "0")} total decodes.</p>
                  </div>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{dependencyResult?.can_deactivate ? "Yes" : "Review"}</p>
                    <p style={styles.statLabel}>Can deactivate</p>
                    <p style={styles.statDetail}>Active bindings/pairings should be reviewed first.</p>
                  </div>
                </div>

                {(dependencyResult?.warnings || []).length > 0 && (
                  <div style={styles.warning}>
                    <strong>Warnings</strong>
                    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                      {(dependencyResult.warnings || []).map((warning) => (
                        <div key={warning}>• {warning}</div>
                      ))}
                    </div>
                  </div>
                )}

                {visibleGroups.length === 0 ? (
                  <div style={{ ...styles.safePanel, marginTop: 14 }}>
                    No linked camera dependencies were returned by the dependency endpoint.
                  </div>
                ) : (
                  <div style={styles.dependencyModalList}>
                    {visibleGroups.map((group) => (
                      <div key={group.key} style={styles.dependencyModalGroup(group.severity, group.blocking_delete)}>
                        <div style={styles.dependencyModalGroupHeader}>
                          <div>
                            <div style={{ fontWeight: 900 }}>{group.label}</div>
                            <div style={styles.subtle}>{group.note || group.table}</div>
                          </div>
                          <span style={styles.statusBadge}>{Number(group.count || 0)}</span>
                        </div>

                        {(group.records || []).length > 0 ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                            {(group.records || []).map((record, index) => (
                              <div key={`${group.key}-${record.id || index}`} style={styles.dependencyRecordRow}>
                                <div>
                                  <div style={{ fontWeight: 800 }}>{safeText(record.label || record.id)}</div>
                                  <div style={styles.subtle}>
                                    ID: {safeText(record.id)}{record.status ? ` · ${record.status}` : ""}{record.received_at ? ` · ${formatDateTime(record.received_at)}` : ""}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ ...styles.subtle, marginTop: 10 }}>
                            {Number(group.count || 0)} linked record(s). No preview rows returned.
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            <div className="rm-resource-footer" style={styles.resourceRedactionFooter}>
              <button
                type="button"
                onClick={() => selectedCamera && previewDependencies(selectedCamera, true)}
                style={styles.resourceModalActionButton(styles.secondaryButton)}
                disabled={!selectedCamera?.id || dependencyLoading || !!actionLoading}
              >
                Re-check dependencies
              </button>
              <button
                type="button"
                onClick={() => selectedCamera && runCameraAction(selectedCamera, selectedCamera.ingest_enabled === false ? "enable-ingest" : "disable-ingest")}
                style={styles.resourceModalActionButton(styles.archiveButton)}
                disabled={!selectedCamera?.id || !!actionLoading}
              >
                {selectedCamera?.ingest_enabled === false ? "Enable ingest" : "Disable ingest"}
              </button>
              <button
                type="button"
                onClick={() => selectedCamera && runCameraAction(selectedCamera, selectedCamera.is_active === false ? "reactivate" : "deactivate")}
                style={styles.resourceModalActionButton(styles.archiveButton)}
                disabled={!selectedCamera?.id || !!actionLoading}
              >
                {selectedCamera?.is_active === false ? "Reactivate camera" : "Deactivate camera"}
              </button>
              <button
                type="button"
                onClick={() => setDependencyModalOpen(false)}
                style={styles.resourceModalActionButton(styles.primaryButton)}
                disabled={!!actionLoading}
              >
                Close
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}
    </div>
  );
};


const CameraIngestEventManagementSection = ({ styles, accent, actionButton }) => {
  const [query, setQuery] = useState("");
  const [parseStatus, setParseStatus] = useState("");
  const [cameraId, setCameraId] = useState("");
  const [jobId, setJobId] = useState("");
  const [failedOnly, setFailedOnly] = useState(false);
  const [zeroDecodeOnly, setZeroDecodeOnly] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dependencyResult, setDependencyResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dependencyLoading, setDependencyLoading] = useState(false);
  const [error, setError] = useState("");
  const [dependencyError, setDependencyError] = useState("");
  const [dependencyModalOpen, setDependencyModalOpen] = useState(false);

  const parseStatusOptions = [
    { value: "", label: "All statuses" },
    { value: "received", label: "Received" },
    { value: "parsed", label: "Parsed" },
    { value: "error", label: "Error" },
    { value: "failed", label: "Failed" },
  ];

  const eventLabel = (event) => {
    if (!event) return "Ingest event";
    return `Event #${event.id || ""}`;
  };

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        q: query || undefined,
        parse_status: parseStatus || undefined,
        camera_id: cameraId || undefined,
        job_id: jobId || undefined,
        failed_only: failedOnly ? "true" : "false",
        zero_decode_only: zeroDecodeOnly ? "true" : "false",
        limit: 100,
      };
      const response = await api.get("/anpr/cameras/ingest-events/admin/search", { params });
      const rows = normaliseCollection(response.data);
      setEvents(rows);
      if (selectedEvent && !rows.some((row) => String(row.id) === String(selectedEvent.id))) {
        setSelectedEvent(null);
        setDependencyResult(null);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Camera ingest event search failed. Backend endpoint /api/anpr/cameras/ingest-events/admin/search may not be available.");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [cameraId, failedOnly, jobId, parseStatus, query, selectedEvent, zeroDecodeOnly]);

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewDependencies = async (event, openModal = true) => {
    if (!event?.id) return;
    setSelectedEvent(event);
    setDependencyResult(null);
    setDependencyError("");
    if (openModal) setDependencyModalOpen(true);
    setDependencyLoading(true);
    try {
      const response = await api.get(`/anpr/cameras/ingest-events/${event.id}/dependencies`);
      setDependencyResult(response.data || {});
    } catch (err) {
      setDependencyError(err?.response?.data?.detail || err?.message || "Camera ingest event dependency analysis failed.");
    } finally {
      setDependencyLoading(false);
    }
  };

  const statusBadge = (event) => {
    const lower = String(event?.parse_status || "unknown").toLowerCase();
    const hasError = event?.has_error || lower === "error" || lower === "failed" || !!event?.parse_error;
    const background = hasError ? "#fef2f2" : lower === "parsed" ? "#ecfdf5" : `${accent}14`;
    const color = hasError ? "#991b1b" : lower === "parsed" ? "#047857" : accent;
    return <span style={{ ...styles.statusBadge, background, color }}>{safeText(event?.parse_status, "unknown")}</span>;
  };

  const groups = dependencyGroupsFromResult(dependencyResult);
  const visibleGroups = groups.filter((group) => Number(group.count || 0) > 0 || (group.records || []).length > 0);
  const selectedLabel = eventLabel(selectedEvent);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="rm-hero" style={styles.hero}>
        <div className="rm-title-row" style={styles.titleRow}>
          <div>
            <p style={styles.eyebrow}>ANPR / Camera Ingest Events</p>
            <h3 style={styles.h3}>Camera Ingest Event Records Management</h3>
            <p style={styles.description}>
              RM10B adds a view-only operational layer for inbound camera payloads. These records are not archived,
              redacted, or deleted from Records Management because they form part of the camera ingest audit trail
              and may link directly to evidential ANPR sightings.
            </p>
          </div>
          <span style={styles.badge}>Phase RM10B: view-only ingest events</span>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <div className="rm-toolbar-grid" style={{ ...styles.toolbarGrid, gridTemplateColumns: "minmax(220px, 1fr) 150px 120px 120px 130px 130px 120px" }}>
          <label style={styles.fieldLabel}>
            Search events
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Event ID, camera, serial, MAC, job ref, status, error..."
              style={styles.input}
            />
          </label>

          <label style={styles.fieldLabel}>
            Parse status
            <select value={parseStatus} onChange={(event) => setParseStatus(event.target.value)} style={styles.input}>
              {parseStatusOptions.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label style={styles.fieldLabel}>
            Camera ID
            <input value={cameraId} onChange={(event) => setCameraId(event.target.value)} placeholder="Any" style={styles.input} />
          </label>

          <label style={styles.fieldLabel}>
            Job ID
            <input value={jobId} onChange={(event) => setJobId(event.target.value)} placeholder="Any" style={styles.input} />
          </label>

          <label style={styles.checkboxLabel}>
            <input type="checkbox" checked={failedOnly} onChange={(event) => setFailedOnly(event.target.checked)} />
            Failed only
          </label>

          <label style={styles.checkboxLabel}>
            <input type="checkbox" checked={zeroDecodeOnly} onChange={(event) => setZeroDecodeOnly(event.target.checked)} />
            Zero decode
          </label>

          <button type="button" onClick={loadEvents} disabled={loading} style={styles.primaryButton}>
            {loading ? "Loading..." : "Search"}
          </button>
        </div>

        {error && <div style={styles.warning}>{error}</div>}

        <div className="rm-table-shell" style={styles.tableShell}>
          <table className="rm-responsive-table" style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Event</th>
                <th style={styles.th}>Camera</th>
                <th style={styles.th}>Job</th>
                <th style={styles.th}>Source</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Decodes</th>
                <th style={styles.th}>Received</th>
                <th style={styles.th}>Error</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && !loading && (
                <tr>
                  <td className="rm-empty-table-cell" colSpan="9" style={styles.emptyTableCell}>
                    No camera ingest events loaded. This view uses /api/anpr/cameras/ingest-events/admin/search.
                  </td>
                </tr>
              )}

              {events.map((event) => {
                const isSelected = selectedEvent && String(selectedEvent.id) === String(event.id);
                return (
                  <tr key={event.id || JSON.stringify(event)} style={isSelected ? styles.selectedRow : undefined}>
                    <td data-label="Event" style={styles.td}>
                      <div style={{ fontWeight: 900 }}>Event #{safeText(event.id)}</div>
                      <div style={styles.subtle}>Tenant: {safeText(event.tenant_id)}</div>
                    </td>
                    <td data-label="Camera" style={styles.td}>
                      <div>{safeText(event.camera_name || event.camera_device_id)}</div>
                      <div style={styles.subtle}>{safeText(event.mac_address || event.serial_no)}</div>
                    </td>
                    <td data-label="Job" style={styles.td}>
                      <div>{safeText(event.job_ref || event.job_id)}</div>
                      <div style={styles.subtle}>Job ID: {safeText(event.job_id)}</div>
                    </td>
                    <td data-label="Source" style={styles.td}>
                      <div>{safeText(event.source_type)}</div>
                      <div style={styles.subtle}>{safeText(event.content_type)}</div>
                    </td>
                    <td data-label="Status" style={styles.td}>{statusBadge(event)}</td>
                    <td data-label="Decodes" style={styles.td}>
                      <div>{safeText(event.decode_count, "0")} decoded</div>
                      <div style={styles.subtle}>{safeText(event.inserted_count, "0")} inserted · {safeText(event.duplicate_count, "0")} duplicate</div>
                    </td>
                    <td data-label="Received" style={styles.td}>{formatDateTime(event.received_at)}</td>
                    <td data-label="Error" style={styles.td}>
                      <div style={{ maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {safeText(event.parse_error)}
                      </div>
                    </td>
                    <td data-label="Actions" style={styles.td}>
                      <div className="rm-action-row" style={styles.actionRow}>
                        <button type="button" onClick={() => previewDependencies(event)} style={styles.secondaryButton}>
                          Dependencies
                        </button>
                        <button type="button" disabled style={styles.disabledButton} title="Ingest events are view-only">
                          View only
                        </button>
                        <button type="button" disabled style={styles.disabledDangerButton} title="Hard delete is disabled for ingest events">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 900 }}>Camera ingest event controls</h4>
        <div style={styles.checkList}>
          <div style={styles.checkItem}>View-only by design: no archive, restore, redact, or delete actions.</div>
          <div style={styles.checkItem}>Filters support failed events, zero-decode events, parse status, camera ID, job ID, and text search.</div>
          <div style={styles.checkItem}>Dependency analysis shows camera, job, and generated ANPR sightings.</div>
          <div style={styles.checkItem}>Dependency inspection writes to the central audit_log as camera_ingest_event / dependency_viewed.</div>
          <div style={styles.checkItem}>Sightings and Plate Sessions are implemented as separate RM10C/RM10D view-only domains.</div>
        </div>
      </div>

      {dependencyModalOpen && (
        <ModalWrapper
          title="Camera ingest event dependency map"
          subtitle={`${selectedLabel} — source camera, job link, generated sightings, and operational status.`}
          onClose={() => setDependencyModalOpen(false)}
          maxWidth="980px"
          width="min(980px, 96vw)"
        >
          <div style={styles.modalBody}>
            {dependencyLoading && <div style={styles.emptyPanel}>Loading ingest event dependency details...</div>}
            {dependencyError && <div style={styles.warning}>{dependencyError}</div>}

            {!dependencyLoading && !dependencyError && dependencyResult && (
              <>
                <div style={styles.warning}>
                  <strong>Recommendation:</strong> {safeText(dependencyResult?.recommendation, "Ingest events are view-only operational records.")}
                </div>

                <div className="rm-modal-stats-grid" style={styles.modalStatsGrid}>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{safeText(dependencyResult?.event?.decode_count, "0")}</p>
                    <p style={styles.statLabel}>Decode count</p>
                    <p style={styles.statDetail}>Payload-level decode count.</p>
                  </div>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{safeText(dependencyResult?.dependency_counts?.anpr_sightings, "0")}</p>
                    <p style={styles.statLabel}>Sightings</p>
                    <p style={styles.statDetail}>Rows generated from this ingest event.</p>
                  </div>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>View-only</p>
                    <p style={styles.statLabel}>Lifecycle</p>
                    <p style={styles.statDetail}>No delete/archive/redact controls.</p>
                  </div>
                </div>

                {dependencyResult?.event?.parse_error && (
                  <div style={styles.warning}>
                    <strong>Parse error</strong>
                    <div style={{ marginTop: 8 }}>{dependencyResult.event.parse_error}</div>
                  </div>
                )}

                {visibleGroups.length === 0 ? (
                  <div style={{ ...styles.safePanel, marginTop: 14 }}>
                    No linked ingest event dependencies were returned by the dependency endpoint.
                  </div>
                ) : (
                  <div style={styles.dependencyModalList}>
                    {visibleGroups.map((group) => (
                      <div key={group.key} style={styles.dependencyModalGroup(group.severity, group.blocking_delete)}>
                        <div style={styles.dependencyModalGroupHeader}>
                          <div>
                            <div style={{ fontWeight: 900 }}>{group.label}</div>
                            <div style={styles.subtle}>{group.note || group.table}</div>
                          </div>
                          <span style={styles.statusBadge}>{Number(group.count || 0)}</span>
                        </div>

                        {(group.records || []).length > 0 ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                            {(group.records || []).map((record, index) => (
                              <div key={`${group.key}-${record.id || index}`} style={styles.dependencyRecordRow}>
                                <div>
                                  <div style={{ fontWeight: 800 }}>{safeText(record.label || record.id)}</div>
                                  <div style={styles.subtle}>
                                    ID: {safeText(record.id)}{record.status ? ` · ${record.status}` : ""}{record.frame_ts_utc ? ` · ${formatDateTime(record.frame_ts_utc)}` : ""}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ ...styles.subtle, marginTop: 10 }}>
                            {Number(group.count || 0)} linked record(s). No preview rows returned.
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            <div className="rm-resource-footer" style={styles.resourceRedactionFooter}>
              <button
                type="button"
                onClick={() => selectedEvent && previewDependencies(selectedEvent, true)}
                style={styles.resourceModalActionButton(styles.secondaryButton)}
                disabled={!selectedEvent?.id || dependencyLoading}
              >
                Re-check dependencies
              </button>
              <button type="button" disabled style={styles.resourceModalActionButton(styles.disabledButton)} title="Ingest events are view-only">
                View only
              </button>
              <button type="button" disabled style={styles.resourceModalActionButton(styles.disabledDangerButton)} title="Delete is disabled for ingest events">
                Delete disabled
              </button>
              <button
                type="button"
                onClick={() => setDependencyModalOpen(false)}
                style={styles.resourceModalActionButton(styles.primaryButton)}
              >
                Close
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}
    </div>
  );
};


const AnprSightingManagementSection = ({ styles, accent, actionButton }) => {
  const [query, setQuery] = useState("");
  const [plate, setPlate] = useState("");
  const [jobId, setJobId] = useState("");
  const [cameraId, setCameraId] = useState("");
  const [ingestEventId, setIngestEventId] = useState("");
  const [dvlaStatus, setDvlaStatus] = useState("");
  const [vehicleMake, setVehicleMake] = useState("");
  const [vehicleModel, setVehicleModel] = useState("");
  const [fromTs, setFromTs] = useState("");
  const [toTs, setToTs] = useState("");
  const [minConfidence, setMinConfidence] = useState("");
  const [withImagesOnly, setWithImagesOnly] = useState(false);
  const [sightings, setSightings] = useState([]);
  const [selectedSighting, setSelectedSighting] = useState(null);
  const [dependencyResult, setDependencyResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dependencyLoading, setDependencyLoading] = useState(false);
  const [error, setError] = useState("");
  const [dependencyError, setDependencyError] = useState("");
  const [dependencyModalOpen, setDependencyModalOpen] = useState(false);

  const sightingLabel = (sighting) => sighting?.plate_normalized || sighting?.plate || sighting?.spaced_vrm || `Sighting #${sighting?.id || ""}`;

  const loadSightings = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        q: query || undefined,
        plate: plate || undefined,
        job_id: jobId || undefined,
        camera_id: cameraId || undefined,
        ingest_event_id: ingestEventId || undefined,
        dvla_status: dvlaStatus || undefined,
        vehicle_make: vehicleMake || undefined,
        vehicle_model: vehicleModel || undefined,
        from_ts: fromTs || undefined,
        to_ts: toTs || undefined,
        min_confidence: minConfidence || undefined,
        with_images_only: withImagesOnly ? "true" : "false",
        limit: 100,
      };
      const response = await api.get("/anpr/cameras/sightings/admin/search", { params });
      const rows = normaliseCollection(response.data);
      setSightings(rows);
      if (selectedSighting && !rows.some((row) => String(row.id) === String(selectedSighting.id))) {
        setSelectedSighting(null);
        setDependencyResult(null);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "ANPR sightings search failed. Backend endpoint /api/anpr/cameras/sightings/admin/search may not be available.");
      setSightings([]);
    } finally {
      setLoading(false);
    }
  }, [cameraId, dvlaStatus, fromTs, ingestEventId, jobId, minConfidence, plate, query, selectedSighting, toTs, vehicleMake, vehicleModel, withImagesOnly]);

  useEffect(() => {
    loadSightings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewDependencies = async (sighting, openModal = true) => {
    if (!sighting?.id) return;
    setSelectedSighting(sighting);
    setDependencyResult(null);
    setDependencyError("");
    if (openModal) setDependencyModalOpen(true);
    setDependencyLoading(true);
    try {
      const response = await api.get(`/anpr/cameras/sightings/${sighting.id}/dependencies`);
      setDependencyResult(response.data || {});
    } catch (err) {
      setDependencyError(err?.response?.data?.detail || err?.message || "ANPR sighting dependency analysis failed.");
    } finally {
      setDependencyLoading(false);
    }
  };

  const groups = dependencyGroupsFromResult(dependencyResult);
  const visibleGroups = groups.filter((group) => Number(group.count || 0) > 0 || (group.records || []).length > 0);
  const blockers = groups.filter((group) => group.blocking_delete && Number(group.count || 0) > 0);
  const selectedLabel = sightingLabel(selectedSighting);

  const dvlaBadge = (value) => {
    const label = safeText(value, "unknown");
    const lower = String(label).toLowerCase();
    const ok = lower === "200" || lower === "ok" || lower === "success";
    const missing = label === "-" || lower === "unknown" || lower === "null";
    return <span style={{ ...styles.statusBadge, background: ok ? "#ecfdf5" : missing ? "#f3f4f6" : "#fffbeb", color: ok ? "#047857" : missing ? "#4b5563" : "#92400e" }}>{label}</span>;
  };

  const imageCount = (sighting) => [sighting?.snapshot_url, sighting?.plate_secondary_url, sighting?.overview_url].filter(Boolean).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="rm-hero" style={styles.hero}>
        <div className="rm-title-row" style={styles.titleRow}>
          <div>
            <p style={styles.eyebrow}>ANPR / Reads & Sightings</p>
            <h3 style={styles.h3}>ANPR Sightings Records Management</h3>
            <p style={styles.description}>
              RM10C manages ANPR reads as view-only evidential records. Search, filter, inspect dependencies,
              review camera/job/ingest links, image references, DVLA state, GPS context, and raw decode detail.
              Archive, delete, and redact are intentionally disabled until retention governance exists.
            </p>
          </div>
          <span style={styles.badge}>Phase RM10C: current</span>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <div className="rm-toolbar-grid" style={{ ...styles.toolbarGrid, gridTemplateColumns: "minmax(220px, 1fr) 160px 110px 110px 150px 120px" }}>
          <label style={styles.fieldLabel}>
            Search sightings
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Plate, camera, job, vehicle..." style={styles.input} />
          </label>
          <label style={styles.fieldLabel}>
            Plate
            <input value={plate} onChange={(event) => setPlate(event.target.value)} placeholder="AB12CDE" style={styles.input} />
          </label>
          <label style={styles.fieldLabel}>
            Job ID
            <input value={jobId} onChange={(event) => setJobId(event.target.value)} placeholder="104" style={styles.input} />
          </label>
          <label style={styles.fieldLabel}>
            Camera ID
            <input value={cameraId} onChange={(event) => setCameraId(event.target.value)} placeholder="4" style={styles.input} />
          </label>
          <label style={styles.fieldLabel}>
            DVLA status
            <input value={dvlaStatus} onChange={(event) => setDvlaStatus(event.target.value)} placeholder="200 / null / error" style={styles.input} />
          </label>
          <button type="button" onClick={loadSightings} disabled={loading} style={styles.primaryButton}>
            {loading ? "Loading..." : "Search"}
          </button>
        </div>

        <div className="rm-toolbar-grid" style={{ ...styles.toolbarGrid, gridTemplateColumns: "130px 150px 150px 150px 150px 170px", marginTop: 12 }}>
          <label style={styles.fieldLabel}>
            Ingest Event ID
            <input value={ingestEventId} onChange={(event) => setIngestEventId(event.target.value)} placeholder="Event ID" style={styles.input} />
          </label>
          <label style={styles.fieldLabel}>
            Vehicle make
            <input value={vehicleMake} onChange={(event) => setVehicleMake(event.target.value)} placeholder="Ford" style={styles.input} />
          </label>
          <label style={styles.fieldLabel}>
            Vehicle model
            <input value={vehicleModel} onChange={(event) => setVehicleModel(event.target.value)} placeholder="Transit" style={styles.input} />
          </label>
          <label style={styles.fieldLabel}>
            From
            <input type="datetime-local" value={fromTs} onChange={(event) => setFromTs(event.target.value)} style={styles.input} />
          </label>
          <label style={styles.fieldLabel}>
            To
            <input type="datetime-local" value={toTs} onChange={(event) => setToTs(event.target.value)} style={styles.input} />
          </label>
          <label style={styles.fieldLabel}>
            Min confidence
            <input value={minConfidence} onChange={(event) => setMinConfidence(event.target.value)} placeholder="75" style={styles.input} />
          </label>
        </div>

        <label style={{ ...styles.checkboxLabel, marginTop: 10 }}>
          <input type="checkbox" checked={withImagesOnly} onChange={(event) => setWithImagesOnly(event.target.checked)} />
          With images only
        </label>

        {error && <div style={styles.warning}>{error}</div>}

        <div className="rm-table-shell" style={styles.tableShell}>
          <table className="rm-responsive-table" style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Plate</th>
                <th style={styles.th}>Timestamp</th>
                <th style={styles.th}>Camera</th>
                <th style={styles.th}>Job</th>
                <th style={styles.th}>Score</th>
                <th style={styles.th}>DVLA</th>
                <th style={styles.th}>Vehicle</th>
                <th style={styles.th}>Images</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sightings.length === 0 && !loading && (
                <tr>
                  <td className="rm-empty-table-cell" colSpan="9" style={styles.emptyTableCell}>
                    No ANPR sightings loaded. This view uses /api/anpr/cameras/sightings/admin/search.
                  </td>
                </tr>
              )}

              {sightings.map((sighting) => {
                const isSelected = selectedSighting && String(selectedSighting.id) === String(sighting.id);
                return (
                  <tr key={sighting.id || JSON.stringify(sighting)} style={isSelected ? styles.selectedRow : undefined}>
                    <td data-label="Plate" style={styles.td}>
                      <div style={{ fontWeight: 900 }}>{sightingLabel(sighting)}</div>
                      <div style={styles.subtle}>ID: {safeText(sighting.id)} · Event: {safeText(sighting.ingest_event_id)}</div>
                    </td>
                    <td data-label="Timestamp" style={styles.td}>{formatDateTime(sighting.frame_ts_utc || sighting.created_at)}</td>
                    <td data-label="Camera" style={styles.td}>
                      <div>{safeText(sighting.camera_name || sighting.camera_device_id)}</div>
                      <div style={styles.subtle}>Camera ID: {safeText(sighting.camera_device_id)}</div>
                    </td>
                    <td data-label="Job" style={styles.td}>
                      <div>{safeText(sighting.job_ref || sighting.job_id)}</div>
                      <div style={styles.subtle}>Run: {safeText(sighting.run_id)}</div>
                    </td>
                    <td data-label="Score" style={styles.td}>{safeText(sighting.score || sighting.yolo_conf || sighting.ocr_conf)}</td>
                    <td data-label="DVLA" style={styles.td}>{dvlaBadge(sighting.dvla_status)}</td>
                    <td data-label="Vehicle" style={styles.td}>
                      <div>{safeText(`${sighting.vehicle_make || ""} ${sighting.vehicle_model || ""}`.trim())}</div>
                      <div style={styles.subtle}>{safeText(sighting.vehicle_color || sighting.vehicle_category)}</div>
                    </td>
                    <td data-label="Images" style={styles.td}>{imageCount(sighting)} image(s)</td>
                    <td data-label="Actions" style={styles.td}>
                      <div className="rm-action-row" style={styles.actionRow}>
                        <button type="button" onClick={() => previewDependencies(sighting)} style={styles.secondaryButton}>
                          Dependencies
                        </button>
                        <button type="button" disabled style={styles.disabledDangerButton} title="Sightings are view-only until retention policy exists">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 900 }}>ANPR sighting governance</h4>
        <div style={styles.checkList}>
          <div style={styles.checkItem}>Sightings are view-only evidential records.</div>
          <div style={styles.checkItem}>Dependency analysis links sightings to camera devices, ingest events, jobs, plate sessions, and image assets.</div>
          <div style={styles.checkItem}>Filters support plate, job, camera, ingest event, date range, confidence, DVLA status, make/model, and image availability.</div>
          <div style={styles.checkItem}>Archive, redact, and delete are intentionally disabled until a formal ANPR retention policy exists.</div>
          <div style={styles.checkItem}>Dependency inspection writes to the central audit log as anpr_sighting / dependency_viewed.</div>
        </div>
      </div>

      {dependencyModalOpen && (
        <ModalWrapper
          title="ANPR sighting dependency map"
          subtitle={`${selectedLabel} — camera, ingest, job, plate session, image, DVLA, GPS, and raw decode context.`}
          onClose={() => setDependencyModalOpen(false)}
          maxWidth="980px"
          width="min(980px, 96vw)"
        >
          <div style={styles.modalBody}>
            {dependencyLoading && <div style={styles.emptyPanel}>Loading ANPR sighting dependency details...</div>}
            {dependencyError && <div style={styles.warning}>{dependencyError}</div>}

            {!dependencyLoading && !dependencyError && dependencyResult && (
              <>
                <div style={blockers.length > 0 ? styles.warning : styles.safePanel}>
                  <strong>Recommendation:</strong> {safeText(dependencyResult?.recommendation, "Review dependency state before changing this sighting.")}
                </div>

                <div className="rm-modal-stats-grid" style={styles.modalStatsGrid}>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{safeText(dependencyResult?.sighting?.plate_normalized || dependencyResult?.sighting?.plate)}</p>
                    <p style={styles.statLabel}>Plate</p>
                    <p style={styles.statDetail}>Normalized read value.</p>
                  </div>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{safeText(dependencyResult?.sighting?.score || dependencyResult?.sighting?.yolo_conf || dependencyResult?.sighting?.ocr_conf)}</p>
                    <p style={styles.statLabel}>Confidence</p>
                    <p style={styles.statDetail}>Best available score.</p>
                  </div>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{safeText(dependencyResult?.total_dependencies, "0")}</p>
                    <p style={styles.statLabel}>Linked records</p>
                    <p style={styles.statDetail}>Traceability links returned.</p>
                  </div>
                </div>

                {visibleGroups.length === 0 ? (
                  <div style={{ ...styles.safePanel, marginTop: 14 }}>
                    No linked sighting dependencies were returned by the dependency endpoint.
                  </div>
                ) : (
                  <div style={styles.dependencyModalList}>
                    {visibleGroups.map((group) => (
                      <div key={group.key} style={styles.dependencyModalGroup(group.severity, group.blocking_delete)}>
                        <div style={styles.dependencyModalGroupHeader}>
                          <div>
                            <div style={{ fontWeight: 900 }}>{group.label}</div>
                            <div style={styles.subtle}>{group.note || group.table}</div>
                          </div>
                          <span style={styles.statusBadge}>{Number(group.count || 0)}</span>
                        </div>

                        {(group.records || []).length > 0 ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                            {(group.records || []).map((record, index) => (
                              <div key={`${group.key}-${record.id || index}`} style={styles.dependencyRecordRow}>
                                <div>
                                  <div style={{ fontWeight: 800 }}>{safeText(record.label || record.id)}</div>
                                  <div style={styles.subtle}>
                                    ID: {safeText(record.id)}{record.status ? ` · ${record.status}` : ""}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ ...styles.subtle, marginTop: 10 }}>
                            {Number(group.count || 0)} linked record(s). No preview rows returned.
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="rm-split-grid" style={styles.splitGrid}>
                  <div className="rm-panel-card" style={styles.placeholder}>
                    <h4 style={{ margin: "0 0 8px 0", fontSize: 15, fontWeight: 900 }}>DVLA / GPS / motion</h4>
                    <div style={styles.checkList}>
                      <div style={styles.checkItem}>DVLA status: {safeText(dependencyResult?.sighting?.dvla_status)}</div>
                      <div style={styles.checkItem}>DVLA cached: {formatDateTime(dependencyResult?.sighting?.dvla_cached_at)}</div>
                      <div style={styles.checkItem}>Location: {safeText(dependencyResult?.sighting?.lat)}, {safeText(dependencyResult?.sighting?.lon)}</div>
                      <div style={styles.checkItem}>Motion: {safeText(dependencyResult?.sighting?.motion)} · Direction: {safeText(dependencyResult?.sighting?.direction_deg)}</div>
                    </div>
                  </div>
                  <div className="rm-panel-card" style={styles.placeholder}>
                    <h4 style={{ margin: "0 0 8px 0", fontSize: 15, fontWeight: 900 }}>Raw decode JSON</h4>
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12, color: "#374151", maxHeight: 260, overflow: "auto" }}>
                      {formatJsonPreview(dependencyResult?.sighting?.raw_decode_json)}
                    </pre>
                  </div>
                </div>
              </>
            )}

            <div className="rm-resource-footer" style={styles.resourceRedactionFooter}>
              <button
                type="button"
                onClick={() => selectedSighting && previewDependencies(selectedSighting, true)}
                style={styles.resourceModalActionButton(styles.secondaryButton)}
                disabled={!selectedSighting?.id || dependencyLoading}
              >
                Re-check dependencies
              </button>
              <button type="button" disabled style={styles.resourceModalActionButton(styles.disabledDangerButton)} title="Sightings are view-only">
                Archive disabled
              </button>
              <button type="button" disabled style={styles.resourceModalActionButton(styles.disabledDangerButton)} title="Sightings are view-only">
                Delete disabled
              </button>
              <button type="button" onClick={() => setDependencyModalOpen(false)} style={styles.resourceModalActionButton(styles.primaryButton)}>
                Close
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}
    </div>
  );
};


const PlateSessionManagementSection = ({ styles, accent, actionButton }) => {
  const [query, setQuery] = useState("");
  const [plate, setPlate] = useState("");
  const [runId, setRunId] = useState("");
  const [minHits, setMinHits] = useState("");
  const [minDwell, setMinDwell] = useState("");
  const [minConfidence, setMinConfidence] = useState("");
  const [fromCreatedAt, setFromCreatedAt] = useState("");
  const [toCreatedAt, setToCreatedAt] = useState("");
  const [withImagesOnly, setWithImagesOnly] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [dependencyResult, setDependencyResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dependencyLoading, setDependencyLoading] = useState(false);
  const [error, setError] = useState("");
  const [dependencyError, setDependencyError] = useState("");
  const [dependencyModalOpen, setDependencyModalOpen] = useState(false);

  const sessionLabel = (session) => session?.plate_normalized || `Plate session #${session?.id || ""}`;

  const loadSessions = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        q: query || undefined,
        plate: plate || undefined,
        run_id: runId || undefined,
        min_hits: minHits || undefined,
        min_dwell_s: minDwell || undefined,
        min_confidence: minConfidence || undefined,
        from_created_at: fromCreatedAt || undefined,
        to_created_at: toCreatedAt || undefined,
        with_images_only: withImagesOnly ? "true" : "false",
        limit: 100,
      };
      const response = await api.get("/anpr/cameras/plate-sessions/admin/search", { params });
      const rows = normaliseCollection(response.data);
      setSessions(rows);
      if (selectedSession && !rows.some((row) => String(row.id) === String(selectedSession.id))) {
        setSelectedSession(null);
        setDependencyResult(null);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Plate sessions search failed. Backend endpoint /api/anpr/cameras/plate-sessions/admin/search may not be available.");
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [fromCreatedAt, minConfidence, minDwell, minHits, plate, query, runId, selectedSession, toCreatedAt, withImagesOnly]);

  useEffect(() => {
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewDependencies = async (session, openModal = true) => {
    if (!session?.id) return;
    setSelectedSession(session);
    setDependencyResult(null);
    setDependencyError("");
    if (openModal) setDependencyModalOpen(true);
    setDependencyLoading(true);
    try {
      const response = await api.get(`/anpr/cameras/plate-sessions/${session.id}/dependencies`);
      setDependencyResult(response.data || {});
    } catch (err) {
      setDependencyError(err?.response?.data?.detail || err?.message || "Plate session dependency analysis failed.");
    } finally {
      setDependencyLoading(false);
    }
  };

  const groups = dependencyGroupsFromResult(dependencyResult);
  const visibleGroups = groups.filter((group) => Number(group.count || 0) > 0 || (group.records || []).length > 0);
  const blockers = groups.filter((group) => group.blocking_delete && Number(group.count || 0) > 0);
  const selectedLabel = sessionLabel(selectedSession);

  const imageCount = (session) => [session?.first_snapshot_url, session?.best_snapshot_url, session?.best_snapshot_path].filter(Boolean).length;
  const formatSeconds = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return String(value);
    return `${numeric.toFixed(numeric >= 10 ? 0 : 2)}s`;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="rm-hero" style={styles.hero}>
        <div className="rm-title-row" style={styles.titleRow}>
          <div>
            <p style={styles.eyebrow}>ANPR / Plate Sessions</p>
            <h3 style={styles.h3}>Plate Session Records Management</h3>
            <p style={styles.description}>
              RM10D completes the ANPR view-only records stack. Plate sessions are derived journey/session summaries
              from ANPR sightings. This view supports search, confidence/hit/dwell filters, image indicators,
              linked sighting dependency inspection, and central audit logging. Destructive controls remain disabled.
            </p>
          </div>
          <span style={styles.badge}>Phase RM10D: complete</span>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <div className="rm-toolbar-grid" style={{ ...styles.toolbarGrid, gridTemplateColumns: "minmax(180px, 1fr) 150px 100px 100px 110px 120px" }}>
          <label style={styles.fieldLabel}>
            Search sessions
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Plate, run ID, session index..." style={styles.input} />
          </label>
          <label style={styles.fieldLabel}>
            Plate
            <input value={plate} onChange={(event) => setPlate(event.target.value)} placeholder="AB12CDE" style={styles.input} />
          </label>
          <label style={styles.fieldLabel}>
            Run ID
            <input value={runId} onChange={(event) => setRunId(event.target.value)} placeholder="Run" style={styles.input} />
          </label>
          <label style={styles.fieldLabel}>
            Min hits
            <input value={minHits} onChange={(event) => setMinHits(event.target.value)} placeholder="Hits" style={styles.input} />
          </label>
          <label style={styles.checkboxLabel}>
            <input type="checkbox" checked={withImagesOnly} onChange={(event) => setWithImagesOnly(event.target.checked)} />
            Images only
          </label>
          <button type="button" onClick={loadSessions} disabled={loading} style={styles.primaryButton}>
            {loading ? "Loading..." : "Search"}
          </button>
        </div>

        <div className="rm-toolbar-grid" style={{ ...styles.toolbarGrid, gridTemplateColumns: "repeat(4, minmax(140px, 1fr))", marginTop: 12 }}>
          <label style={styles.fieldLabel}>
            Min dwell seconds
            <input value={minDwell} onChange={(event) => setMinDwell(event.target.value)} placeholder="e.g. 5" style={styles.input} />
          </label>
          <label style={styles.fieldLabel}>
            Min confidence
            <input value={minConfidence} onChange={(event) => setMinConfidence(event.target.value)} placeholder="e.g. 75" style={styles.input} />
          </label>
          <label style={styles.fieldLabel}>
            Created from
            <input type="datetime-local" value={fromCreatedAt} onChange={(event) => setFromCreatedAt(event.target.value)} style={styles.input} />
          </label>
          <label style={styles.fieldLabel}>
            Created to
            <input type="datetime-local" value={toCreatedAt} onChange={(event) => setToCreatedAt(event.target.value)} style={styles.input} />
          </label>
        </div>

        {error && <div style={styles.warning}>{error}</div>}

        <div className="rm-table-shell" style={styles.tableShell}>
          <table className="rm-responsive-table" style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Plate</th>
                <th style={styles.th}>Run / Session</th>
                <th style={styles.th}>Hits</th>
                <th style={styles.th}>Dwell</th>
                <th style={styles.th}>Confidence</th>
                <th style={styles.th}>Frames</th>
                <th style={styles.th}>Geo / Motion</th>
                <th style={styles.th}>Images</th>
                <th style={styles.th}>Updated</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.length === 0 && !loading && (
                <tr>
                  <td className="rm-empty-table-cell" colSpan="10" style={styles.emptyTableCell}>
                    No plate session records loaded. This view uses /api/anpr/cameras/plate-sessions/admin/search.
                  </td>
                </tr>
              )}

              {sessions.map((session) => {
                const isSelected = selectedSession && String(selectedSession.id) === String(session.id);
                return (
                  <tr key={session.id || `${session.plate_normalized}-${session.session_idx}`} style={isSelected ? styles.selectedRow : undefined}>
                    <td data-label="Plate" style={styles.td}>
                      <div style={{ fontWeight: 900 }}>{safeText(session.plate_normalized)}</div>
                      <div style={styles.subtle}>ID: {safeText(session.id)}</div>
                    </td>
                    <td data-label="Run / Session" style={styles.td}>
                      <div>Run: {safeText(session.run_id)}</div>
                      <div style={styles.subtle}>Session: {safeText(session.session_idx)}</div>
                    </td>
                    <td data-label="Hits" style={styles.td}>{safeText(session.hit_count, "0")}</td>
                    <td data-label="Dwell" style={styles.td}>{formatSeconds(session.dwell_s)}</td>
                    <td data-label="Confidence" style={styles.td}>
                      <div>Max: {safeText(session.max_yolo_conf)}</div>
                      <div style={styles.subtle}>Avg: {safeText(session.avg_yolo_conf)}</div>
                    </td>
                    <td data-label="Frames" style={styles.td}>
                      <div>{safeText(session.first_frame_idx)} → {safeText(session.last_frame_idx)}</div>
                      <div style={styles.subtle}>{formatSeconds(session.first_seen_s)} → {formatSeconds(session.last_seen_s)}</div>
                    </td>
                    <td data-label="Geo / Motion" style={styles.td}>
                      <div>{safeText(session.lat_first)}, {safeText(session.lon_first)}</div>
                      <div style={styles.subtle}>Speed: {safeText(session.speed_mps_avg)} · Heading: {safeText(session.heading_deg_avg)}</div>
                    </td>
                    <td data-label="Images" style={styles.td}>{imageCount(session)} image(s)</td>
                    <td data-label="Updated" style={styles.td}>{formatDateTime(session.updated_at || session.created_at)}</td>
                    <td data-label="Actions" style={styles.td}>
                      <div className="rm-action-row" style={styles.actionRow}>
                        <button type="button" onClick={() => previewDependencies(session)} style={styles.secondaryButton}>
                          Dependencies
                        </button>
                        <button type="button" disabled style={styles.disabledDangerButton} title="Plate sessions are view-only until retention policy exists">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 900 }}>Plate session governance</h4>
        <div style={styles.checkList}>
          <div style={styles.checkItem}>Plate sessions are view-only derived ANPR records.</div>
          <div style={styles.checkItem}>Dependency analysis links sessions to related sightings, run context, image assets, and geo/motion summaries.</div>
          <div style={styles.checkItem}>Filters support plate, run, hit count, dwell time, confidence, created range, and image availability.</div>
          <div style={styles.checkItem}>Archive, redact, and delete are intentionally disabled until a formal ANPR retention policy exists.</div>
          <div style={styles.checkItem}>Dependency inspection writes to the central audit log as plate_session / dependency_viewed.</div>
        </div>
      </div>

      {dependencyModalOpen && (
        <ModalWrapper
          title="Plate session dependency map"
          subtitle={`${selectedLabel} — related sightings, run context, images, GPS, dwell, confidence, and motion summary.`}
          onClose={() => setDependencyModalOpen(false)}
          maxWidth="980px"
          width="min(980px, 96vw)"
        >
          <div style={styles.modalBody}>
            {dependencyLoading && <div style={styles.emptyPanel}>Loading plate session dependency details...</div>}
            {dependencyError && <div style={styles.warning}>{dependencyError}</div>}

            {!dependencyLoading && !dependencyError && dependencyResult && (
              <>
                <div style={blockers.length > 0 ? styles.warning : styles.safePanel}>
                  <strong>Recommendation:</strong> {safeText(dependencyResult?.recommendation, "Review dependency state before changing this plate session.")}
                </div>

                <div className="rm-modal-stats-grid" style={styles.modalStatsGrid}>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{safeText(dependencyResult?.plate_session?.plate_normalized)}</p>
                    <p style={styles.statLabel}>Plate</p>
                    <p style={styles.statDetail}>Normalized session plate.</p>
                  </div>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{safeText(dependencyResult?.plate_session?.hit_count, "0")}</p>
                    <p style={styles.statLabel}>Hits</p>
                    <p style={styles.statDetail}>Sightings grouped into this session.</p>
                  </div>
                  <div style={styles.statCard}>
                    <p style={styles.statValue}>{formatSeconds(dependencyResult?.plate_session?.dwell_s)}</p>
                    <p style={styles.statLabel}>Dwell</p>
                    <p style={styles.statDetail}>Session duration.</p>
                  </div>
                </div>

                {visibleGroups.length === 0 ? (
                  <div style={{ ...styles.safePanel, marginTop: 14 }}>
                    No linked plate session dependencies were returned by the dependency endpoint.
                  </div>
                ) : (
                  <div style={styles.dependencyModalList}>
                    {visibleGroups.map((group) => (
                      <div key={group.key} style={styles.dependencyModalGroup(group.severity, group.blocking_delete)}>
                        <div style={styles.dependencyModalGroupHeader}>
                          <div>
                            <div style={{ fontWeight: 900 }}>{group.label}</div>
                            <div style={styles.subtle}>{group.note || group.table}</div>
                          </div>
                          <span style={styles.statusBadge}>{Number(group.count || 0)}</span>
                        </div>

                        {(group.records || []).length > 0 ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                            {(group.records || []).map((record, index) => (
                              <div key={`${group.key}-${record.id || index}`} style={styles.dependencyRecordRow}>
                                <div>
                                  <div style={{ fontWeight: 800 }}>{safeText(record.label || record.id)}</div>
                                  <div style={styles.subtle}>
                                    ID: {safeText(record.id)}{record.status ? ` · ${record.status}` : ""}{record.frame_ts_utc ? ` · ${formatDateTime(record.frame_ts_utc)}` : ""}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ ...styles.subtle, marginTop: 10 }}>
                            {Number(group.count || 0)} linked record(s). No preview rows returned.
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="rm-split-grid" style={styles.splitGrid}>
                  <div className="rm-panel-card" style={styles.placeholder}>
                    <h4 style={{ margin: "0 0 8px 0", fontSize: 15, fontWeight: 900 }}>Session facts</h4>
                    <div style={styles.checkList}>
                      <div style={styles.checkItem}>Run: {safeText(dependencyResult?.plate_session?.run_id)}</div>
                      <div style={styles.checkItem}>First/last frame: {safeText(dependencyResult?.plate_session?.first_frame_idx)} → {safeText(dependencyResult?.plate_session?.last_frame_idx)}</div>
                      <div style={styles.checkItem}>Confidence max/avg: {safeText(dependencyResult?.plate_session?.max_yolo_conf)} / {safeText(dependencyResult?.plate_session?.avg_yolo_conf)}</div>
                      <div style={styles.checkItem}>Speed/heading avg: {safeText(dependencyResult?.plate_session?.speed_mps_avg)} / {safeText(dependencyResult?.plate_session?.heading_deg_avg)}</div>
                    </div>
                  </div>
                  <div className="rm-panel-card" style={styles.placeholder}>
                    <h4 style={{ margin: "0 0 8px 0", fontSize: 15, fontWeight: 900 }}>Image references</h4>
                    <div style={styles.checkList}>
                      <div style={styles.checkItem}>First snapshot URL: {safeText(dependencyResult?.plate_session?.first_snapshot_url)}</div>
                      <div style={styles.checkItem}>Best snapshot URL: {safeText(dependencyResult?.plate_session?.best_snapshot_url)}</div>
                      <div style={styles.checkItem}>Best snapshot path: {safeText(dependencyResult?.plate_session?.best_snapshot_path)}</div>
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="rm-resource-footer" style={styles.resourceRedactionFooter}>
              <button
                type="button"
                onClick={() => selectedSession && previewDependencies(selectedSession, true)}
                style={styles.resourceModalActionButton(styles.secondaryButton)}
                disabled={!selectedSession?.id || dependencyLoading}
              >
                Re-check dependencies
              </button>
              <button type="button" disabled style={styles.resourceModalActionButton(styles.disabledDangerButton)} title="Plate sessions are view-only">
                Archive disabled
              </button>
              <button type="button" disabled style={styles.resourceModalActionButton(styles.disabledDangerButton)} title="Plate sessions are view-only">
                Delete disabled
              </button>
              <button type="button" onClick={() => setDependencyModalOpen(false)} style={styles.resourceModalActionButton(styles.primaryButton)}>
                Close
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}
    </div>
  );
};

const AuditLogManagementSection = ({ styles, accent, actionButton }) => {
  const [query, setQuery] = useState("");
  const [entityType, setEntityType] = useState("");
  const [action, setAction] = useState("");
  const [auditRows, setAuditRows] = useState([]);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadAuditRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        q: query || undefined,
        entity_type: entityType || undefined,
        action: action || undefined,
        limit: 100,
      };
      const response = await api.get("/audit-log/admin/search", { params });
      const rows = normaliseCollection(response.data);
      setAuditRows(rows);
      if (selectedAudit && !rows.some((row) => String(row.id) === String(selectedAudit.id))) {
        setSelectedAudit(null);
      }
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Audit log search failed. Backend endpoint /api/audit-log/admin/search may not be available yet."
      );
      setAuditRows([]);
    } finally {
      setLoading(false);
    }
  }, [action, entityType, query, selectedAudit]);

  useEffect(() => {
    loadAuditRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const actionBadge = (value) => {
    const lower = String(value || "unknown").toLowerCase();
    const background = lower === "archive" ? "#fffbeb" : lower === "restore" ? "#ecfdf5" : `${accent}14`;
    const color = lower === "archive" ? "#92400e" : lower === "restore" ? "#065f46" : accent;
    return <span style={{ ...styles.statusBadge, background, color }}>{safeText(value, "unknown")}</span>;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="rm-hero" style={styles.hero}>
        <div className="rm-title-row" style={styles.titleRow}>
          <div>
            <p style={styles.eyebrow}>Governance / Audit Log</p>
            <h3 style={styles.h3}>Records Management Audit Log</h3>
            <p style={styles.description}>
              Central lifecycle audit viewer for dependency inspections, archive actions, restore actions,
              future redaction workflows, delete attempts, recovery actions, and purge governance.
            </p>
          </div>
          <span style={styles.badge}>Phase RM8C: audit active</span>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <div className="rm-toolbar-grid" style={styles.toolbarGrid}>
          <label style={styles.fieldLabel}>
            Search audit rows
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Entity ID, action, entity type, details..."
              style={styles.input}
            />
          </label>

          <label style={styles.fieldLabel}>
            Entity
            <select value={entityType} onChange={(event) => setEntityType(event.target.value)} style={styles.input}>
              {AUDIT_ENTITY_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label style={styles.fieldLabel}>
            Action
            <select value={action} onChange={(event) => setAction(event.target.value)} style={styles.input}>
              {AUDIT_ACTION_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <button type="button" onClick={loadAuditRows} disabled={loading} style={styles.primaryButton}>
            {loading ? "Loading..." : "Search"}
          </button>
        </div>

        {error && <div style={styles.warning}>{error}</div>}

        <div className="rm-table-shell" style={styles.tableShell}>
          <table className="rm-responsive-table" style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Timestamp</th>
                <th style={styles.th}>User</th>
                <th style={styles.th}>Action</th>
                <th style={styles.th}>Entity</th>
                <th style={styles.th}>Record</th>
                <th style={styles.th}>Details</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {auditRows.length === 0 && !loading && (
                <tr>
                  <td className="rm-empty-table-cell" colSpan="7" style={styles.emptyTableCell}>
                    No audit rows loaded. Generate events by opening dependencies, archiving, or restoring a record.
                  </td>
                </tr>
              )}

              {auditRows.map((row) => {
                const isSelected = selectedAudit && String(selectedAudit.id) === String(row.id);
                return (
                  <tr key={row.id || JSON.stringify(row)} style={isSelected ? styles.selectedRow : undefined}>
                    <td data-label="Timestamp" style={styles.td}>{formatDateTime(row.created_at || row.timestamp)}</td>
                    <td data-label="User" style={styles.td}>{safeText(row.user_email || row.user_name || row.user_id)}</td>
                    <td data-label="Action" style={styles.td}>{actionBadge(row.action)}</td>
                    <td data-label="Entity" style={styles.td}>{safeText(row.entity_type)}</td>
                    <td data-label="Record" style={styles.td}>{safeText(row.entity_id)}</td>
                    <td data-label="Details" style={styles.td}>
                      <div style={{ maxWidth: 360, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {formatAuditSummary(row)}
                      </div>
                    </td>
                    <td data-label="Actions" style={styles.td}>
                      <button type="button" onClick={() => setSelectedAudit(row)} style={styles.secondaryButton}>
                        Inspect
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 900 }}>Audit validation checklist</h4>
        <div style={styles.checkList}>
          <div style={styles.checkItem}>Audit capture is active for completed lifecycle domains: Quotes, Opportunities, Contacts, Customers, and Jobs.</div>
          <div style={styles.checkItem}>Dependency inspection writes dependency_viewed events with dependency counts.</div>
          <div style={styles.checkItem}>Archive and Restore actions write before/after lifecycle state where available.</div>
          <div style={styles.checkItem}>The table now displays readable summaries instead of raw JSON.</div>
          <div style={styles.checkItem}>No hard delete should be enabled until delete attempts, purge approvals, and recovery actions are also audited.</div>
        </div>
      </div>

      {selectedAudit && (
        <ModalWrapper
          title="Audit event detail"
          subtitle={`${safeText(selectedAudit.entity_type)} #${safeText(selectedAudit.entity_id)} — ${safeText(selectedAudit.action)}`}
          onClose={() => setSelectedAudit(null)}
          maxWidth="900px"
          width="min(900px, 96vw)"
        >
          <div style={styles.modalBody}>
            <div className="rm-modal-stats-grid" style={styles.modalStatsGrid}>
              <div style={styles.statCard}>
                <p style={styles.statValue}>{safeText(selectedAudit.id)}</p>
                <p style={styles.statLabel}>Audit ID</p>
                <p style={styles.statDetail}>Primary audit_log row.</p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statValue}>{safeText(selectedAudit.entity_type)}</p>
                <p style={styles.statLabel}>Entity</p>
                <p style={styles.statDetail}>Entity type captured by backend.</p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statValue}>{safeText(selectedAudit.action)}</p>
                <p style={styles.statLabel}>Action</p>
                <p style={styles.statDetail}>Lifecycle or governance event.</p>
              </div>
            </div>

            <div className="rm-panel-card" style={styles.placeholder}>
              <h4 style={{ margin: "0 0 8px 0", fontSize: 15, fontWeight: 900 }}>Event metadata</h4>
              <div style={styles.checkList}>
                <div style={styles.checkItem}>Created: {formatDateTime(selectedAudit.created_at || selectedAudit.timestamp)}</div>
                <div style={styles.checkItem}>Tenant: {safeText(selectedAudit.tenant_id)}</div>
                <div style={styles.checkItem}>User: {safeText(selectedAudit.user_email || selectedAudit.user_name || selectedAudit.user_id)}</div>
                <div style={styles.checkItem}>IP: {safeText(selectedAudit.ip_address)}</div>
                <div style={styles.checkItem}>User Agent: {safeText(selectedAudit.user_agent)}</div>
              </div>
            </div>

            <div className="rm-panel-card" style={styles.placeholder}>
              <h4 style={{ margin: "0 0 8px 0", fontSize: 15, fontWeight: 900 }}>Details</h4>
              <pre style={styles.auditJsonBlock}>
                {JSON.stringify(selectedAudit.details || {}, null, 2)}
              </pre>
            </div>

            <div style={styles.auditValueStack}>
              <div className="rm-panel-card" style={styles.placeholder}>
                <h4 style={{ margin: "0 0 8px 0", fontSize: 15, fontWeight: 900 }}>Old values</h4>
                <pre style={styles.auditJsonBlock}>
                  {JSON.stringify(selectedAudit.old_values || {}, null, 2)}
                </pre>
              </div>
              <div className="rm-panel-card" style={styles.placeholder}>
                <h4 style={{ margin: "0 0 8px 0", fontSize: 15, fontWeight: 900 }}>New values</h4>
                <pre style={styles.auditJsonBlock}>
                  {JSON.stringify(selectedAudit.new_values || {}, null, 2)}
                </pre>
              </div>
            </div>

            <div className="rm-modal-footer" style={styles.modalFooter}>
              <button type="button" onClick={() => setSelectedAudit(null)} style={styles.primaryButton}>
                Close
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}
    </div>
  );
};


const WorkflowRunsManagementSection = ({ styles, actionButton }) => {
  const auditedRoutes = [
    "POST /api/jobs/{job_id}/work-packages/{work_package_id}/workflow/instantiate",
    "GET /api/jobs/{job_id}/work-packages/{work_package_id}/workflow",
    "GET /api/jobs/{job_id}/work-packages/{work_package_id}/workflow/{job_workflow_id}",
    "POST /api/jobs/{job_id}/work-packages/{work_package_id}/tasks/{job_task_id}/complete",
    "POST /api/jobs/{job_id}/work-packages/{work_package_id}/tasks/{job_task_id}/reopen",
    "POST /api/jobs/{job_id}/work-packages/{work_package_id}/tasks/{job_task_id}/approve",
    "POST /api/jobs/{job_id}/work-packages/{work_package_id}/tasks/{job_task_id}/reject",
    "POST /api/jobs/{job_id}/work-packages/{work_package_id}/tasks/{job_task_id}/override/*",
    "POST /api/jobs/{job_id}/work-packages/{work_package_id}/tasks/{job_task_id}/notifications/{notification_id}/retry",
    "POST /api/jobs/{job_id}/work-packages/{work_package_id}/tasks/{job_task_id}/actions/retry",
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="rm-hero" style={styles.hero}>
        <div className="rm-title-row" style={styles.titleRow}>
          <div>
            <p style={styles.eyebrow}>Operations / Workflow Runs</p>
            <h3 style={styles.h3}>Workflow Runtime Records Management</h3>
            <p style={styles.description}>
              AUD6 route review is complete. Runtime workflow routes derive tenant_id from the authenticated user,
              use canonical auth, set app.user_id for mutating runtime actions, and delegate business rules to the service layer.
              This tab is currently a governance summary; a backend-backed runtime workflow search table should be added when RM workflow lifecycle tooling is required.
            </p>
          </div>
          <span style={styles.badge}>AUD6: route verified</span>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 900 }}>Workflow route review result</h4>
        <div style={styles.checkList}>
          <div style={styles.checkItem}>workflow.py passed route-level review: canonical auth, tenant from current_user, no client tenant_id, no hardcoded tenant fallback, app.user_id context on runtime mutations.</div>
          <div style={styles.checkItem}>Template admin, version, step, action, binding, and category routes passed route-level review with service-layer tenant scoping.</div>
          <div style={styles.checkItem}>Workflow reporting and planner projection routes passed as authenticated, tenant-scoped, read-only endpoints.</div>
          <div style={styles.checkItem}>Smoke tests passed for representative workflow template, category, binding, report, and planner endpoints: 401 without token and 200 with bearer token.</div>
          <div style={styles.checkItem}>No AUD6 route patches were required.</div>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 900 }}>Runtime endpoints covered</h4>
        <div style={styles.checkList}>
          {auditedRoutes.map((route) => (
            <div key={route} style={styles.checkItem}>{route}</div>
          ))}
        </div>
        <div style={{ ...styles.safePanel, marginTop: 12, borderColor: `${actionButton}33` }}>
          Service-layer follow-up remains separate: verify workflow state-transition rules, override approval policy, notification/action retry audit entries, frozen runtime-copy integrity, and published-template immutability against real workflow data.
        </div>
      </div>
    </div>
  );
};

const TaskManagementSection = ({ styles, actionButton }) => {
  const auditedControls = [
    "Task completion, reopen, approval, and rejection routes are authenticated and tenant-scoped.",
    "Override request, approve, reject, and apply routes are authenticated and tenant-scoped.",
    "Notification retry and backend action retry routes are authenticated and tenant-scoped.",
    "Explicit task assignment list/create/update/deactivate routes are authenticated and tenant-scoped.",
    "Task assignment mutation routes set app.user_id for downstream audit/trigger use.",
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="rm-hero" style={styles.hero}>
        <div className="rm-title-row" style={styles.titleRow}>
          <div>
            <p style={styles.eyebrow}>Operations / Tasks</p>
            <h3 style={styles.h3}>Workflow Task Records Management</h3>
            <p style={styles.description}>
              AUD6 verified the route security posture for runtime task actions and explicit task assignment management.
              This tab intentionally summarises the current governance position until a backend-backed task search, assignment matrix,
              overdue task view, and repair workflow are added to Records Management.
            </p>
          </div>
          <span style={styles.badge}>AUD6: task routes verified</span>
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 900 }}>Task route review result</h4>
        <div style={styles.checkList}>
          {auditedControls.map((item) => (
            <div key={item} style={styles.checkItem}>{item}</div>
          ))}
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 900 }}>Next build requirement</h4>
        <div style={styles.checkList}>
          <div style={styles.checkItem}>Add backend-backed task search when Records Management needs direct task administration.</div>
          <div style={styles.checkItem}>Expose assignment matrix, overdue task report, task event audit, and task repair controls only after service-layer transition and audit rules are verified.</div>
          <div style={styles.checkItem}>Keep task assignment separate from job assignment; task assignment may inherit from the job, diverge from the job, or remain unassigned.</div>
          <div style={styles.checkItem}>Do not expose destructive task actions until purge/recovery governance exists.</div>
        </div>
        <div style={{ ...styles.safePanel, marginTop: 12, borderColor: `${actionButton}33` }}>
          AUD6 closed route-level risk. The next meaningful workflow audit pass should be service-layer state transitions and audit event creation, not another route grep.
        </div>
      </div>
    </div>
  );
};

const RecordsManagementPanel = () => {
  const { theme } = useTheme();
  const [activeKey, setActiveKey] = useState("overview");

  const activeItem = useMemo(() => {
    for (const group of NAV_GROUPS) {
      const found = group.items.find((item) => item.key === activeKey);
      if (found) return found;
    }
    return NAV_GROUPS[0].items[0];
  }, [activeKey]);

  const accent = theme?.accent_color || "#000000";
  const primary = theme?.primary_color || "#111827";
  const actionButton = theme?.button_action?.normal || accent;

  const styles = {
    shell: {
      display: "grid",
      gridTemplateColumns: "300px minmax(0, 1fr)",
      gap: 18,
      minHeight: 680,
    },
    sidebar: {
      background: "#ffffff",
      border: "1px solid #e5e7eb",
      borderRadius: 14,
      padding: 14,
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      alignSelf: "start",
      maxHeight: "calc(100vh - 210px)",
      overflowY: "auto",
    },
    sidebarTitle: {
      margin: "0 0 4px 0",
      fontSize: 16,
      fontWeight: 800,
      color: "#111827",
      fontFamily: theme?.font_header,
    },
    sidebarSubtitle: {
      margin: "0 0 14px 0",
      fontSize: 12,
      color: "#6b7280",
      lineHeight: 1.4,
    },
    groupLabel: {
      marginTop: 16,
      marginBottom: 6,
      padding: "0 4px",
      fontSize: 11,
      color: "#6b7280",
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
    },
    navButton: (isActive) => ({
      width: "100%",
      textAlign: "left",
      border: isActive ? `1px solid ${accent}` : "1px solid transparent",
      background: isActive ? `${accent}14` : "transparent",
      color: isActive ? "#111827" : "#374151",
      borderRadius: 10,
      padding: "9px 10px",
      cursor: "pointer",
      fontSize: 13,
      fontWeight: isActive ? 800 : 600,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 10,
    }),
    content: {
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 16,
    },
    hero: {
      background: "#ffffff",
      border: "1px solid #e5e7eb",
      borderRadius: 14,
      padding: 18,
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    },
    titleRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 16,
      flexWrap: "wrap",
    },
    eyebrow: {
      margin: "0 0 6px 0",
      color: accent,
      fontSize: 12,
      fontWeight: 800,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
    },
    h3: {
      margin: 0,
      fontFamily: theme?.font_header,
      color: "#111827",
      fontSize: 22,
      fontWeight: 800,
    },
    description: {
      margin: "8px 0 0 0",
      color: "#4b5563",
      fontSize: 14,
      lineHeight: 1.55,
      maxWidth: 900,
    },
    badge: {
      background: `${primary}12`,
      border: `1px solid ${primary}33`,
      color: primary,
      borderRadius: 999,
      padding: "7px 10px",
      fontSize: 12,
      fontWeight: 800,
      whiteSpace: "nowrap",
    },
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      gap: 12,
      marginTop: 16,
    },
    statCard: {
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      padding: 12,
      background: "#f9fafb",
      minHeight: 96,
    },
    statValue: {
      margin: 0,
      color: "#111827",
      fontSize: 22,
      fontWeight: 900,
      lineHeight: 1.1,
    },
    statLabel: {
      margin: "6px 0 4px 0",
      color: "#374151",
      fontSize: 13,
      fontWeight: 800,
    },
    statDetail: {
      margin: 0,
      color: "#6b7280",
      fontSize: 12,
      lineHeight: 1.35,
    },
    placeholder: {
      background: "#ffffff",
      border: "1px solid #e5e7eb",
      borderRadius: 14,
      padding: 18,
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    },
    phaseGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
      gap: 12,
    },
    phaseCard: {
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      padding: 12,
      background: "#ffffff",
    },
    domainStatusGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 12,
    },
    domainStatusCard: {
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      padding: 12,
      background: "#ffffff",
    },
    domainGroupTitle: {
      margin: "0 0 10px 0",
      fontSize: 13,
      fontWeight: 900,
      color: "#111827",
      textTransform: "uppercase",
      letterSpacing: "0.04em",
    },
    domainStatusRow: {
      display: "grid",
      gridTemplateColumns: "minmax(120px, 0.7fr) 105px minmax(0, 1fr)",
      gap: 10,
      alignItems: "center",
      borderTop: "1px solid #f3f4f6",
      paddingTop: 9,
      marginTop: 9,
      fontSize: 13,
    },
    domainName: {
      fontWeight: 900,
      color: "#111827",
    },
    domainDetail: {
      color: "#6b7280",
      lineHeight: 1.35,
      fontSize: 12,
    },
    lifecycleStatusBadge: (status) => {
      const value = String(status || "").toLowerCase();
      const complete = value.includes("complete") || value.includes("signed off") || value.includes("fixed") || value.includes("verified");
      const next = value.includes("next") || value.includes("current") || value.includes("in progress") || value.includes("aud9");
      const available = value.includes("available") || value.includes("shared") || value.includes("validated");
      return {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 999,
        padding: "4px 8px",
        fontSize: 11,
        fontWeight: 900,
        background: complete ? "#ecfdf5" : next ? `${actionButton}16` : available ? "#eff6ff" : "#f9fafb",
        color: complete ? "#047857" : next ? actionButton : available ? "#1d4ed8" : "#6b7280",
        border: complete ? "1px solid #10b98155" : next ? `1px solid ${actionButton}33` : available ? "1px solid #bfdbfe" : "1px solid #e5e7eb",
        textTransform: "capitalize",
        whiteSpace: "nowrap",
      };
    },
    phaseLabel: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 44,
      borderRadius: 999,
      padding: "4px 8px",
      background: `${actionButton}16`,
      color: actionButton,
      fontWeight: 900,
      fontSize: 12,
      marginBottom: 8,
    },
    muted: {
      color: "#6b7280",
      fontSize: 13,
      lineHeight: 1.45,
    },
    warning: {
      border: "1px solid #f59e0b55",
      background: "#fffbeb",
      borderRadius: 12,
      padding: 12,
      color: "#92400e",
      fontSize: 13,
      lineHeight: 1.45,
      marginTop: 14,
    },
    safePanel: {
      border: "1px solid #10b98155",
      background: "#ecfdf5",
      borderRadius: 12,
      padding: 12,
      color: "#065f46",
      fontSize: 13,
      lineHeight: 1.45,
    },
    emptyPanel: {
      border: "1px dashed #d1d5db",
      background: "#f9fafb",
      borderRadius: 12,
      padding: 12,
      color: "#6b7280",
      fontSize: 13,
      lineHeight: 1.45,
    },
    toolbarGrid: {
      display: "grid",
      gridTemplateColumns: "minmax(240px, 1fr) 180px 150px 120px",
      gap: 12,
      alignItems: "end",
    },
    fieldLabel: {
      display: "flex",
      flexDirection: "column",
      gap: 6,
      fontSize: 12,
      fontWeight: 800,
      color: "#374151",
    },
    checkboxLabel: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      minHeight: 38,
      fontSize: 13,
      fontWeight: 800,
      color: "#374151",
    },
    input: {
      width: "100%",
      border: "1px solid #d1d5db",
      borderRadius: 10,
      padding: "9px 10px",
      fontSize: 13,
      outline: "none",
      background: "#ffffff",
      color: "#111827",
    },
    primaryButton: {
      minHeight: 38,
      border: `1px solid ${actionButton}`,
      background: actionButton,
      color: "#ffffff",
      borderRadius: 10,
      padding: "9px 12px",
      fontWeight: 900,
      cursor: "pointer",
    },
    secondaryButton: {
      border: `1px solid ${accent}55`,
      background: `${accent}10`,
      color: accent,
      borderRadius: 9,
      padding: "7px 9px",
      fontSize: 12,
      fontWeight: 900,
      cursor: "pointer",
      whiteSpace: "nowrap",
    },
    archiveButton: {
      border: "1px solid #f59e0b66",
      background: "#fffbeb",
      color: "#92400e",
      borderRadius: 9,
      padding: "7px 9px",
      fontSize: 12,
      fontWeight: 900,
      cursor: "pointer",
      whiteSpace: "nowrap",
    },
    disabledButton: {
      border: "1px solid #d1d5db",
      background: "#f3f4f6",
      color: "#9ca3af",
      borderRadius: 9,
      padding: "7px 9px",
      fontSize: 12,
      fontWeight: 900,
      cursor: "not-allowed",
      whiteSpace: "nowrap",
    },
    disabledDangerButton: {
      border: "1px solid #fecaca",
      background: "#fef2f2",
      color: "#fca5a5",
      borderRadius: 9,
      padding: "7px 9px",
      fontSize: 12,
      fontWeight: 900,
      cursor: "not-allowed",
      whiteSpace: "nowrap",
    },
    tableShell: {
      marginTop: 14,
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      overflow: "auto",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      minWidth: 920,
      fontSize: 13,
    },
    th: {
      background: "#f9fafb",
      borderBottom: "1px solid #e5e7eb",
      padding: "10px 12px",
      textAlign: "left",
      fontSize: 12,
      color: "#374151",
      fontWeight: 900,
      whiteSpace: "nowrap",
    },
    td: {
      borderBottom: "1px solid #f3f4f6",
      padding: "10px 12px",
      color: "#111827",
      verticalAlign: "middle",
    },
    emptyTableCell: {
      padding: 18,
      color: "#6b7280",
      textAlign: "center",
      lineHeight: 1.45,
    },
    selectedRow: {
      background: `${accent}08`,
    },
    subtle: {
      fontSize: 12,
      color: "#6b7280",
      marginTop: 2,
    },
    inlineLink: {
      fontSize: 12,
      fontWeight: 900,
      color: accent,
      textDecoration: "underline",
      textUnderlineOffset: 3,
      cursor: "pointer",
      whiteSpace: "nowrap",
    },
    statusBadge: {
      display: "inline-flex",
      alignItems: "center",
      borderRadius: 999,
      padding: "4px 8px",
      fontSize: 12,
      fontWeight: 900,
      textTransform: "capitalize",
    },
    actionRow: {
      display: "flex",
      gap: 6,
      alignItems: "center",
      flexWrap: "wrap",
    },
    splitGrid: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) minmax(320px, 0.75fr)",
      gap: 16,
    },
    auditValueStack: {
      display: "flex",
      flexDirection: "column",
      gap: 14,
    },
    auditJsonBlock: {
      margin: 0,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      overflowX: "auto",
      fontSize: 12,
      lineHeight: 1.45,
      color: "#374151",
      maxWidth: "100%",
    },
    dependencyBox: {
      border: "1px solid #dbeafe",
      background: "#eff6ff",
      borderRadius: 12,
      padding: 12,
      color: "#1e3a8a",
      fontSize: 13,
    },
    dependencyRow: {
      display: "flex",
      justifyContent: "space-between",
      gap: 12,
      borderTop: "1px solid rgba(30,58,138,0.14)",
      paddingTop: 8,
    },
    modalBody: {
      display: "flex",
      flexDirection: "column",
      gap: 14,
      maxHeight: "min(78vh, 760px)",
      overflowY: "auto",
      paddingRight: 2,
    },
    modalOverlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(17,24,39,0.58)",
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 18,
    },
    modalCard: {
      width: "min(980px, 96vw)",
      maxHeight: "88vh",
      overflowY: "auto",
      background: "#ffffff",
      borderRadius: 16,
      border: "1px solid #e5e7eb",
      boxShadow: "0 24px 80px rgba(0,0,0,0.28)",
      padding: 18,
    },
    modalHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 16,
      marginBottom: 14,
    },
    modalCloseButton: {
      border: "1px solid #e5e7eb",
      background: "#ffffff",
      color: "#111827",
      borderRadius: 10,
      width: 38,
      height: 38,
      cursor: "pointer",
      fontSize: 24,
      lineHeight: 1,
      fontWeight: 800,
    },
    modalStatsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: 12,
      marginTop: 14,
    },
    dependencyModalList: {
      display: "flex",
      flexDirection: "column",
      gap: 12,
      marginTop: 14,
    },
    dependencyModalGroup: (severity, blocking) => ({
      border: blocking ? "1px solid #f59e0b66" : severity === "danger" ? "1px solid #fecaca" : "1px solid #e5e7eb",
      background: blocking ? "#fffbeb" : severity === "danger" ? "#fef2f2" : "#ffffff",
      borderRadius: 12,
      padding: 12,
    }),
    dependencyModalGroupHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 12,
    },
    dependencyRecordRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 12,
      borderTop: "1px solid #f3f4f6",
      paddingTop: 8,
    },
    modalFooter: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 10,
      marginTop: 16,
      paddingTop: 14,
      borderTop: "1px solid #e5e7eb",
    },
    resourceRedactionFooter: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      alignItems: "stretch",
      gap: 8,
      marginTop: 16,
      paddingTop: 14,
      borderTop: "1px solid #e5e7eb",
    },
    resourceModalActionButton: (baseStyle) => ({
      ...baseStyle,
      width: "100%",
      minHeight: 38,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      whiteSpace: "normal",
    }),
    checkList: {
      display: "flex",
      flexDirection: "column",
      gap: 8,
    },
    checkItem: {
      border: "1px solid #e5e7eb",
      borderRadius: 10,
      padding: "9px 10px",
      background: "#f9fafb",
      color: "#374151",
      fontSize: 13,
      fontWeight: 700,
    },
  };

  const renderOverview = () => (
    <>
      <div className="rm-hero" style={styles.hero}>
        <div className="rm-title-row" style={styles.titleRow}>
          <div>
            <p style={styles.eyebrow}>Records Management</p>
            <h3 style={styles.h3}>Data Administration & Lifecycle Management</h3>
            <p style={styles.description}>
              This area is now the operational control surface for tenant-safe records management and audit evidence:
              CRM, jobs, resources, workflows, ANPR records, documents, reports, widgets, settings,
              tenant isolation checks, bootstrap validation, audit, recovery, and data integrity.
            </p>
          </div>
          <span style={{ ...styles.badge, background: "#ecfdf5", color: "#047857", border: "1px solid #10b98155" }}>AUD8 Signed Off</span>
        </div>

        <div className="rm-stats-grid" style={styles.statsGrid}>
          {QUICK_STATS.map((stat) => (
            <div key={stat.label} style={styles.statCard}>
              <p style={styles.statValue}>{stat.value}</p>
              <p style={styles.statLabel}>{stat.label}</p>
              <p style={styles.statDetail}>{stat.detail}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 12px 0", fontSize: 16, fontWeight: 900 }}>Build sequence</h4>
        <div className="rm-phase-grid" style={styles.phaseGrid}>
          {PHASES.map((phase) => (
            <div key={phase.phase} style={styles.phaseCard}>
              <div style={styles.phaseLabel}>{phase.phase}</div>
              <div style={{ fontWeight: 900, color: "#111827", marginBottom: 4 }}>{phase.title}</div>
              <div style={{ fontSize: 12, fontWeight: 800, color: accent, marginBottom: 6 }}>{phase.status}</div>
              <div style={styles.muted}>{phase.detail}</div>
            </div>
          ))}
        </div>
        <div style={styles.warning}>
          Records Management lifecycle coverage is now strong across CRM, Jobs, Documents, Resources, Workflow Runs, Tasks, Camera Devices, Camera Ingest Events, ANPR Sightings, and Plate Sessions. Audit coverage has moved beyond lifecycle events: customer, contact, opportunity, quote, job, document, and workflow routes now have verified audit/security coverage. Delete remains blocked by design for governed lifecycle domains until dependency-enforced soft delete, recovery, purge governance, and validated audit integration exist.
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 12px 0", fontSize: 16, fontWeight: 900 }}>Lifecycle coverage by domain</h4>
        <div className="rm-domain-status-grid" style={styles.domainStatusGrid}>
          {DOMAIN_STATUS_GROUPS.map((group) => (
            <div key={group.group} style={styles.domainStatusCard}>
              <h5 style={styles.domainGroupTitle}>{group.group}</h5>
              {group.domains.map((domain) => (
                <div key={domain.label} className="rm-domain-status-row" style={styles.domainStatusRow}>
                  <div style={styles.domainName}>{domain.label}</div>
                  <span style={styles.lifecycleStatusBadge(domain.status)}>{domain.status}</span>
                  <div style={styles.domainDetail}>{domain.detail}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div style={{ ...styles.safePanel, marginTop: 14 }}>
          Current standard for archive-capable domains: search, dependency modal, archive, restore, include-archived visibility, archived-state actions, and hard delete disabled. Resource, workflow, task, and ANPR operational domains use specialised controls instead of forced archive/restore.
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 12px 0", fontSize: 16, fontWeight: 900 }}>Audit readiness</h4>
        <div style={styles.checkList}>
          {AUDIT_READINESS_FINDINGS.map((item) => (
            <div key={item.label} style={styles.checkItem}>
              <div className="rm-status-summary-row" style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                <strong>{item.label}</strong>
                <span style={styles.lifecycleStatusBadge(item.status)}>{item.status}</span>
              </div>
              <div style={{ ...styles.subtle, marginTop: 6 }}>{item.detail}</div>
            </div>
          ))}
        </div>
        <div style={styles.warning}>
          Audit Log is no longer just a lifecycle viewer. It now has verified coverage for request logging, failed login, user/role/theme changes, resource reassignment/redaction, CRM record updates, quote/opportunity updates, job updates, document upload/update/signature/delete events, AUD6 workflow route security verification, SEC1-SEC19 route hardening evidence, AUD8 tenant-isolation findings, and AUD9 unauthenticated endpoint remediation. AUD8 staff/API closure has zero confirmed failures. AUD9 reduced unexpected anonymous successes from 12 to 6 across 478 operations; the six remaining findings are intentional infrastructure/UI bootstrap policy endpoints rather than identified application authentication bypasses.
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 12px 0", fontSize: 16, fontWeight: 900 }}>Audit sign-off and future programme</h4>
        <div className="rm-phase-grid" style={styles.phaseGrid}>
          {AUDIT_PROGRESS.map((item) => (
            <div key={item.phase} style={styles.phaseCard}>
              <div style={styles.phaseLabel}>{item.phase}</div>
              <div style={{ fontWeight: 900, color: "#111827", marginBottom: 4 }}>{item.title}</div>
              <div style={{ fontSize: 12, fontWeight: 800, color: accent, marginBottom: 6 }}>{item.status}</div>
              <div style={styles.muted}>{item.detail}</div>
            </div>
          ))}
        </div>
        <div style={{ ...styles.safePanel, marginTop: 14 }}>
          AUD8 staff/API tenant isolation is signed off. AUD9 unauthenticated endpoint hardening is substantially complete: 478 operations analysed, 201 denied as expected, 6 intentional-public findings remaining, and 271 mutation endpoints skipped safely. Final portal trust-boundary sign-off requires real portal identities. RBAC Console, Bootstrap Generator and remaining Records Management UX/governance remain separate implementation programmes.
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 12px 0", fontSize: 16, fontWeight: 900 }}>Security audit progress</h4>
        <div className="rm-phase-grid" style={styles.phaseGrid}>
          {SECURITY_AUDIT_PROGRESS.map((item) => (
            <div key={item.phase} style={styles.phaseCard}>
              <div style={styles.phaseLabel}>{item.phase}</div>
              <div style={{ fontWeight: 900, color: "#111827", marginBottom: 4 }}>{item.title}</div>
              <div style={{ fontSize: 12, fontWeight: 800, color: accent, marginBottom: 6 }}>{item.status}</div>
              <div style={styles.muted}>{item.detail}</div>
            </div>
          ))}
        </div>
        <div style={styles.warning}>
          Security remediation rule: any route found using legacy auth, hardcoded users, client-supplied tenant_id, or unsafe default tenant_id=1 must be hardened before feature work continues on that module. SEC1–SEC19 are complete, verified, or formally deferred. Do not duplicate completed SEC work. The only deferred security design item is conversation.py inbound tenant attribution, which needs an Unassigned Inbox strategy rather than a blind current_user patch.
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 12px 0", fontSize: 16, fontWeight: 900 }}>AUD8 tenant-isolation findings</h4>
        <div style={styles.checkList}>
          {AUD8_TENANT_ISOLATION_FINDINGS.map((item) => (
            <div key={item.label} style={styles.checkItem}>
              <div className="rm-status-summary-row" style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                <strong>{item.label}</strong>
                <span style={styles.lifecycleStatusBadge(item.status)}>{item.status}</span>
              </div>
              <div style={{ ...styles.subtle, marginTop: 6 }}>{item.detail}</div>
            </div>
          ))}
        </div>
        <div style={{ ...styles.safePanel, marginTop: 14 }}>
          AUD8 is signed off. The principal remediations were tenant-owned taxonomy, tenant-safe selectors/settings, shared-catalogue classification, and removal of implicit Super Admin cross-tenant user access.
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 12px 0", fontSize: 16, fontWeight: 900 }}>Outstanding programme — separate from AUD8 sign-off</h4>
        <div style={styles.checkList}>
          {POST_AUD8_PROGRAMME.map((item) => (
            <div key={item.label} style={styles.checkItem}>
              <div className="rm-status-summary-row" style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                <strong>{item.label}</strong>
                <span style={styles.lifecycleStatusBadge(item.status)}>{item.status}</span>
              </div>
              <div style={{ ...styles.subtle, marginTop: 6 }}>{item.detail}</div>
            </div>
          ))}
        </div>
        <div style={{ ...styles.warning, marginTop: 14 }}>
          These items remain open because they require implementation or a different authentication boundary. They do not invalidate the AUD8 staff/API tenant-isolation sign-off.
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 12px 0", fontSize: 16, fontWeight: 900 }}>Bootstrap Generator rules</h4>
        <div style={styles.checkList}>
          {BOOTSTRAP_GENERATOR_RULES.map((item) => (
            <div key={item} style={styles.checkItem}>{item}</div>
          ))}
        </div>
        <div style={{ ...styles.safePanel, marginTop: 14 }}>
          Bootstrap generation must create realistic tenant data and must be reversible. Use generated-record batch tracking so seeded tenants can be safely cleaned up without contaminating production data.
        </div>
      </div>

      <div className="rm-panel-card" style={styles.placeholder}>
        <h4 style={{ margin: "0 0 12px 0", fontSize: 16, fontWeight: 900 }}>Resource redaction approach</h4>
        <div style={styles.checkList}>
          {RESOURCE_REDACTION_PRINCIPLES.map((item) => (
            <div key={item} style={styles.checkItem}>{item}</div>
          ))}
        </div>
        <div style={{ ...styles.safePanel, marginTop: 14 }}>
          Resources now use a two-step dependency reassignment and gated redaction workflow instead of the simple archive/restore pattern.
        </div>
      </div>
    </>
  );

  const renderPlaceholder = () => (
    <div className="rm-panel-card" style={styles.placeholder}>
      <h4 style={{ margin: "0 0 8px 0", fontSize: 17, fontWeight: 900 }}>{activeItem.label}</h4>
      <p style={{ ...styles.muted, marginTop: 0 }}>{activeItem.description}</p>
      <div style={styles.warning}>
        This section is intentionally scaffolded or roadmap-backed only. The next implementation pass should add backend-backed
        search, dependency analysis, archive/restore actions where appropriate, audit visibility, tenant/user ownership checks,
        cross-tenant selector checks, and role-safe destructive controls for this record domain.
      </div>
    </div>
  );

  const renderDataIntegrity = () => (
    <div className="rm-data-integrity">
      <div className="rm-hero" style={styles.hero}>
        <div className="rm-title-row" style={styles.titleRow}>
          <div>
            <p style={styles.eyebrow}>Governance / Data Integrity</p>
            <h3 style={styles.h3}>Data Integrity / Legacy Maintenance Tools</h3>
            <p style={styles.description}>
              Existing maintenance tools remain available while Records Management evolves into a safer lifecycle
              administration area. The maintenance widget is rendered directly below this context panel so its own
              border is not nested inside another bordered widget wrapper.
            </p>
          </div>
          <span style={styles.badge}>Administrative tools</span>
        </div>
      </div>
      <AdminMaintenanceWidget />
    </div>
  );

  const renderContent = () => {
    if (activeKey === "overview") return renderOverview();
    if (activeKey === "quotes") return <QuoteManagementSection styles={styles} accent={accent} actionButton={actionButton} />;
    if (activeKey === "customers") return <CustomerManagementSection styles={styles} accent={accent} actionButton={actionButton} />;
    if (activeKey === "jobs") return <JobManagementSection styles={styles} accent={accent} actionButton={actionButton} />;
    if (activeKey === "contacts") return <ContactManagementSection styles={styles} accent={accent} actionButton={actionButton} />;
    if (activeKey === "opportunities") return <OpportunityManagementSection styles={styles} accent={accent} actionButton={actionButton} />;
    if (activeKey === "resources") return <ResourceManagementSection styles={styles} accent={accent} actionButton={actionButton} />;
    if (activeKey === "workflow_runs") return <WorkflowRunsManagementSection styles={styles} actionButton={actionButton} />;
    if (activeKey === "tasks") return <TaskManagementSection styles={styles} actionButton={actionButton} />;
    if (activeKey === "camera_devices") return <CameraDeviceManagementSection styles={styles} accent={accent} actionButton={actionButton} />;
    if (activeKey === "camera_ingest_events") return <CameraIngestEventManagementSection styles={styles} accent={accent} actionButton={actionButton} />;
    if (activeKey === "anpr_reads") return <AnprSightingManagementSection styles={styles} accent={accent} actionButton={actionButton} />;
    if (activeKey === "plate_sessions") return <PlateSessionManagementSection styles={styles} accent={accent} actionButton={actionButton} />;
    if (activeKey === "documents") return <DocumentManagementSection styles={styles} accent={accent} actionButton={actionButton} />;
    if (activeKey === "audit_log") return <AuditLogManagementSection styles={styles} accent={accent} actionButton={actionButton} />;
    if (activeKey === "data_integrity") return renderDataIntegrity();
    if (STATIC_RECORD_SECTIONS[activeKey]) {
      return <StaticRecordSection section={STATIC_RECORD_SECTIONS[activeKey]} styles={styles} accent={accent} />;
    }
    return renderPlaceholder();
  };

  return (
    <>
      <style>{`
        .rm-mobile-navigation {
          display: none;
        }

        @media (max-width: 768px) {
          .rm-shell {
            display: block !important;
            min-height: 0 !important;
          }

          .rm-sidebar {
            display: none !important;
          }

          .rm-mobile-navigation {
            display: block;
            margin-bottom: 12px;
            padding: 12px;
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            box-shadow: 0 1px 4px rgba(0,0,0,0.06);
          }

          .rm-mobile-navigation label {
            display: block;
            margin-bottom: 6px;
            color: #374151;
            font-size: 12px;
            font-weight: 900;
          }

          .rm-mobile-navigation select {
            width: 100%;
            min-height: 46px;
            padding: 10px 38px 10px 12px;
            border: 1px solid #d1d5db;
            border-radius: 10px;
            background: #ffffff;
            color: #111827;
            font-size: 16px;
            font-weight: 700;
          }

          .rm-mobile-navigation p {
            margin: 8px 0 0 0;
            color: #6b7280;
            font-size: 12px;
            line-height: 1.4;
          }

          .rm-content {
            gap: 12px !important;
            min-width: 0 !important;
          }

          .rm-hero,
          .rm-panel-card {
            padding: 14px !important;
            border-radius: 12px !important;
          }

          .rm-title-row {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 10px !important;
          }

          .rm-title-row > span {
            align-self: flex-start;
            max-width: 100%;
            white-space: normal !important;
          }

          .rm-stats-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            gap: 10px !important;
          }

          .rm-phase-grid,
          .rm-domain-status-grid,
          .rm-split-grid,
          .rm-modal-stats-grid {
            grid-template-columns: minmax(0, 1fr) !important;
          }

          .rm-domain-status-row {
            grid-template-columns: minmax(0, 1fr) auto !important;
            gap: 8px !important;
            align-items: start !important;
          }

          .rm-domain-status-row > :nth-child(3) {
            grid-column: 1 / -1;
          }

          .rm-status-summary-row {
            align-items: flex-start !important;
            flex-direction: column !important;
            gap: 6px !important;
          }

          .rm-toolbar-grid {
            grid-template-columns: minmax(0, 1fr) !important;
            align-items: stretch !important;
            gap: 10px !important;
          }

          .rm-toolbar-grid input,
          .rm-toolbar-grid select,
          .rm-toolbar-grid button {
            min-height: 44px;
          }

          .rm-toolbar-grid button {
            width: 100%;
          }

          .rm-toolbar-grid label {
            min-width: 0;
          }

          .rm-toolbar-grid label:has(input[type="checkbox"]) {
            min-height: 44px !important;
            padding: 8px 10px;
            border: 1px solid #e5e7eb;
            border-radius: 10px;
            background: #f9fafb;
          }

          .rm-toolbar-grid input:not([type="checkbox"]),
          .rm-toolbar-grid select {
            box-sizing: border-box;
          }

          .rm-toolbar-grid input[type="checkbox"] {
            width: 20px;
            height: 20px;
            min-height: 20px;
            flex: 0 0 auto;
          }

          .rm-table-shell {
            margin-top: 12px !important;
            border: 0 !important;
            border-radius: 0 !important;
            overflow: visible !important;
          }

          .rm-responsive-table {
            min-width: 0 !important;
            width: 100% !important;
            border-collapse: separate !important;
            border-spacing: 0 !important;
          }

          .rm-responsive-table thead {
            display: none;
          }

          .rm-responsive-table tbody,
          .rm-responsive-table tr,
          .rm-responsive-table td {
            display: block;
            width: 100%;
          }

          .rm-responsive-table tbody tr {
            margin: 0 0 12px 0;
            padding: 3px 12px;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            background: #ffffff;
            box-shadow: 0 1px 3px rgba(0,0,0,0.04);
            overflow: hidden;
          }

          .rm-responsive-table td {
            display: grid !important;
            grid-template-columns: minmax(92px, 0.34fr) minmax(0, 1fr);
            gap: 10px;
            align-items: start;
            padding: 10px 0 !important;
            border-bottom: 1px solid #f3f4f6 !important;
            overflow-wrap: anywhere;
            word-break: break-word;
          }

          .rm-responsive-table td:last-child {
            border-bottom: 0 !important;
          }

          .rm-responsive-table td::before {
            content: attr(data-label);
            color: #6b7280;
            font-size: 10px;
            font-weight: 900;
            letter-spacing: 0.04em;
            line-height: 1.35;
            text-transform: uppercase;
          }

          .rm-responsive-table td[data-label="Actions"] {
            grid-template-columns: minmax(0, 1fr) !important;
          }

          .rm-responsive-table td[data-label="Actions"]::before {
            margin-bottom: 2px;
          }

          .rm-responsive-table td[data-label="Actions"] > button {
            width: 100%;
            min-height: 44px;
            white-space: normal !important;
          }

          .rm-responsive-table td[data-label="Details"] > div,
          .rm-responsive-table td[data-label="Error"] > div {
            max-width: none !important;
            white-space: normal !important;
            overflow: visible !important;
            text-overflow: clip !important;
          }

          .rm-responsive-table .rm-empty-table-cell {
            display: block !important;
            padding: 16px 4px !important;
            border-bottom: 0 !important;
            text-align: left !important;
          }

          .rm-responsive-table .rm-empty-table-cell::before {
            content: none;
          }

          .rm-action-row {
            display: grid !important;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 8px !important;
            width: 100%;
          }

          .rm-action-row button {
            width: 100%;
            min-height: 44px;
            padding: 9px 8px !important;
            white-space: normal !important;
          }

          .rm-modal-footer,
          .rm-resource-footer {
            display: grid !important;
            grid-template-columns: minmax(0, 1fr) !important;
            align-items: stretch !important;
            gap: 8px !important;
          }

          .rm-modal-footer button,
          .rm-resource-footer button {
            width: 100%;
            min-height: 44px;
            white-space: normal !important;
          }

          .rm-dependency-record-row {
            align-items: stretch !important;
            flex-direction: column !important;
          }

          .rm-dependency-record-row button {
            width: 100%;
            min-height: 44px;
          }

          .rm-data-integrity {
            display: flex;
            flex-direction: column;
            gap: 12px;
            min-width: 0;
          }

          .rm-data-integrity > * {
            min-width: 0;
          }
        }

        @media (max-width: 420px) {
          .rm-stats-grid,
          .rm-action-row {
            grid-template-columns: minmax(0, 1fr) !important;
          }
        }
      `}</style>

      <div className="rm-mobile-navigation">
        <label htmlFor="records-management-mobile-section">Records section</label>
        <select
          id="records-management-mobile-section"
          value={activeKey}
          onChange={(event) => setActiveKey(event.target.value)}
          aria-label="Select Records Management section"
        >
          {NAV_GROUPS.map((group) => (
            <optgroup key={group.group} label={group.group}>
              {group.items.map((item) => (
                <option key={item.key} value={item.key}>
                  {item.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <p>{activeItem.description}</p>
      </div>

      <div className="rm-shell" style={styles.shell}>
        <aside className="rm-sidebar" style={styles.sidebar}>
          <h3 style={styles.sidebarTitle}>Records</h3>
          <p style={styles.sidebarSubtitle}>
            Internal navigation for operational records, governance, recovery, and data repair.
          </p>

          {NAV_GROUPS.map((group) => (
            <div key={group.group}>
              <div style={styles.groupLabel}>{group.group}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {group.items.map((item) => {
                  const isActive = activeKey === item.key;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setActiveKey(item.key)}
                      style={styles.navButton(isActive)}
                      title={item.description}
                    >
                      <span>{item.label}</span>
                      {isActive && <span aria-hidden="true">›</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </aside>

        <main className="rm-content" style={styles.content}>{renderContent()}</main>
      </div>
    </>
  );
};

export default RecordsManagementPanel;
